// ============================================================================
// 《천하장기》 14-화면 UI 대격변 & 권위 서버 · 검증형 훈수/위기방지 · 3-버킷 경제 클라이언트
// - 01_lobby ~ 14_more 14개 핵심 화면 및 바텀시트/오버레이 상태 머신
// - 실시간 9x10 한국장기판 인터랙티브 착수 · 합법 행마 표시 · 상수 3턴 궤적 · 훈수 화살표
// - 권위 서버(Socket.IO) 연동: 게스트 세션 인증, commandId 멱등 착수, 스냅샷 복구, visibility 분리
// - 3-버킷 인벤토리(matchGrant -> dailyAllowance -> purchasedInventory) 및 통합 SKU 카탈로그 연동
// ============================================================================

import { io, Socket } from 'socket.io-client';
import {
  BoardGrid,
  FormationType,
  Move,
  PIECE_NAMES_KO,
  Piece,
  PieceType,
  Pos,
  Side,
  applyMoveOnClone,
  calculateMaterialScores,
  createInitialBoard,
  formatKoreanNotation,
  getAllLegalMoves,
  getBoardHash,
  getLegalMovesForPiece,
  isBikjangState,
  isKingInCheck
} from '../core/JanggiRules.js';
import {
  ALL_TIERS,
  CATALOG_SKUS,
  ConsumableItemType,
  UserProfileState,
  applyDailyRefill,
  applyMatchResultToProfile,
  consumeInventoryItem,
  createDefaultUserProfile,
  ensureInventoryBuckets,
  getBucketTotal,
  getTierByIndex,
  migrateUserProfile,
  purchaseCatalogSku,
  syncFlatInventoryFromBuckets
} from '../core/TierSystem.js';
import {
  CrisisGuardConfig,
  CrisisInterventionResult,
  GuardablePieceType,
  HunsuCoachingData,
  HunsuEligibilityResult,
  TacticalVisionResult,
  canMovePossiblyTriggerCrisisFast,
  computeHunsuCoaching,
  computeSangsuMasuVision,
  createDefaultCrisisGuardConfig,
  detectMoveTriggersMultiTurnCrisis,
  evaluateHunsuEligibility,
  formatMoveKey
} from '../core/SangsuMasuEngine.js';
import {
  BOARD_SKIN_CATALOG,
  BoardSkinItem,
  ENDGAME_PUZZLE_STAGES,
  EnemyIntentVisionResult,
  LeaderboardCategory,
  PIECE_SKIN_CATALOG,
  PieceSkinItem,
  computeEnemyIntentVision,
  getBoardSkinById,
  getLeaderboardByCategory,
  getPieceSkinById,
  validateEndgamePuzzleSolution
} from '../core/TianTianContentEngine.js';
import { selectAiMoveForTier } from '../core/JanggiAI.js';
import { RecordedPly, generatePostGameReview } from '../core/ReviewAnalyzer.js';
import {
  BEAUTY_KNIGHT_CHAPTERS,
  BeautyPoseId,
  canHireChapterSecretary,
  getBeautyChapterById,
  getChapterClearedStars,
  getUnlockedPosesForChapter
} from '../core/BeautyKnightCampaign.js';
import { SoundEngine } from './SoundEngine.js';
import {
  DrawnPiece,
  HitResult,
  Point,
  SQUARE_BOARD,
  backingStore,
  boardToPoint,
  clientToPoint,
  fitBoard,
  hitBoard,
  hitBoardDetailed,
  pointToBoard,
  resolveBoardInput
} from './BoardGeometry.js';
import { AnalysisBroker, AnalysisStamp, stampKey } from './AnalysisBroker.js';
import {
  IDLE_LIMIT,
  TURN_MS,
  TurnClock,
  commitLegalAction,
  createTurnClock,
  expireTurn,
  finishClock,
  remainingMs,
  restoreClockOnUndo
} from '../core/TurnClock.js';

export interface ScreenSpec {
  id: string;
  number: string;
  title: string;
  subtitle: string;
  description: string;
  checks: string[];
}

export const SCREEN_DATA: ScreenSpec[] = [
  {
    id: 'lobby',
    number: '01',
    title: '홈 · 로비',
    subtitle: '들어오자마자 무엇을 누를지 보이는 첫 화면',
    description: '가운데 대국 시작, 왼쪽 친구랑, 오른쪽 혼자 두기만 먼저 보이게 정리했습니다. 정답찾기와 내 비서는 아래에 조용히 두고, 복잡한 설정은 더보기로 옮겼습니다.',
    checks: ['주요 선택지 3개만 크게 배치', '한채린 기본 인사와 바로 연결', '하단 4탭(홈 · 기록 · 상점 · 더보기) 고정']
  },
  {
    id: 'friends',
    number: '02',
    title: '친구랑 두기',
    subtitle: '초대하기와 방 번호 입력만 남긴 친구 대국',
    description: '카톡이나 문자로 초대 링크를 보내거나, 받은 4자리 방 번호를 입력해 바로 만나는 흐름입니다.',
    checks: ['친구 초대하기 큰 버튼', '4자리 방 번호 입력창 분리', '공개 빠른 대국 큐와 완전 격리']
  },
  {
    id: 'matching',
    number: '03',
    title: '대국 시작 · 매칭',
    subtitle: '차분하게 상대를 기다리는 화면',
    description: '급수와 차림을 복잡하게 묻지 않고 바로 상대를 찾습니다. 기다리는 동안에는 취소가 잘 보이고, 비서가 함께한다는 안내만 남겼습니다.',
    checks: ['현재 내 이름과 대기 상태만 표시', '기다리기 취소 버튼 명확화', '불필요한 설정 문구 제거']
  },
  {
    id: 'solo',
    number: '04',
    title: '혼자 두기',
    subtitle: 'AI 대국, 미녀대국, 정답찾기를 고르는 곳',
    description: '혼자 편하게 두고 싶을 때 들어오는 곳입니다. 하던 대국을 이어서 하거나, AI와 한 판, 미녀대국, 정답찾기 중 하나를 고릅니다.',
    checks: ['이어하기 카드 상단 배치', 'AI · 미녀대국 · 정답찾기 3단 구성', 'AI 관전과 실력 알아보기는 아래 보조 버튼으로 분리']
  },
  {
    id: 'secretary',
    number: '05',
    title: '내 비서',
    subtitle: '기본 비서 한채린과 함께하는 화면',
    description: '처음부터 한채린이 함께합니다. 미녀대국에서 첫 승리만 거두면 새 비서와 동행할 수 있고, 화보는 그다음 목표로 차근차근 모을 수 있습니다.',
    checks: ['한채린 기본 동행 상태 명확화', '1관문 승리 즉시 비서 동행 해금', '비서 바꾸기 · 화보 보기 · 미녀대국 연결']
  },
  {
    id: 'campaign',
    number: '06',
    title: '미녀대국',
    subtitle: '이번 상대와 다섯 번의 만남에 집중',
    description: '스무 명을 한꺼번에 늘어놓지 않고, 지금 만나는 비서와 5단계 진행만 먼저 보여줍니다. 일반 플레이에서는 1~5관문 모두 훈수를 쓸 수 있습니다.',
    checks: ['현재 비서 카드와 5단계 표시', '3~5관문 포함 전 구간 훈수 허용', '다음 인물 미리보기 4칸과 전체 보기 분리']
  },
  {
    id: 'puzzles',
    number: '07',
    title: '정답찾기',
    subtitle: '오늘의 한 문제부터 가볍게 푸는 곳',
    description: '어려운 한자 이름 대신 정답찾기로 통일했습니다. 오늘의 문제를 바로 풀거나 칠성취회 같은 고전 문제를 골라 차분히 풀어볼 수 있습니다.',
    checks: ['오늘의 한 문제 카드와 미리보기 판', '복수 승리 해법 인정 구조', '칠성취회 등 고전 문제 카드 정돈']
  },
  {
    id: 'game',
    number: '08',
    title: '대국 화면',
    subtitle: '장기판이 가장 넓고 또렷하게 보이는 화면',
    description: '상대 정보, 장기판, 내 차례, 아래 3개 버튼만 남겼습니다. 훈수는 좋은 수가 있을 때만 열리고, 준비 중에는 횟수가 줄지 않습니다.',
    checks: ['장기판 중심 배치', '하단 3버튼: 훈수 · 상수 · 메뉴', '훈수 대기 상태 안내와 내 화면 전용 문구']
  },
  {
    id: 'hint',
    number: '09',
    title: '훈수 바텀시트',
    subtitle: '장기판을 가리지 않고 아래에서 열리는 도움',
    description: '좋은 수가 준비됐을 때만 열립니다. 보기 전에는 횟수가 줄지 않으며, 좋은 수 보기를 누르면 판 위에 추천 화살표와 짧은 설명이 나타납니다.',
    checks: ['장기판 60% 이상 노출 유지', '열람 전 차감 없음 명시', '추천 확인 후 닫기 / 이 수 두기 선택']
  },
  {
    id: 'shield',
    number: '10',
    title: '위기 감지 시트',
    subtitle: '무료로 먼저 알려주고 선택은 내가 하는 흐름',
    description: '중요한 말을 잃을 수 있을 때 먼저 무료로 알려줍니다. 계획한 수라면 그대로 두고, 안전한 수를 보고 싶을 때만 1회를 사용합니다.',
    checks: ['경고 자체는 무료라는 문구 표시', '그대로 두기(0회 차감)와 안전한 수 보기(1회) 나란히 배치', '희생타·미끼수 오경고 방지']
  },
  {
    id: 'reconnect',
    number: '11',
    title: '연결 확인',
    subtitle: '누구의 연결을 기다리는지 분명한 안내',
    description: '잠깐 화면을 바꿨다고 바로 끝내지 않습니다. 내 연결인지 상대 연결인지 구분해 보여주고, 대국판을 지켜두었다가 차례를 다시 맞춥니다.',
    checks: ['상대 연결 / 내 연결 상태 전환 가능', '남은 시간 카운트다운 표시', '마지막 대국판 보관 안내']
  },
  {
    id: 'result',
    number: '12',
    title: '대국 결과',
    subtitle: '승패 이유와 다음 할 일이 한눈에 보이는 결과',
    description: '왜 끝났는지, 점수가 어떻게 바뀌었는지 크게 보여줍니다. 이겼을 때는 비서가 축하해주고, 한 판 더 두기나 다시 보기로 자연스럽게 이어집니다.',
    checks: ['승리 상태와 무효 대국 상태 모두 지원', '결과 이유와 점수 변화를 분리 표시', '한 판 더 두기 / 이 대국 다시 보기 제공']
  },
  {
    id: 'shop',
    number: '13',
    title: '상점',
    subtitle: '도움 아이템과 꾸미기만 깔끔하게 나눈 상점',
    description: '오늘 무료로 남은 횟수와 내가 산 보유분을 먼저 나누어 보여줍니다. 어디서 열어도 같은 이름과 가격으로 보이도록 정리했습니다.',
    checks: ['도움 아이템 / 꾸미기 2탭 구성', '오늘 무료 횟수와 구매 보유분 분리 표시', '통합 SKU 카탈로그 가격 일원화']
  },
  {
    id: 'more',
    number: '14',
    title: '더보기 · 설정',
    subtitle: '기록, 순위, 차림, 큰 글씨를 모아둔 곳',
    description: '첫 화면을 복잡하게 만들던 기능을 한곳에 모았습니다. 대국 기록, 순위, AI 관전, 실력 측정, 장기말 차림, 소리, 큰 글씨를 여기서 편하게 바꿉니다.',
    checks: ['대국 기록 · 순위 · AI 관전 · 실력 측정 타일', '장기말 차림과 도움 설정 정리', '소리 · 큰 글씨 · 화면 설정 토글 제공']
  }
];

const PORTRAIT_URL = '/assets/secretary_portrait.png';

const ICONS: Record<string, string> = {
  back: '<path d="M15 5 8 12l7 7"/>',
  close: '<path d="M6 6l12 12M18 6 6 18"/>',
  settings: '<circle cx="12" cy="12" r="3.2"/><path d="M12 2.8v2.4M12 18.8v2.4M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M2.8 12h2.4M18.8 12h2.4M4.9 19.1l1.7-1.7M17.4 6.6l1.7-1.7"/>',
  help: '<circle cx="12" cy="12" r="9"/><path d="M9.6 9.2a2.5 2.5 0 1 1 3.7 2.2c-.9.5-1.3 1-1.3 2v.4"/><circle cx="12" cy="17.2" r=".9" fill="currentColor" stroke="none"/>',
  home: '<path d="M4 10.8 12 4l8 6.8"/><path d="M6.5 9.8V19h11V9.8"/>',
  book: '<path d="M5.5 4.5h11a2 2 0 0 1 2 2v13H7.5a2 2 0 0 0-2 2z"/><path d="M5.5 4.5v15a2 2 0 0 1 2-2h11"/>',
  bag: '<path d="M6 8h12l-1 11.5H7L6 8Z"/><path d="M9 8V6.5a3 3 0 0 1 6 0V8"/>',
  more: '<circle cx="6" cy="12" r="1.6" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none"/><circle cx="18" cy="12" r="1.6" fill="currentColor" stroke="none"/>',
  people: '<circle cx="9" cy="8.5" r="2.8"/><circle cx="15.5" cy="9.5" r="2.3"/><path d="M4.2 18.2c.6-2.8 2.6-4.4 5.3-4.4s4.7 1.6 5.3 4.4"/><path d="M14.2 17.8c.5-1.9 1.8-3.1 3.6-3.3"/>',
  cap: '<path d="M3.5 9.5 12 5l8.5 4.5L12 14 3.5 9.5Z"/><path d="M6.5 11.5v4.2c0 1.4 2.5 2.8 5.5 2.8s5.5-1.4 5.5-2.8v-4.2"/>',
  bulb: '<path d="M9 18h6M10 21h4"/><path d="M8.5 14.5C7.2 13.4 6.5 11.9 6.5 10a5.5 5.5 0 1 1 11 0c0 1.9-.7 3.4-2 4.5-.7.6-1.1 1.3-1.2 2.2H9.7c-.1-.9-.5-1.6-1.2-2.2Z"/>',
  heart: '<path d="M12 19.5s-6.8-4.3-8.4-8.2A4.6 4.6 0 0 1 12 6.8a4.6 4.6 0 0 1 8.4 4.5c-1.6 3.9-8.4 8.2-8.4 8.2Z"/>',
  share: '<path d="M14 5l5 5-5 5"/><path d="M19 10H9a5 5 0 0 0-5 5v4"/>',
  lock: '<rect x="5.5" y="10.5" width="13" height="9" rx="2"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/>',
  check: '<path d="M5.5 12.5 10 17l8.5-9"/>',
  chevron: '<path d="m9 6 6 6-6 6"/>',
  arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
  eye: '<path d="M2.8 12s3.4-6 9.2-6 9.2 6 9.2 6-3.4 6-9.2 6-9.2-6-9.2-6Z"/><circle cx="12" cy="12" r="2.8"/>',
  target: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/>',
  shield: '<path d="M12 3.5 19 6.5v5.2c0 4.5-2.9 7.6-7 9.3-4.1-1.7-7-4.8-7-9.3V6.5l7-3Z"/>',
  wifi: '<path d="M4.5 9.5a11 11 0 0 1 15 0"/><path d="M7.5 13a6.8 6.8 0 0 1 9 0"/><circle cx="12" cy="17" r="1.4" fill="currentColor" stroke="none"/>',
  refresh: '<path d="M19 7v5h-5"/><path d="M18.4 11.5A7 7 0 1 0 19 15"/>',
  trophy: '<path d="M7 4.5h10v4a5 5 0 0 1-10 0v-4Z"/><path d="M7 6H4.5v2a3 3 0 0 0 3 3M17 6h2.5v2a3 3 0 0 1-3 3M12 13.5v4M8.5 19.5h7"/>',
  crown: '<path d="m3.8 17.5 1.4-9 4.5 3.8L12 6l2.3 6.3 4.5-3.8 1.4 9H3.8Z"/>',
  grid: '<rect x="4.5" y="4.5" width="6" height="6" rx="1"/><rect x="13.5" y="4.5" width="6" height="6" rx="1"/><rect x="4.5" y="13.5" width="6" height="6" rx="1"/><rect x="13.5" y="13.5" width="6" height="6" rx="1"/>',
  palette: '<path d="M12 4a8 8 0 1 0 0 16h1.4a2.1 2.1 0 0 0 0-4.2H12a2 2 0 0 1 0-4h4a4 4 0 0 0 4-4c0-2.2-3.6-3.8-8-3.8Z"/>',
  audio: '<path d="M5 10v4h3.5l4.5 3.8V6.2L8.5 10H5Z"/><path d="M16.5 9.2a4.2 4.2 0 0 1 0 5.6"/>',
  text: '<path d="M4 6h10M9 6v12M14 11h6M17 11v7"/>',
  frame: '<path d="M4.5 9V5.5H8M16 5.5h3.5V9M19.5 15v3.5H16M8 18.5H4.5V15"/>',
  smile: '<circle cx="12" cy="12" r="8.5"/><path d="M8.5 14.2c.9 1.3 2.1 2 3.5 2s2.6-.7 3.5-2"/><circle cx="9.2" cy="10" r=".9" fill="currentColor" stroke="none"/><circle cx="14.8" cy="10" r=".9" fill="currentColor" stroke="none"/>',
  undo: '<path d="M9 7 4.5 11.5 9 16"/><path d="M5 11.5h8.5a5.5 5.5 0 0 1 0 11H11"/>',
  play: '<path d="m8 6 10 6-10 6V6Z"/>',
  leaf: '<path d="M18.5 5.5c-6.5.3-11 3.8-12 10.5 6.7-1 10.2-5.5 12-10.5Z"/><path d="M6.5 16c2.2-2.2 5-4 8.5-5.2"/>'
};

function ico(name: string): string {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name] || ''}</svg>`;
}

// ============================================================================
// 기물 포획 그룹 배지 포맷터 (Test 16 호환 + 대국 화면 표기)
// ============================================================================
export function formatCapturedGroupBadgesHtml(capturedPieces: Piece[], perspectiveSide: Side): string {
  if (!capturedPieces || capturedPieces.length === 0) {
    return '<span class="muted">없음</span>';
  }
  const order: PieceType[] = ['cha', 'po', 'ma', 'sang', 'sa', 'jol'];
  const counts: Partial<Record<PieceType, number>> = {};
  for (const p of capturedPieces) {
    counts[p.type] = (counts[p.type] || 0) + 1;
  }
  const pills: string[] = [];
  for (const t of order) {
    const cnt = counts[t];
    if (!cnt) continue;
    const hanja = PIECE_NAMES_KO[t][perspectiveSide === 'cho' ? 'han' : 'cho'];
    pills.push(
      `<span class="captured-group-pill"><span class="cap-hanja-icon">${hanja}</span><span class="cap-count-txt">${cnt}</span></span>`
    );
  }
  return pills.join(' ');
}

let boardResizeObserver: ResizeObserver | null = null;

export function syncBoardCanvasAspect(): void {
  if (typeof document === 'undefined') return;
  const wrap = document.querySelector('.board-wrap') as HTMLElement | null;
  if (wrap) {
    wrap.dataset.aspectSynced = 'true';
  }
  const gameScreen = document.querySelector('.game-screen') as HTMLElement | null;
  const matchBoardWrap = document.querySelector('.game-screen .board-wrap:not(.compact)') as HTMLElement | null;
  if (!gameScreen || !matchBoardWrap) {
    if (boardResizeObserver) {
      boardResizeObserver.disconnect();
      boardResizeObserver = null;
    }
    return;
  }

  const applySquareFit = () => {
    const screenRect = gameScreen.getBoundingClientRect();
    if (screenRect.width <= 0 || screenRect.height <= 0) return;
    const edgePx = screenRect.width >= 600 ? 16 : 6;
    gameScreen.style.setProperty('--game-edge-px', `${edgePx}px`);
    const availW = Math.floor(screenRect.width - edgePx * 2);

    const hudSelectors = [
      '.game-header',
      '.player-line',
      '.turn-clock-bar',
      '.turn-row',
      '.puzzle-extra-row',
      '.self-line',
      '.assist-row',
      '.assist-caption'
    ];
    let usedHudH = 28; // vertical gaps + padding slack
    for (const sel of hudSelectors) {
      const el = gameScreen.querySelector(sel) as HTMLElement | null;
      if (el) {
        usedHudH += el.getBoundingClientRect().height;
      }
    }
    const availH = Math.max(200, Math.floor(screenRect.height - usedHudH));
    const clampedW = Math.max(120, Math.min(720, availW));
    const clampedH = Math.max(120, Math.min(720, availH));
    const fitted = fitBoard({ left: 0, top: 0, width: clampedW, height: clampedH }, SQUARE_BOARD);
    const size = Math.floor(fitted.width);
    if (size > 0) {
      matchBoardWrap.style.width = `${size}px`;
      matchBoardWrap.style.height = `${size}px`;
      gameScreen.style.setProperty('--board-size-px', `${size}px`);
      const matchCanvas = matchBoardWrap.querySelector<HTMLCanvasElement>('canvas.janggi-canvas');
      if (matchCanvas) {
        matchCanvas.dataset.cssWidth = String(size);
        matchCanvas.dataset.cssHeight = String(size);
      }
    }
  };

  applySquareFit();

  if (typeof ResizeObserver !== 'undefined') {
    if (boardResizeObserver) {
      boardResizeObserver.disconnect();
    }
    boardResizeObserver = new ResizeObserver(() => {
      applySquareFit();
      renderAllBoardCanvases();
    });
    boardResizeObserver.observe(gameScreen);
  }
}

// ============================================================================
// 12종 HD 화투/전통풍 감정표현 스티커 카탈로그 & 7단계 계급별 포획 타격 VFX 스펙
// ============================================================================
export interface EmoteItem {
  id: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  soundType: 'laugh' | 'tease' | 'angry' | 'cry' | 'praise';
}

export const EMOTE_CATALOG: EmoteItem[] = [
  { id: 'laugh', title: '호탕웃음', subtitle: '하하하! 통쾌하구먼!', imageUrl: '/assets/images/astra/emote_laugh_hd.png', soundType: 'laugh' },
  { id: 'tease', title: '약올리기', subtitle: '메~롱! 그걸론 어림없지!', imageUrl: '/assets/images/astra/emote_tease_hd.png', soundType: 'tease' },
  { id: 'angry', title: '버럭(분노)', subtitle: '아니, 이런 무례한 수를!!', imageUrl: '/assets/images/astra/emote_angry_hd.png', soundType: 'angry' },
  { id: 'cry', title: '통곡(눈물)', subtitle: '아이고 내 대차(車)야 ㅠㅠ', imageUrl: '/assets/images/astra/emote_cry_hd.png', soundType: 'cry' },
  { id: 'thumbsup', title: '명수 극찬', subtitle: '과연 천하의 명수(名手)요!', imageUrl: '/assets/images/astra/emote_thumbsup_hd.png', soundType: 'praise' },
  { id: 'tea', title: '식혜 여유', subtitle: '천천히 생각하시게~ 껄껄', imageUrl: '/assets/images/astra/emote_tea_hd.png', soundType: 'laugh' },
  { id: 'yawn', title: '장고 하품', subtitle: '날 새겠소~ 빨리 좀 두시구려!', imageUrl: '/assets/images/astra/emote_yawn_hd.png', soundType: 'tease' },
  { id: 'sweat', title: '식은땀 뻘뻘', subtitle: '헉.. 외통수 위기인가?!', imageUrl: '/assets/images/astra/emote_sweat_hd.png', soundType: 'cry' },
  { id: 'confident', title: '승리 장담', subtitle: '외통이오! 승부는 끝났소!', imageUrl: '/assets/images/astra/emote_confident_hd.png', soundType: 'laugh' },
  { id: 'dizzy', title: '혼비백산', subtitle: '앗! 미처 못 본 상(象) 길이!', imageUrl: '/assets/images/astra/emote_dizzy_hd.png', soundType: 'cry' },
  { id: 'beg', title: '한수 무르기', subtitle: '어르신, 한 수만 봐주시오!', imageUrl: '/assets/images/astra/emote_beg_hd.png', soundType: 'cry' },
  { id: 'bow', title: '정중한 예절', subtitle: '좋은 대국 한 수 배웠습니다!', imageUrl: '/assets/images/astra/emote_bow_hd.png', soundType: 'praise' }
];

export interface CaptureVfxSpec {
  rankOrder: number;
  calloutText: string;
  primaryColor: string;
  secondaryColor: string;
  particleCount: number;
  ringCount: number;
  maxRingRadius: number;
  shakePx: number;
  slashStyle: 'none' | 'wind' | 'hoof' | 'cannon' | 'shield_shatter' | 'cross_slash' | 'dragon_nova';
}

export const CAPTURE_VFX_BY_TYPE: Record<PieceType, CaptureVfxSpec> = {
  jol: {
    rankOrder: 1,
    calloutText: '卒 격파!',
    primaryColor: '#fde047',
    secondaryColor: '#f59e0b',
    particleCount: 14,
    ringCount: 1,
    maxRingRadius: 56,
    shakePx: 1.5,
    slashStyle: 'none'
  },
  sang: {
    rankOrder: 2,
    calloutText: '象 장거리 타격!',
    primaryColor: '#34d399',
    secondaryColor: '#fde047',
    particleCount: 24,
    ringCount: 2,
    maxRingRadius: 76,
    shakePx: 3.0,
    slashStyle: 'wind'
  },
  ma: {
    rankOrder: 3,
    calloutText: '馬 기마 분쇄!',
    primaryColor: '#38bdf8',
    secondaryColor: '#fef08a',
    particleCount: 34,
    ringCount: 2,
    maxRingRadius: 92,
    shakePx: 4.5,
    slashStyle: 'hoof'
  },
  po: {
    rankOrder: 4,
    calloutText: '包 중포 포격!!',
    primaryColor: '#fb923c',
    secondaryColor: '#ef4444',
    particleCount: 46,
    ringCount: 3,
    maxRingRadius: 115,
    shakePx: 6.5,
    slashStyle: 'cannon'
  },
  sa: {
    rankOrder: 5,
    calloutText: '士 궁성수호 파쇄!!',
    primaryColor: '#e879f9',
    secondaryColor: '#fde047',
    particleCount: 58,
    ringCount: 3,
    maxRingRadius: 135,
    shakePx: 8.5,
    slashStyle: 'shield_shatter'
  },
  cha: {
    rankOrder: 6,
    calloutText: '車 대차 일섬 격파!!!',
    primaryColor: '#ef4444',
    secondaryColor: '#fef08a',
    particleCount: 78,
    ringCount: 4,
    maxRingRadius: 170,
    shakePx: 12.0,
    slashStyle: 'cross_slash'
  },
  king: {
    rankOrder: 7,
    calloutText: '將 외통 천하제패!!!',
    primaryColor: '#fde047',
    secondaryColor: '#dc2626',
    particleCount: 115,
    ringCount: 5,
    maxRingRadius: 240,
    shakePx: 16.0,
    slashStyle: 'dragon_nova'
  }
};

interface MoveAnimationState {
  from: Pos;
  to: Pos;
  piece: Piece;
  startTime: number;
  duration: number;
}

interface CaptureParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  alpha: number;
}

interface CaptureBurstEffect {
  x: number;
  y: number;
  spec: CaptureVfxSpec;
  startTime: number;
  duration: number;
}

export interface SavedMatchRecord {
  id: string;
  title: string;
  modeLabel: string;
  outcome: 'win' | 'loss' | 'void';
  reason: string;
  dateLabel: string;
  mySide: Side;
  history: RecordedPly[];
}

export function normalizeFormation(raw: string): FormationType {
  if (raw.includes('상마상마') || raw === 'sangMaSangMa') return 'sangMaSangMa';
  if (raw.includes('안상') || raw.includes('마상상마') || raw === 'maSangSangMa') return 'maSangSangMa';
  if (raw.includes('바깥상') || raw.includes('상마마상') || raw === 'sangMaMaSang') return 'sangMaMaSang';
  return 'maSangMaSang';
}

export function formationDisplayLabel(f: FormationType): string {
  switch (f) {
    case 'sangMaSangMa':
      return '상마상마';
    case 'maSangSangMa':
      return '안상(마상상마)';
    case 'sangMaMaSang':
      return '바깥상(상마마상)';
    default:
      return '마상마상';
  }
}

// ============================================================================
// 클라이언트 상태 (프로필 · 대국 · PvP 소켓 · 훈수/위기감지 시트)
// ============================================================================
const PROFILE_STORAGE_KEY = 'janggi_user_profile_v3';
const SESSION_TOKEN_KEY = 'janggi_session_token_v1';
const FORMATION_STORAGE_KEY = 'janggi_my_formation_v1';
const MATCH_RECORDS_STORAGE_KEY = 'janggi_saved_match_records_v1';

function loadInitialProfile(): UserProfileState {
  try {
    const rawStr = localStorage.getItem(PROFILE_STORAGE_KEY);
    const parsed = rawStr ? JSON.parse(rawStr) : null;
    const migrated = migrateUserProfile(parsed);
    const refilled = applyDailyRefill(migrated, false);
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(refilled));
    return refilled;
  } catch {
    return createDefaultUserProfile('대국수');
  }
}

let userProfile: UserProfileState = loadInitialProfile();
let sessionToken: string = (() => {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY) || '';
  } catch {
    return '';
  }
})();

let myFormation: FormationType = (() => {
  try {
    const saved = localStorage.getItem(FORMATION_STORAGE_KEY);
    return saved ? normalizeFormation(saved) : 'maSangMaSang';
  } catch {
    return 'maSangMaSang';
  }
})();

let savedMatchRecords: SavedMatchRecord[] = (() => {
  try {
    const raw = localStorage.getItem(MATCH_RECORDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
})();

function saveMatchRecords(): void {
  try {
    localStorage.setItem(MATCH_RECORDS_STORAGE_KEY, JSON.stringify(savedMatchRecords.slice(0, 20)));
  } catch {
    // ignore storage quota errors
  }
}

function saveProfile(): void {
  syncFlatInventoryFromBuckets(userProfile);
  try {
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(userProfile));
  } catch {
    // ignore storage quota errors
  }
}

const SURROUND_THEME_STORAGE_KEY = 'janggi_surround_theme_v3';
export type SurroundThemeId = 'graphite' | 'warm-stone';

let surroundTheme: SurroundThemeId = (() => {
  try {
    const saved = localStorage.getItem(SURROUND_THEME_STORAGE_KEY);
    return saved === 'warm-stone' ? 'warm-stone' : 'graphite';
  } catch {
    return 'graphite';
  }
})();

const soundEngine = new SoundEngine();
let soundEnabled = soundEngine.isEnabled();
let fullscreenEnabled = false;
let deferredPwaInstallPrompt: any = null;
let guardConfig: CrisisGuardConfig = createDefaultCrisisGuardConfig();

// 현재 화면 및 세부 상태
const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
let currentScreen: string = urlParams.get('screen') || 'lobby';
if (!SCREEN_DATA.some(s => s.id === currentScreen) && currentScreen !== 'rank') {
  currentScreen = 'lobby';
}
if (typeof document !== 'undefined' && urlParams.get('capture') === '1') {
  document.body?.classList.add('capture');
}
if (typeof document !== 'undefined' && urlParams.get('launch') === 'app') {
  document.body?.classList.add('app-launch');
}

let touchDiagEnabled = urlParams.get('touchDiag') === '1';
let lastTouchDiagnostic: { rawPoint: Point; hit: HitResult } | null = null;
let lastDrawnSelectablePieces: DrawnPiece[] = [];

interface BoardDragState {
  pointerId: number;
  originPos: Pos;
  startPoint: Point;
  currentPoint: Point;
  isDragging: boolean;
  hoverTargetPos: Pos | null;
}

let activeBoardDrag: BoardDragState | null = null;
let lastCanvasPointerHandledAt = 0;
let dragRedrawRafId: number | null = null;

let resultState: 'win' | 'loss' | 'void' = (urlParams.get('result') as any) || 'win';
let resultReasonText = '상대가 기권했어요.';
let resultDeltaText = '+ 18';
let shopTab: 'help' | 'skin' = 'help';
let activeRankCategory: LeaderboardCategory = 'tier';
let connectState: 'opponent' | 'mine' = (urlParams.get('connect') as any) || 'opponent';
let hintState: 'ready' | 'revealed' = (urlParams.get('hint') as any) || 'ready';
let hintSource: 'hunsu' | 'shield' = 'hunsu';
let toastTimer: any = null;

// 실시간 대국 보드 및 상태
let activeGameMode: 'pvp' | 'ai' | 'eval' | 'campaign' | 'puzzle' | 'spectate' = 'ai';
let activeMatchSessionId = 1;
let localTurnGeneration = 1;
let spectatorTimer: any = null;
let spectatorStepInFlight = false;
let activeChapterId = 'ch_01';
let activeStageIndex = 1; // 0..4 (default 2nd stage for preview)
let activePuzzleId = 'puzzle_01';
let aiOpponentTierIndex = 9; // 9급
let puzzleMoveBudgetRemaining = 10;
let campaignMoveBudgetRemaining = 60;
let activeReviewRecordId: string | null = null;
let selectedReviewPlyIndex = 0;

let activeBoard: BoardGrid = (() => {
  const b = createInitialBoard(myFormation, 'sangMaSangMa');
  return b;
})();
let mySide: Side = 'cho';
let currentTurn: Side = 'cho';
let moveNumber = 1;
let selectedPos: Pos | null = null;
let legalTargetsForSelected: Pos[] = [];
let capturedByMe: Piece[] = [];
let capturedByOpponent: Piece[] = [];
let recordedHistory: RecordedPly[] = [];

// 30초 턴 시계 & 3회 연속 자기 차례 시간초과 실격 상태
let pvpTurnClock: TurnClock | null = null;
let localTurnClock: TurnClock | null = null;
let serverClockOffsetMs = 0;
let localRemainingBudgetStack: Array<{ budgetMs: number; isTimeoutPass: boolean }> = [];
const lastClockAudioSecondByToken = new Map<string, Set<number>>();

// 60fps 기물 슬라이딩 & 7단계 계급별 포획 타격 VFX 상태
let moveAnim: MoveAnimationState | null = null;
let captureParticles: CaptureParticle[] = [];
let activeCaptureBursts: CaptureBurstEffect[] = [];
let screenShakePx = 0;
let boardAnimRafId: number | null = null;

// 훈수/상수/천안통/방지권 상태 + Web Worker 분석 브로커 (메인 스레드 블로킹 0ms)
let currentEligibility: HunsuEligibilityResult | null = null;
const hunsuEligibilityCache = new Map<string, HunsuEligibilityResult>();
let hunsuAsyncTimer: any = null;
let hunsuEvaluationPending = false;
let assistRevealInFlight = false;
let crisisCheckInFlight = false;
let revealedCoachingForTurn: HunsuCoachingData | null = null;
let revealedTurnHash = '';
let activeSangsuVision: TacticalVisionResult | null = null;
let activeEnemyIntentVision: EnemyIntentVisionResult | null = null;
let pendingCrisis: { candidateMove: Move; crisis: CrisisInterventionResult } | null = null;
let suppressedMoveKeys = new Set<string>();
let shieldUsesInCurrentMatch = 0;
let lastMove: Move | null = null;

function createBrowserAnalysisWorker(): Worker {
  return new Worker('/analysis-worker.js?v=20261028_v3_fast1', { type: 'module' });
}

const bgAnalysisBroker = new AnalysisBroker(
  createBrowserAnalysisWorker as any,
  reqSide => buildCurrentAnalysisStamp(reqSide || mySide, activeBoard)
);
const actionAnalysisBroker = new AnalysisBroker(
  createBrowserAnalysisWorker as any,
  reqSide => buildCurrentAnalysisStamp(reqSide || currentTurn, activeBoard)
);

function setBoundedHunsuCache(key: string, val: HunsuEligibilityResult): void {
  if (!hunsuEligibilityCache.has(key) && hunsuEligibilityCache.size >= 64) {
    const oldest = hunsuEligibilityCache.keys().next().value;
    if (oldest !== undefined) hunsuEligibilityCache.delete(oldest);
  }
  hunsuEligibilityCache.set(key, val);
}

function buildCurrentAnalysisStamp(requestSide: Side = mySide, boardOverride: BoardGrid = activeBoard): AnalysisStamp {
  return {
    matchId:
      activeGameMode === 'pvp'
        ? pvpMatchId || pvpRoomId || 'pvp_unassigned'
        : `local_${activeGameMode}_${activeMatchSessionId}`,
    sessionId: activeMatchSessionId,
    epoch: activeGameMode === 'pvp' ? pvpEpoch : 1,
    version: activeGameMode === 'pvp' ? pvpVersion : recordedHistory.length,
    boardHash: getBoardHash(boardOverride, currentTurn),
    sideToMove: currentTurn,
    requestSide,
    turnGeneration: localTurnGeneration,
    engineVersion: 'janggi_kja_v1_ai_v3'
  };
}

// 화보 앨범 뷰어 상태 (20인 미녀 비서 × 5관문 화보 전체 감상)
let albumPreviewChapterId: string = userProfile.equippedSecretaryId || 'ch_01';
let albumPreviewPoseId: BeautyPoseId = userProfile.equippedSecretaryPoseId || 'pose1';
let allPosesUnlockedPreview = true;

// 6종 명품 장기판 HD 텍스처 프리로드
const boardTexturesBySkinId: Record<string, HTMLImageElement> = {};
if (typeof Image !== 'undefined') {
  for (const bSkin of BOARD_SKIN_CATALOG) {
    const img = new Image();
    img.src = bSkin.imageUrl;
    img.onload = () => renderAllBoardCanvases();
    boardTexturesBySkinId[bSkin.id] = img;
  }
}

const PIECE_RADIUS_SCALE: Record<PieceType, number> = {
  king: 1.18,
  cha: 1.05,
  po: 1.05,
  ma: 1.0,
  sang: 1.0,
  sa: 0.9,
  jol: 0.88
};

export const CANVAS_W = SQUARE_BOARD.width; // 760 (1:1 Square Board)
export const CANVAS_H = SQUARE_BOARD.height; // 760
const BOARD_PADDING_X = SQUARE_BOARD.padX; // 48
const BOARD_PADDING_Y = SQUARE_BOARD.padY; // 48
const CELL_W = (CANVAS_W - BOARD_PADDING_X * 2) / 8;
const CELL_H = (CANVAS_H - BOARD_PADDING_Y * 2) / 9;

function boardToPixel(pos: Pos): { x: number; y: number } {
  return boardToPoint(pos, mySide, SQUARE_BOARD);
}

function pixelToBoard(px: number, py: number): Pos | null {
  return hitBoard({ x: px, y: py }, mySide, SQUARE_BOARD, lastDrawnSelectablePieces);
}

// PvP 소켓 및 대국 동기화 상태 (committedBoard vs pendingMove 분리)
interface PvpPendingMoveState {
  commandId: string;
  expectedVersion: number;
  move: Move;
  sentAtMs: number;
  clackPlayed?: boolean;
}

let socket: Socket | null = null;
let pvpRoomId: string | null = null;
let pvpMatchId: string | null = null;
let pvpInviteCode = '7241';
let pvpEpoch = 1;
let pvpVersion = 0;
let awaitingMoveAck = false;
let pvpPendingMove: PvpPendingMoveState | null = null;
const appliedPvpCommitKeys = new Set<string>();
const appliedPvpResultIds = new Set<string>();
let opponentNickname = '산들바람';
let opponentTierLabel = '7급';
let graceCountdownSec = 45;

// ============================================================================
// UI 컴포넌트 빌더 헬퍼
// ============================================================================
const IMG_FALLBACK = `onerror="this.onerror=null;this.src='/assets/images/knights/knight_01_pose1.png'"`;

const b = (target: string, content: string, cls = 'btn btn-primary') =>
  `<button class="${cls}" data-go="${target}">${content}</button>`;

const act = (action: string, content: string, cls = 'btn btn-primary', extraAttr = '') =>
  `<button class="${cls}" data-action="${action}" ${extraAttr}>${content}</button>`;

function getActiveSecretaryPortrait(): string {
  const ch = getBeautyChapterById(userProfile.equippedSecretaryId || 'ch_01');
  const pose = userProfile.equippedSecretaryPoseId || 'pose1';
  return ch.poses[pose]?.imageUrl || ch.poses.pose1.imageUrl || PORTRAIT_URL;
}

function getActiveSecretaryName(): string {
  const ch = getBeautyChapterById(userProfile.equippedSecretaryId || 'ch_01');
  return ch.knightName || '한채린';
}

const avatar = (kind: 'mine' | 'opponent' | 'secretary' = 'mine', size = '') =>
  kind === 'secretary'
    ? `<img class="avatar ${size}" src="${getActiveSecretaryPortrait()}" alt="${getActiveSecretaryName()}" ${IMG_FALLBACK}>`
    : `<span class="avatar illustrated ${size}">${kind === 'opponent' ? '漢' : '楚'}</span>`;

const tag = (txt: string, type = '') => `<span class="tag ${type}">${txt}</span>`;

function header(title: string, back = 'lobby', action = 'help'): string {
  return `<header class="screen-header">${b(back, ico('back'), 'round-btn')}<div class="head-title"><h1>${title}</h1></div>${act(action, ico(action === 'help' ? 'help' : 'settings'), 'round-btn plain')}</header>`;
}

function nav(active = 'home'): string {
  const items: Array<[string, string, string]> = [
    ['home', '홈', 'lobby'],
    ['trophy', '랭킹', 'rank'],
    ['bag', '상점', 'shop'],
    ['more', '더보기', 'more']
  ];
  return (
    `<nav class="bottom-nav" aria-label="하단 메뉴">` +
    items
      .map(
        ([icon, label, id]) =>
          `<button class="nav-item ${id === active || icon === active ? 'active' : ''}" data-go="${id}">${ico(icon)}<span>${label}</span></button>`
      )
      .join('') +
    `</nav>`
  );
}

function screen(id: string, body: string, foot = ''): string {
  return `<section class="screen paper" data-screen="${id}">${body}${foot}</section>`;
}

function list(
  icon: string,
  title: string,
  copy: string,
  target: string,
  color = '',
  compact = false,
  action = false,
  extraAttr = ''
): string {
  return `<button class="list-card ${compact ? 'compact' : ''}" ${
    action ? `data-action="${target}"` : `data-go="${target}"`
  } ${extraAttr}><span class="icon-medallion ${color}">${ico(icon)}</span><span><h3>${title}</h3><p>${copy}</p></span><span class="chev">${ico('chevron')}</span></button>`;
}

// ============================================================================
// 60fps 기물 슬라이딩 애니메이션 & 7단계 계급별 포획 타격 VFX 루프 (R01)
// ============================================================================
function stepBoardAnimation(): void {
  const now = performance.now();
  let needsNextFrame = false;

  if (moveAnim) {
    if (now - moveAnim.startTime >= moveAnim.duration) {
      moveAnim = null;
    } else {
      needsNextFrame = true;
    }
  }

  if (activeCaptureBursts.length > 0) {
    activeCaptureBursts = activeCaptureBursts.filter(b => now - b.startTime < b.duration);
    if (activeCaptureBursts.length > 0) {
      needsNextFrame = true;
    }
  }

  if (captureParticles.length > 0) {
    for (const p of captureParticles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.18;
      p.alpha -= 0.032;
    }
    captureParticles = captureParticles.filter(p => p.alpha > 0.02);
    if (captureParticles.length > 0) {
      needsNextFrame = true;
    }
  }

  if (screenShakePx > 0.15) {
    screenShakePx *= 0.82;
    needsNextFrame = true;
  } else {
    screenShakePx = 0;
  }

  renderAllBoardCanvases();

  if (needsNextFrame && typeof requestAnimationFrame !== 'undefined') {
    boardAnimRafId = requestAnimationFrame(stepBoardAnimation);
  } else {
    boardAnimRafId = null;
  }
}

function ensureBoardAnimLoop(): void {
  if (boardAnimRafId === null && typeof requestAnimationFrame !== 'undefined') {
    boardAnimRafId = requestAnimationFrame(stepBoardAnimation);
  } else {
    renderAllBoardCanvases();
  }
}

export function triggerMoveAnimation(from: Pos, to: Pos, piece: Piece): void {
  moveAnim = {
    from: { ...from },
    to: { ...to },
    piece,
    startTime: performance.now(),
    duration: 120
  };
  ensureBoardAnimLoop();
}

export function triggerTieredCaptureVfx(pos: Pos, capturedType: PieceType): void {
  const spec = CAPTURE_VFX_BY_TYPE[capturedType] || CAPTURE_VFX_BY_TYPE.jol;
  const pt = boardToPixel(pos);
  activeCaptureBursts.push({
    x: pt.x,
    y: pt.y,
    spec,
    startTime: performance.now(),
    duration: 420
  });
  screenShakePx = Math.max(screenShakePx, spec.shakePx);

  for (let i = 0; i < spec.particleCount; i++) {
    const angle = (Math.PI * 2 * i) / spec.particleCount + (Math.random() - 0.5) * 0.35;
    const speed = 2.2 + Math.random() * (3.5 + spec.rankOrder * 0.9);
    captureParticles.push({
      x: pt.x,
      y: pt.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 1.2,
      radius: 2.5 + Math.random() * (2.5 + spec.rankOrder * 0.4),
      color: i % 2 === 0 ? spec.primaryColor : spec.secondaryColor,
      alpha: 1.0
    });
  }
  ensureBoardAnimLoop();
}

// ============================================================================
// 비동기 훈수 자격 평가 & 캐시 (R01 & R21: 기물 선택/착수 시 메인 스레드 블로킹 0ms)
// ============================================================================
function getHunsuCacheKey(boardGrid: BoardGrid, side: Side): string {
  return `${getBoardHash(boardGrid, side)}:${side}`;
}

function scheduleAsyncHunsuEvaluation(): void {
  if (hunsuAsyncTimer) {
    clearTimeout(hunsuAsyncTimer);
    hunsuAsyncTimer = null;
  }
  if (activeGameMode === 'spectate') {
    hunsuEvaluationPending = false;
    currentEligibility = null;
    return;
  }

  const targetBoard = activeBoard;
  const targetSide = mySide;
  const key = getHunsuCacheKey(targetBoard, targetSide);
  if (hunsuEligibilityCache.has(key)) {
    currentEligibility = hunsuEligibilityCache.get(key)!;
    hunsuEvaluationPending = false;
    updateAssistDomInPlace();
    return;
  }

  hunsuEvaluationPending = true;
  currentEligibility = null;
  updateAssistDomInPlace();

  if (typeof Worker !== 'undefined') {
    const delayMs = moveAnim ? 135 : 16;
    hunsuAsyncTimer = setTimeout(() => {
      hunsuAsyncTimer = null;
      if (getHunsuCacheKey(activeBoard, mySide) !== key) return;
      const stamp = buildCurrentAnalysisStamp(targetSide, targetBoard);
      bgAnalysisBroker
        .request<HunsuEligibilityResult>('eligibility', { board: targetBoard, side: targetSide }, stamp)
        .then(outcome => {
          if (outcome.status !== 'ok') return;
          const computed = outcome.value;
          setBoundedHunsuCache(key, computed);
          if (stamp.sessionId === activeMatchSessionId && getHunsuCacheKey(activeBoard, mySide) === key) {
            currentEligibility = computed;
            hunsuEvaluationPending = false;
            updateAssistDomInPlace();
          }
        })
        .catch(() => {
          // Superseded or cancelled worker analysis — zero item deduction
        });
    }, delayMs);
  } else {
    hunsuAsyncTimer = setTimeout(() => {
      hunsuAsyncTimer = null;
      const computed = evaluateHunsuEligibility(targetBoard, targetSide);
      setBoundedHunsuCache(key, computed);
      if (getHunsuCacheKey(activeBoard, mySide) === key) {
        currentEligibility = computed;
        hunsuEvaluationPending = false;
        updateAssistDomInPlace();
      }
    }, 12);
  }
}

function updateAssistDomInPlace(): void {
  if (typeof document === 'undefined') return;
  const btn = document.querySelector<HTMLButtonElement>('.assist-row .assist-button:first-child');
  const cap = document.querySelector<HTMLElement>('.assist-caption');

  if (hunsuEvaluationPending && currentScreen !== 'hint') {
    if (btn) {
      btn.className = 'assist-button disabled analyzing';
      btn.setAttribute('data-action', 'hint-analyzing');
      btn.innerHTML = `${ico('bulb')}분석 중`;
    }
    if (cap) {
      cap.className = 'assist-caption';
      cap.textContent = '좋은 수를 살피고 있어요 · 차감 없음';
    }
    return;
  }

  if (!currentEligibility && currentScreen !== 'hint') return;
  const isReady = currentScreen === 'hint' || currentEligibility?.status === 'eligible';
  if (btn) {
    btn.className = `assist-button ${isReady ? 'ready' : 'disabled'}`;
    btn.setAttribute('data-action', isReady ? 'hint-detail' : 'hint-waiting');
    btn.innerHTML = `${ico('bulb')}훈수${isReady ? ' 보기' : ''}`;
  }
  if (cap) {
    cap.className = `assist-caption ${isReady ? 'ready' : ''}`;
    cap.textContent = isReady
      ? '좋은 수가 있어요 · 내 화면에만 보여요'
      : '지금은 어느 수를 두어도 무난해요 · 차감 없음';
  }
}

export function isTurnClockActiveMode(mode = activeGameMode): boolean {
  return mode === 'pvp' || mode === 'ai' || mode === 'eval' || mode === 'campaign';
}

function getActiveTurnClock(): TurnClock | null {
  if (activeGameMode === 'pvp') return pvpTurnClock;
  if (isTurnClockActiveMode()) return localTurnClock;
  return null;
}

function getActiveClockNowMs(): number {
  return activeGameMode === 'pvp' ? Date.now() + serverClockOffsetMs : Date.now();
}

function handleLocalTurnTimeout(): void {
  if (!localTurnClock || localTurnClock.status !== 'running' || activeGameMode === 'pvp' || !isTurnClockActiveMode()) {
    return;
  }
  if (!['game', 'hint', 'shield'].includes(currentScreen)) {
    return;
  }
  const nowMs = Date.now();
  if (remainingMs(localTurnClock, nowMs) > 0) return;

  const timedOutSide = currentTurn;
  const inCheck = isKingInCheck(activeBoard, timedOutSide);
  const exp = expireTurn(localTurnClock, localTurnClock.token, nowMs, inCheck);
  if (exp.kind === 'ignored') return;
  localTurnClock = exp.clock;
  localTurnGeneration += 1;

  // 진행 중이던 선택/시트/워커 탐색 정리 (미열람 아이템 차감 0회 보장)
  selectedPos = null;
  legalTargetsForSelected = [];
  activeBoardDrag = null;
  pendingCrisis = null;
  crisisCheckInFlight = false;
  assistRevealInFlight = false;
  actionAnalysisBroker.cancel('state_changed', true);
  bgAnalysisBroker.cancel();

  if (exp.kind === 'idle_forfeit' || exp.kind === 'time_forfeit_in_check') {
    const winnerSide: Side = exp.winner || (timedOutSide === 'cho' ? 'han' : 'cho');
    const outcome: 'win' | 'loss' = winnerSide === mySide ? 'win' : 'loss';
    const reasonMsg =
      exp.kind === 'time_forfeit_in_check'
        ? outcome === 'loss'
          ? '장군 상태에서 제한시간(30초)을 넘겨 패배했어요.'
          : '상대가 장군 상태에서 시간초과되어 승리했어요!'
        : outcome === 'loss'
        ? '3회 연속 시간초과로 실격패했어요.'
        : '상대의 3회 연속 시간초과로 승리했어요!';
    handleLocalMatchFinished(outcome, reasonMsg);
    return;
  }

  // 비장군 1·2회차 시간초과 -> 자동 턴 넘김(timeout_pass), lastMove(직전 실제 착수 표시)는 그대로 보존!
  const snapshotBoard = activeBoard.map(r => r.map(c => (c ? { ...c } : null)));
  recordedHistory.push({
    ply: recordedHistory.length + 1,
    side: timedOutSide,
    move: {
      from: { row: 0, col: 0 },
      to: { row: 0, col: 0 },
      piece: { id: 'timeout_pass_king', type: 'king', side: timedOutSide },
      captured: null,
      notation: '시간초과쉼'
    },
    boardBefore: snapshotBoard,
    boardAfter: snapshotBoard,
    elapsedSeconds: 30
  });
  localRemainingBudgetStack.push({ budgetMs: 0, isTimeoutPass: true });

  moveNumber += 1;
  currentTurn = localTurnClock.token.side;
  const streak = localTurnClock.idleStreak[timedOutSide] ?? 1;

  if (soundEnabled) {
    soundEngine.playSfx('sfx_byoyomi_tick', 0.9);
  }

  if (timedOutSide === mySide) {
    showToast(`시간초과로 차례가 넘어갔어요 (연속 시간초과 ${streak}/${IDLE_LIMIT})`);
  } else {
    showToast(`상대 시간초과로 내 차례가 되었어요 (상대 시간초과 ${streak}/${IDLE_LIMIT})`);
  }

  if (currentScreen !== 'game') {
    renderScreen('game', true);
  } else {
    updateGameHudInPlace();
    renderAllBoardCanvases();
  }

  if (currentTurn !== mySide) {
    scheduleAiTurnResponse(activeMatchSessionId, currentTurn);
  }
}

export function updateClockHud(): void {
  if (typeof document === 'undefined') return;
  if (!['game', 'hint', 'shield', 'reconnect'].includes(currentScreen)) return;

  if (activeGameMode !== 'pvp' && isTurnClockActiveMode() && localTurnClock && localTurnClock.status === 'running') {
    if (remainingMs(localTurnClock, Date.now()) <= 0) {
      handleLocalTurnTimeout();
      return;
    }
  }

  const oppSide: Side = mySide === 'cho' ? 'han' : 'cho';
  const oppLine = document.querySelector<HTMLElement>('.player-line');
  const selfLine = document.querySelector<HTMLElement>('.self-line');
  if (oppLine) {
    oppLine.classList.toggle('is-active-turn', activeGameMode !== 'spectate' && currentTurn === oppSide);
  }
  if (selfLine) {
    selfLine.classList.toggle('is-active-turn', activeGameMode !== 'spectate' && currentTurn === mySide);
  }

  const clk = getActiveTurnClock();
  const barEl = document.querySelector<HTMLElement>('.turn-clock-bar');
  if (!clk || !barEl) return;

  const nowMs = getActiveClockNowMs();
  const durMs = clk.turnDurationMs || TURN_MS;
  const remMs = Math.max(0, Math.min(durMs, remainingMs(clk, nowMs)));
  const remSec = Math.ceil(remMs / 1000);
  const pct = Math.max(0, Math.min(100, (remMs / durMs) * 100));
  const isMyTurn = currentTurn === mySide;
  const isPaused = clk.status === 'paused';

  barEl.classList.toggle('is-my-turn', isMyTurn);
  barEl.classList.toggle('is-opp-turn', !isMyTurn);
  barEl.classList.toggle('is-paused', isPaused);
  barEl.classList.toggle('is-warning', !isPaused && remSec <= 10 && remSec > 5);
  barEl.classList.toggle('is-danger', !isPaused && remSec <= 5);

  const labelEl = barEl.querySelector<HTMLElement>('.turn-clock-label');
  if (labelEl) {
    const pendingTag = activeGameMode === 'pvp' && pvpPendingMove ? ' · 전송 중' : '';
    labelEl.textContent = isPaused
      ? '연결 대기 · 시간 정지'
      : (isMyTurn ? '내 차례' : '상대 차례') + pendingTag;
  }

  const secEl = barEl.querySelector<HTMLElement>('.turn-clock-seconds');
  if (secEl) {
    secEl.textContent = `${String(remSec).padStart(2, '0')}초`;
  }

  const fillEl = barEl.querySelector<HTMLElement>('.turn-clock-fill');
  if (fillEl) {
    fillEl.style.width = `${pct.toFixed(2)}%`;
  }

  const myStreak = clk.idleStreak[mySide] ?? 0;
  const oppStreak = clk.idleStreak[oppSide] ?? 0;

  const myStreakEl = document.querySelector<HTMLElement>('.my-idle-pill');
  if (myStreakEl) {
    myStreakEl.textContent = `시간초과 ${myStreak}/${IDLE_LIMIT}`;
    myStreakEl.classList.toggle('has-warning', myStreak > 0);
    myStreakEl.classList.toggle('is-critical', myStreak >= 2);
  }

  const oppStreakEl = document.querySelector<HTMLElement>('.opp-idle-pill');
  if (oppStreakEl) {
    oppStreakEl.textContent = `시간초과 ${oppStreak}/${IDLE_LIMIT}`;
    oppStreakEl.classList.toggle('has-warning', oppStreak > 0);
    oppStreakEl.classList.toggle('is-critical', oppStreak >= 2);
  }

  // 내 차례 10초 / 5~1초 초읽기 음성 및 경고음 (토큰당 초별 1회만 재생)
  if (
    soundEnabled &&
    !isPaused &&
    clk.status === 'running' &&
    isMyTurn &&
    ['game', 'hint', 'shield'].includes(currentScreen) &&
    (remSec === 10 || (remSec <= 5 && remSec >= 1))
  ) {
    const tokenKey = `${clk.token.epoch}:${clk.token.turnId}:${clk.token.side}`;
    let fired = lastClockAudioSecondByToken.get(tokenKey);
    if (!fired) {
      if (lastClockAudioSecondByToken.size > 16) {
        lastClockAudioSecondByToken.clear();
      }
      fired = new Set<number>();
      lastClockAudioSecondByToken.set(tokenKey, fired);
    }
    if (!fired.has(remSec)) {
      fired.add(remSec);
      if (remSec === 10) {
        soundEngine.playByoyomiStart();
      } else if (remSec === 5) {
        soundEngine.playByoyomiCountdown();
      } else {
        soundEngine.playSfx('sfx_byoyomi_tick', 0.55);
      }
    }
  }
}

function updateGameHudInPlace(): void {
  if (typeof document === 'undefined') return;
  const turnTxt = document.querySelector<HTMLElement>('.turn-row .turn-text');
  if (turnTxt) {
    turnTxt.innerHTML = `<i class="turn-dot"></i>${
      activeGameMode === 'spectate'
        ? `AI 9단 실시간 관전 중 (${currentTurn === 'cho' ? '초 차례' : '한 차례'})`
        : currentTurn === mySide
        ? '내 차례예요'
        : '상대 차례예요'
    }`;
  }
  const moveNumEl = document.querySelector<HTMLElement>('.turn-row .move-number');
  if (moveNumEl) {
    moveNumEl.textContent =
      activeGameMode === 'puzzle'
        ? `${moveNumber}번째 수 · 남은 수순 ${puzzleMoveBudgetRemaining}수`
        : `${moveNumber}번째 수`;
  }
  const oppCapEl = document.querySelector<HTMLElement>('.player-line .player-info small');
  if (oppCapEl) {
    const oppSummary =
      capturedByOpponent.length > 0
        ? formatCapturedGroupBadgesHtml(capturedByOpponent, mySide === 'cho' ? 'han' : 'cho')
        : '<span class="muted">없음</span>';
    oppCapEl.innerHTML = `상대가 잡은 말　${oppSummary}`;
  }
  const myCapEl = document.querySelector<HTMLElement>('.self-line .capture-count');
  if (myCapEl) {
    const mySummary =
      capturedByMe.length > 0 ? formatCapturedGroupBadgesHtml(capturedByMe, mySide) : '<span class="muted">없음</span>';
    myCapEl.innerHTML = `잡은 말　${mySummary}`;
  }
  updateClockHud();
  scheduleAsyncHunsuEvaluation();
}

// ============================================================================
// 9x10 한국장기판 1:1 정방형(760x760) DPR 고해상도 HTML5 Canvas 렌더러
// (6종 명품 장기판 9-Slice 테두리 분리 + 6종 팔각 수제 장기알 baseRadius=32)
// ============================================================================
function board(mode: 'game' | 'hint' | 'puzzle' = 'game', compact = false): string {
  return (
    `<div class="board-wrap ${compact ? 'compact' : ''}">` +
    `<canvas class="board janggi-canvas" data-board-mode="${mode}" data-board-compact="${compact ? '1' : '0'}" width="${CANVAS_W}" height="${CANVAS_H}" role="img" aria-label="9열 10행 교차점과 양쪽 궁을 가진 정방형 한국장기판"></canvas>` +
    `</div>`
  );
}

function syncCanvasBackingStore(canvasEl: HTMLCanvasElement, ctx: CanvasRenderingContext2D): number {
  let cssW = Number(canvasEl.dataset.cssWidth) || 0;
  let cssH = Number(canvasEl.dataset.cssHeight) || 0;
  if ((cssW <= 0 || cssH <= 0) && typeof canvasEl.getBoundingClientRect === 'function') {
    const rect = canvasEl.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      cssW = rect.width;
      cssH = rect.height;
      canvasEl.dataset.cssWidth = String(Math.round(cssW));
      canvasEl.dataset.cssHeight = String(Math.round(cssH));
    }
  }
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  if (cssW > 0 && cssH > 0) {
    const bs = backingStore(
      { left: 0, top: 0, width: cssW, height: cssH },
      SQUARE_BOARD,
      dpr
    );
    if (canvasEl.width !== bs.width || canvasEl.height !== bs.height) {
      canvasEl.width = bs.width;
      canvasEl.height = bs.height;
    }
    ctx.setTransform(bs.scaleX, 0, 0, bs.scaleY, 0, 0);
    return bs.scaleX;
  } else {
    if (canvasEl.width !== CANVAS_W || canvasEl.height !== CANVAS_H) {
      canvasEl.width = CANVAS_W;
      canvasEl.height = CANVAS_H;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return 1;
  }
}

function drawBoardTexture9Slice(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  dr: number
): void {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const srRightW = Math.max(1, iw - (sx + sw));
  const srBottomH = Math.max(1, ih - (sy + sh));
  const dwCenter = CANVAS_W - dr * 2;
  const dhCenter = CANVAS_H - dr * 2;

  // Corners
  ctx.drawImage(img, 0, 0, sx, sy, 0, 0, dr, dr);
  ctx.drawImage(img, sx + sw, 0, srRightW, sy, CANVAS_W - dr, 0, dr, dr);
  ctx.drawImage(img, 0, sy + sh, sx, srBottomH, 0, CANVAS_H - dr, dr, dr);
  ctx.drawImage(img, sx + sw, sy + sh, srRightW, srBottomH, CANVAS_W - dr, CANVAS_H - dr, dr, dr);

  // Edges
  ctx.drawImage(img, sx, 0, sw, sy, dr, 0, dwCenter, dr);
  ctx.drawImage(img, sx, sy + sh, sw, srBottomH, dr, CANVAS_H - dr, dwCenter, dr);
  ctx.drawImage(img, 0, sy, sx, sh, 0, dr, dr, dhCenter);
  ctx.drawImage(img, sx + sw, sy, srRightW, sh, CANVAS_W - dr, dr, dr, dhCenter);

  // Clean inner wood playfield [dr .. CANVAS_W - dr]
  ctx.drawImage(img, sx, sy, sw, sh, dr, dr, dwCenter, dhCenter);
}

function drawPalaceDiagonalsOnCtx(ctx: CanvasRenderingContext2D, startRow: number, endRow: number, strokeColor: string): void {
  const p1 = boardToPixel({ row: startRow, col: 3 });
  const p2 = boardToPixel({ row: endRow, col: 5 });
  const p3 = boardToPixel({ row: startRow, col: 5 });
  const p4 = boardToPixel({ row: endRow, col: 3 });
  ctx.save();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 2.1;
  ctx.beginPath();
  ctx.moveTo(p1.x, p1.y);
  ctx.lineTo(p2.x, p2.y);
  ctx.moveTo(p3.x, p3.y);
  ctx.lineTo(p4.x, p4.y);
  ctx.stroke();
  ctx.restore();
}

function drawTacticalArrowOnCtx(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
  width = 4.5,
  dashed = false
): void {
  const headLen = 16;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  if (dashed) ctx.setLineDash([8, 6]);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2 - headLen * 0.7 * Math.cos(angle), y2 - headLen * 0.7 * Math.sin(angle));
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawBoardCalloutPillOnCtx(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  text: string,
  bgColor: string,
  borderColor: string,
  textColor = '#ffffff'
): void {
  ctx.save();
  ctx.font = '900 13.5px "JnSans", "Malgun Gothic", sans-serif';
  const padX = 12;
  const textW = ctx.measureText(text).width;
  const boxW = Math.max(96, textW + padX * 2);
  const boxH = 26;
  const clampedX = Math.max(boxW / 2 + 8, Math.min(CANVAS_W - boxW / 2 - 8, x));
  const clampedY = Math.max(boxH / 2 + 8, Math.min(CANVAS_H - boxH / 2 - 8, y));

  ctx.shadowColor = 'rgba(0, 0, 0, 0.75)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle = bgColor;
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(clampedX - boxW / 2, clampedY - boxH / 2, boxW, boxH, 13);
  ctx.fill();
  ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.fillStyle = textColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, clampedX, clampedY);
  ctx.restore();
}

// ★ 오프스크린 정적 장기판 레이어 & 팔각 장기알 스프라이트 캐시 (매 프레임 32개 shadowBlur/그라디언트 재계산 제거 -> 60/120fps 무렉)
const boardBgSpriteCache = new Map<string, HTMLCanvasElement>();
const pieceSpriteCache = new Map<string, { canvas: HTMLCanvasElement; logicalSize: number; baseRadius: number }>();

export function clearCanvasSpriteCaches(): void {
  boardBgSpriteCache.clear();
  pieceSpriteCache.clear();
}

// ★ 6종 명품 수제 팔각 입체 장기알 벡터 렌더러 (760x760 정방형 격자에 맞춘 baseRadius = 32 비례 크기)
function renderOctagonalPieceVector(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  piece: Piece,
  scaleMultiplier = 1,
  skin: PieceSkinItem = PIECE_SKIN_CATALOG[1]
): number {
  const baseRadius = 32 * PIECE_RADIUS_SCALE[piece.type] * scaleMultiplier;
  const isCho = piece.side === 'cho';
  const gradColors = isCho ? skin.choBgGradient : skin.hanBgGradient;

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.62)';
  ctx.shadowBlur = 9 * scaleMultiplier;
  ctx.shadowOffsetY = 4 * scaleMultiplier;

  const drawOctagonPath = (r: number) => {
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI / 4) * i + Math.PI / 8;
      const vx = cx + r * Math.cos(angle);
      const vy = cy + r * Math.sin(angle);
      if (i === 0) ctx.moveTo(vx, vy);
      else ctx.lineTo(vx, vy);
    }
    ctx.closePath();
  };

  drawOctagonPath(baseRadius);
  const bodyGrad = ctx.createRadialGradient(
    cx - baseRadius * 0.25,
    cy - baseRadius * 0.25,
    baseRadius * 0.1,
    cx,
    cy,
    baseRadius
  );
  bodyGrad.addColorStop(0, gradColors[0]);
  bodyGrad.addColorStop(0.58, gradColors[1]);
  bodyGrad.addColorStop(1, gradColors[2]);
  ctx.fillStyle = bodyGrad;
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.lineWidth = 2.8;
  ctx.strokeStyle = isCho ? skin.choBorderColor : skin.hanBorderColor;
  ctx.stroke();

  drawOctagonPath(baseRadius * 0.83);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = skin.innerRingColor;
  ctx.stroke();

  if (skin.hasMetallicStuds) {
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI / 4) * i + Math.PI / 8;
      const sx = cx + baseRadius * 0.9 * Math.cos(angle);
      const sy = cy + baseRadius * 0.9 * Math.sin(angle);
      ctx.fillStyle = '#fde047';
      ctx.beginPath();
      ctx.arc(sx, sy, 2.0 * scaleMultiplier, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const meta = PIECE_NAMES_KO[piece.type];
  const hanja = isCho ? meta.hanjaCho : meta.hanjaHan;
  const fontSize = Math.round(baseRadius * 1.12);
  ctx.font = `900 ${fontSize}px "JnSerif", "Batang", "Noto Serif KR", serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillStyle = isCho ? skin.choTextShadow : skin.hanTextShadow;
  ctx.fillText(hanja, cx + 1.0, cy + 1.3);

  ctx.fillStyle = isCho ? skin.choTextColor : skin.hanTextColor;
  ctx.fillText(hanja, cx, cy - 1);

  const korLabel = isCho ? meta.cho : meta.han;
  ctx.font = `800 ${Math.round(baseRadius * 0.31)}px "JnSans", "Malgun Gothic", sans-serif`;
  ctx.fillStyle = isCho ? skin.choTextColor : skin.hanTextColor;
  ctx.fillText(korLabel, cx, cy + baseRadius * 0.62);

  ctx.restore();
  return baseRadius;
}

function drawOctagonalPieceOnCtx(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  piece: Piece,
  scaleMultiplier = 1,
  skin: PieceSkinItem = PIECE_SKIN_CATALOG[1],
  dprScale = 1
): number {
  const baseRadius = 32 * PIECE_RADIUS_SCALE[piece.type] * scaleMultiplier;
  if (typeof document !== 'undefined' && dprScale > 0) {
    const dprBucket = Math.max(0.5, Math.min(2.5, Math.round(dprScale * 20) / 20));
    const cacheKey = `${skin.id}:${piece.side}:${piece.type}:${scaleMultiplier.toFixed(2)}:${dprBucket.toFixed(2)}`;
    let sprite = pieceSpriteCache.get(cacheKey);
    if (!sprite) {
      const pad = Math.ceil(16 * scaleMultiplier + 6);
      const logicalSize = Math.ceil((baseRadius + pad) * 2);
      const off = document.createElement('canvas');
      off.width = Math.max(1, Math.round(logicalSize * dprBucket));
      off.height = off.width;
      const offCtx = off.getContext('2d');
      if (offCtx) {
        offCtx.setTransform(off.width / logicalSize, 0, 0, off.height / logicalSize, 0, 0);
        renderOctagonalPieceVector(offCtx, logicalSize / 2, logicalSize / 2, piece, scaleMultiplier, skin);
        if (pieceSpriteCache.size >= 256) {
          const oldest = pieceSpriteCache.keys().next().value;
          if (oldest !== undefined) pieceSpriteCache.delete(oldest);
        }
        sprite = { canvas: off, logicalSize, baseRadius };
        pieceSpriteCache.set(cacheKey, sprite);
      }
    }
    if (sprite) {
      const half = sprite.logicalSize / 2;
      ctx.drawImage(sprite.canvas, cx - half, cy - half, sprite.logicalSize, sprite.logicalSize);
      return sprite.baseRadius;
    }
  }
  return renderOctagonalPieceVector(ctx, cx, cy, piece, scaleMultiplier, skin);
}

function renderStaticBoardSurfaceOnCtx(ctx: CanvasRenderingContext2D, boardSkin: BoardSkinItem): void {
  // 1) 장착된 6종 명품 장기판 HD 텍스처 (테두리 각인 스킨 2종은 9-Slice 분리로 격자/기물 침범 원천 차단)
  const bgImg = boardTexturesBySkinId[boardSkin.id];
  if (bgImg && bgImg.complete && bgImg.naturalWidth > 0) {
    if (boardSkin.id === 'board_royal_pine') {
      drawBoardTexture9Slice(ctx, bgImg, 160, 160, 704, 704, 12);
    } else if (boardSkin.id === 'board_rosewood_lacquer') {
      drawBoardTexture9Slice(ctx, bgImg, 224, 224, 576, 576, 12);
    } else {
      ctx.drawImage(bgImg, 0, 0, CANVAS_W, CANVAS_H);
    }
    ctx.fillStyle = boardSkin.surfaceTint;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  } else {
    const grad = ctx.createRadialGradient(
      CANVAS_W / 2,
      CANVAS_H / 2,
      50,
      CANVAS_W / 2,
      CANVAS_H / 2,
      CANVAS_W * 0.75
    );
    grad.addColorStop(0, '#e8bf82');
    grad.addColorStop(1, '#b87d32');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  }

  const lineColor = boardSkin.gridLineColor;

  // 테두리 좌표 각인 (1~9열, 1~10행 — 은은하고 깔끔한 여백 배치)
  ctx.save();
  ctx.globalAlpha = 0.72;
  ctx.fillStyle = boardSkin.coordinateColor;
  ctx.font = '700 11.5px "JnSans", "Malgun Gothic", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let c = 0; c < 9; c++) {
    const x = BOARD_PADDING_X + c * CELL_W;
    const labelCol = mySide === 'cho' ? c + 1 : 9 - c;
    ctx.fillText(`${labelCol}`, x, 13);
  }
  for (let r = 0; r < 10; r++) {
    const y = BOARD_PADDING_Y + r * CELL_H;
    const labelRow = mySide === 'cho' ? r + 1 : 10 - r;
    ctx.fillText(`${labelRow}`, 13, y);
  }
  ctx.restore();

  // 2) 9x10 격자선 및 궁성 대각선
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 2.0;
  for (let r = 0; r < 10; r++) {
    const y = BOARD_PADDING_Y + r * CELL_H;
    ctx.beginPath();
    ctx.moveTo(BOARD_PADDING_X, y);
    ctx.lineTo(BOARD_PADDING_X + 8 * CELL_W, y);
    ctx.stroke();
  }
  for (let c = 0; c < 9; c++) {
    const x = BOARD_PADDING_X + c * CELL_W;
    ctx.beginPath();
    ctx.moveTo(x, BOARD_PADDING_Y);
    ctx.lineTo(x, BOARD_PADDING_Y + 9 * CELL_H);
    ctx.stroke();
  }
  drawPalaceDiagonalsOnCtx(ctx, 0, 2, lineColor);
  drawPalaceDiagonalsOnCtx(ctx, 7, 9, lineColor);

  // 3) 포·졸 화점
  const starPoints: [number, number][] = [
    [1, 2], [7, 2],
    [0, 3], [2, 3], [4, 3], [6, 3], [8, 3],
    [0, 6], [2, 6], [4, 6], [6, 6], [8, 6],
    [1, 7], [7, 7]
  ];
  for (const [sc, sr] of starPoints) {
    const p = boardToPixel({ col: sc, row: sr });
    ctx.fillStyle = lineColor;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 3.8, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawStaticBoardLayerCached(
  ctx: CanvasRenderingContext2D,
  boardSkin: BoardSkinItem,
  backingW: number,
  backingH: number
): void {
  if (typeof document !== 'undefined' && backingW > 0 && backingH > 0) {
    const bgImg = boardTexturesBySkinId[boardSkin.id];
    const bgReady = bgImg && bgImg.complete && bgImg.naturalWidth > 0 ? 1 : 0;
    const key = `${boardSkin.id}:${mySide}:${bgReady}:${backingW}x${backingH}`;
    let cached = boardBgSpriteCache.get(key);
    if (!cached) {
      const off = document.createElement('canvas');
      off.width = backingW;
      off.height = backingH;
      const offCtx = off.getContext('2d');
      if (offCtx) {
        offCtx.setTransform(backingW / CANVAS_W, 0, 0, backingH / CANVAS_H, 0, 0);
        renderStaticBoardSurfaceOnCtx(offCtx, boardSkin);
        if (boardBgSpriteCache.size >= 4) {
          const oldest = boardBgSpriteCache.keys().next().value;
          if (oldest !== undefined) boardBgSpriteCache.delete(oldest);
        }
        cached = off;
        boardBgSpriteCache.set(key, cached);
      }
    }
    if (cached) {
      ctx.drawImage(cached, 0, 0, CANVAS_W, CANVAS_H);
      return;
    }
  }
  renderStaticBoardSurfaceOnCtx(ctx, boardSkin);
}

function drawCanvasBoard(canvasEl: HTMLCanvasElement): void {
  const ctx = canvasEl.getContext('2d');
  if (!ctx) return;

  const dprScale = syncCanvasBackingStore(canvasEl, ctx);

  const mode = (canvasEl.getAttribute('data-board-mode') || 'game') as 'game' | 'hint' | 'puzzle';
  const compact = canvasEl.getAttribute('data-board-compact') === '1';

  const boardSkin: BoardSkinItem = getBoardSkinById(userProfile.equippedBoardSkin || 'board_zelkova');
  const pieceSkin: PieceSkinItem = getPieceSkinById(userProfile.equippedPieceSkin || 'piece_boxwood_master');

  const gridToRender: BoardGrid =
    mode === 'puzzle' && compact
      ? ENDGAME_PUZZLE_STAGES.find(s => s.id === activePuzzleId)?.setupBoard() || ENDGAME_PUZZLE_STAGES[0].setupBoard()
      : activeBoard;

  const drawnPiecesThisFrame: DrawnPiece[] = [];

  ctx.save();
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

  if (!compact && screenShakePx > 0.2) {
    const dx = (Math.random() - 0.5) * screenShakePx * 2;
    const dy = (Math.random() - 0.5) * screenShakePx * 2;
    ctx.translate(dx, dy);
  }

  // 1~3) 오프스크린 캐싱된 장기판 나무결·좌표·9x10 격자선·궁성 대각선·화점 단일 drawImage 블릿
  drawStaticBoardLayerCached(ctx, boardSkin, canvasEl.width, canvasEl.height);

  // 4) 호신강기 보호 대상 기물 바닥 오라 링
  if (!compact) {
    for (let r = 0; r < 10; r++) {
      for (let c = 0; c < 9; c++) {
        if (
          activeGameMode === 'pvp' &&
          pvpPendingMove &&
          pvpPendingMove.move.from.row === r &&
          pvpPendingMove.move.from.col === c
        ) {
          continue;
        }
        const p = gridToRender[r]?.[c];
        if (
          p &&
          p.side === mySide &&
          p.type !== 'jol' &&
          p.type !== 'king' &&
          guardConfig[p.type as GuardablePieceType]
        ) {
          const pt = boardToPixel({ row: r, col: c });
          ctx.save();
          ctx.strokeStyle = 'rgba(56, 189, 248, 0.55)';
          ctx.lineWidth = 2.8;
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 34 * PIECE_RADIUS_SCALE[p.type], 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }
      }
    }
  }

  // 5) 최근 확정 착수 지점 표시 (lastCommittedMove — 패스나 선택으로 사라지지 않고 보존)
  if (!compact && lastMove) {
    const fromPx = boardToPixel(lastMove.from);
    const toPx = boardToPixel(lastMove.to);
    ctx.save();
    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 2.8;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.arc(fromPx.x, fromPx.y, 16, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    drawTacticalArrowOnCtx(ctx, fromPx.x, fromPx.y, toPx.x, toPx.y, 'rgba(251, 191, 36, 0.85)', 4.2);
    ctx.strokeStyle = '#fef08a';
    ctx.lineWidth = 4.0;
    ctx.beginPath();
    ctx.arc(toPx.x, toPx.y, 35.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // 6) 선택된 기물 및 합법 이동 칸 하이라이트 (+ 드래그 중 자석 타겟 락온 링)
  if (!compact && selectedPos) {
    const sp = boardToPixel(selectedPos);
    ctx.save();
    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 4.6;
    ctx.beginPath();
    ctx.arc(sp.x, sp.y, 37.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    for (const targetPos of legalTargetsForSelected) {
      const mp = boardToPixel(targetPos);
      const isCapture = gridToRender[targetPos.row]?.[targetPos.col] !== null;
      const isHoverTarget = Boolean(
        activeBoardDrag?.isDragging &&
          activeBoardDrag.hoverTargetPos &&
          activeBoardDrag.hoverTargetPos.row === targetPos.row &&
          activeBoardDrag.hoverTargetPos.col === targetPos.col
      );
      ctx.save();
      if (isCapture) {
        ctx.strokeStyle = isHoverTarget ? '#fde047' : '#ef4444';
        ctx.lineWidth = isHoverTarget ? 5.4 : 4.2;
        ctx.beginPath();
        ctx.arc(mp.x, mp.y, isHoverTarget ? 37.5 : 35, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.fillStyle = isHoverTarget ? 'rgba(16, 185, 129, 0.34)' : 'rgba(16, 185, 129, 0.18)';
        ctx.beginPath();
        ctx.arc(mp.x, mp.y, isHoverTarget ? 31 : 22, 0, Math.PI * 2);
        ctx.fill();
        if (isHoverTarget) {
          ctx.strokeStyle = '#34d399';
          ctx.lineWidth = 3.6;
          ctx.stroke();
        }
        ctx.fillStyle = 'rgba(16, 185, 129, 0.96)';
        ctx.beginPath();
        ctx.arc(mp.x, mp.y, 12.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2.4;
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  // 7) 6종 수제 팔각 입체 장기알 스프라이트 렌더링 + PvP Pending 단일 기물 분리 + 드래그/슬라이딩 보간
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      if (!compact && moveAnim && moveAnim.to.row === r && moveAnim.to.col === c) {
        continue;
      }
      // PvP 서버 ACK 대기 중: 출발 칸 기물을 중복 그리지 않음!
      if (
        !compact &&
        activeGameMode === 'pvp' &&
        pvpPendingMove &&
        pvpPendingMove.move.from.row === r &&
        pvpPendingMove.move.from.col === c
      ) {
        continue;
      }
      const piece = gridToRender[r]?.[c];
      if (piece) {
        const pt = boardToPixel({ row: r, col: c });
        const isPendingTargetEnemy =
          !compact &&
          activeGameMode === 'pvp' &&
          pvpPendingMove &&
          pvpPendingMove.move.to.row === r &&
          pvpPendingMove.move.to.col === c;
        const isBeingDragged =
          !compact &&
          activeBoardDrag?.isDragging &&
          activeBoardDrag.originPos.row === r &&
          activeBoardDrag.originPos.col === c;
        const isSelected = !compact && selectedPos && selectedPos.row === r && selectedPos.col === c;
        const drawY = isSelected && !isBeingDragged ? pt.y - 4 : pt.y;
        const scale = isSelected && !isBeingDragged ? 1.07 : 1;

        if (isPendingTargetEnemy) {
          ctx.save();
          ctx.globalAlpha = 0.24;
          drawOctagonalPieceOnCtx(ctx, pt.x, drawY, piece, scale, pieceSkin, dprScale);
          ctx.restore();
        } else if (isBeingDragged) {
          ctx.save();
          ctx.globalAlpha = 0.3;
          const drawnRadius = drawOctagonalPieceOnCtx(ctx, pt.x, pt.y, piece, 1, pieceSkin, dprScale);
          ctx.restore();
          drawnPiecesThisFrame.push({
            pos: { row: r, col: c },
            center: { x: pt.x, y: pt.y },
            radius: drawnRadius
          });
        } else {
          const drawnRadius = drawOctagonalPieceOnCtx(ctx, pt.x, drawY, piece, scale, pieceSkin, dprScale);
          if (!compact) {
            drawnPiecesThisFrame.push({
              pos: { row: r, col: c },
              center: { x: pt.x, y: drawY },
              radius: drawnRadius
            });
          }
        }
      }
    }
  }

  // 7-A) PvP 서버 승인 대기 중인 착수(pvpPendingMove) 단일 기물 투영 (슬라이딩 중 이중 기물 완전 차단)
  if (!compact && activeGameMode === 'pvp' && pvpPendingMove) {
    const pFrom = boardToPixel(pvpPendingMove.move.from);
    const pTo = boardToPixel(pvpPendingMove.move.to);
    const isSlidingIntoPending = Boolean(
      moveAnim &&
        moveAnim.to.row === pvpPendingMove.move.to.row &&
        moveAnim.to.col === pvpPendingMove.move.to.col
    );
    ctx.save();
    ctx.strokeStyle = 'rgba(63, 210, 199, 0.85)';
    ctx.lineWidth = 2.6;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.arc(pFrom.x, pFrom.y, 15, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    drawTacticalArrowOnCtx(ctx, pFrom.x, pFrom.y, pTo.x, pTo.y, 'rgba(63, 210, 199, 0.88)', 4.2, true);
    if (!isSlidingIntoPending) {
      ctx.globalAlpha = 0.96;
      drawOctagonalPieceOnCtx(ctx, pTo.x, pTo.y - 2, pvpPendingMove.move.piece, 1.05, pieceSkin, dprScale);
      ctx.globalAlpha = 1;
    }
    ctx.strokeStyle = '#3fd2c7';
    ctx.lineWidth = 3.6;
    ctx.beginPath();
    ctx.arc(pTo.x, pTo.y - 2, 36.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    if (Date.now() - pvpPendingMove.sentAtMs >= 200) {
      drawBoardCalloutPillOnCtx(ctx, pTo.x, pTo.y - 44, '전송 중...', '#0f766e', '#5eead4');
    }
  }

  if (!compact && moveAnim) {
    const elapsed = Math.max(0, performance.now() - moveAnim.startTime);
    const t = Math.min(1, elapsed / moveAnim.duration);
    const easeOut = 1 - Math.pow(1 - t, 3);
    const fromPt = boardToPixel(moveAnim.from);
    const toPt = boardToPixel(moveAnim.to);
    const curX = fromPt.x + (toPt.x - fromPt.x) * easeOut;
    const curY = fromPt.y + (toPt.y - fromPt.y) * easeOut - Math.sin(t * Math.PI) * 12;
    drawOctagonalPieceOnCtx(ctx, curX, curY, moveAnim.piece, 1.11, pieceSkin, dprScale);
  }

  // 7-A2) 손가락/마우스 드래그 중인 내 기물 플로팅 렌더링 (시각적 즉각 추종)
  if (!compact && activeBoardDrag?.isDragging) {
    const draggedPiece = gridToRender[activeBoardDrag.originPos.row]?.[activeBoardDrag.originPos.col];
    if (draggedPiece && draggedPiece.side === mySide) {
      drawOctagonalPieceOnCtx(
        ctx,
        activeBoardDrag.currentPoint.x,
        Math.max(24, activeBoardDrag.currentPoint.y - 16),
        draggedPiece,
        1.14,
        pieceSkin,
        dprScale
      );
    }
  }

  // 7-B) 7단계 계급별 포획 타격 버스트 & 파티클 렌더링 (R01)
  if (!compact && activeCaptureBursts.length > 0) {
    const now = performance.now();
    for (const burst of activeCaptureBursts) {
      const progress = Math.min(1, Math.max(0, (now - burst.startTime) / burst.duration));
      const alpha = 1 - progress;
      ctx.save();
      for (let rIdx = 0; rIdx < burst.spec.ringCount; rIdx++) {
        const ringProg = Math.min(1, progress * (1 + rIdx * 0.22));
        const rad = 18 + burst.spec.maxRingRadius * ringProg;
        ctx.strokeStyle = rIdx % 2 === 0 ? burst.spec.primaryColor : burst.spec.secondaryColor;
        ctx.globalAlpha = alpha * (1 - rIdx * 0.18);
        ctx.lineWidth = Math.max(1.5, 5.5 - rIdx * 1.1);
        ctx.beginPath();
        ctx.arc(burst.x, burst.y, rad, 0, Math.PI * 2);
        ctx.stroke();
      }
      if (burst.spec.slashStyle === 'cross_slash' || burst.spec.slashStyle === 'dragon_nova') {
        const slashLen = burst.spec.maxRingRadius * 0.88;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 4.5;
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.moveTo(burst.x - slashLen, burst.y - slashLen);
        ctx.lineTo(burst.x + slashLen, burst.y + slashLen);
        ctx.moveTo(burst.x + slashLen, burst.y - slashLen);
        ctx.lineTo(burst.x - slashLen, burst.y + slashLen);
        ctx.stroke();
      }
      ctx.restore();
      drawBoardCalloutPillOnCtx(
        ctx,
        burst.x,
        burst.y - 44 - progress * 16,
        burst.spec.calloutText,
        burst.spec.secondaryColor,
        burst.spec.primaryColor
      );
    }
  }

  if (!compact && captureParticles.length > 0) {
    ctx.save();
    for (const ptc of captureParticles) {
      ctx.globalAlpha = Math.max(0, ptc.alpha);
      ctx.fillStyle = ptc.color;
      ctx.beginPath();
      ctx.arc(ptc.x, ptc.y, ptc.radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // 8) 상수(象手) 3턴 궤적 오버레이
  if (!compact && activeSangsuVision && activeSangsuVision.nodes.length > 0) {
    for (const br of activeSangsuVision.branches) {
      const fromPx = boardToPixel(br.from);
      const toPx = boardToPixel(br.to);
      drawTacticalArrowOnCtx(ctx, fromPx.x, fromPx.y, toPx.x, toPx.y, br.color, br.turn === 1 ? 4.5 : 3.5);
    }
    for (const node of activeSangsuVision.nodes) {
      const toPx = boardToPixel(node.pos);
      ctx.save();
      ctx.fillStyle = node.color;
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(toPx.x, toPx.y, node.isTargetLock ? 20 : 12.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = node.turn === 1 ? '#000' : '#fff';
      ctx.font = '900 11.5px "JnSans", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(node.isTargetLock ? '타겟' : `${node.turn}턴`, toPx.x, toPx.y);
      ctx.restore();
    }
  }

  // 9) 천안통(상대 노림수) 투시 오버레이
  if (!compact && activeEnemyIntentVision) {
    if (activeEnemyIntentVision.primaryEnemyThreat) {
      const pFrom = boardToPixel(activeEnemyIntentVision.primaryEnemyThreat.from);
      const pTo = boardToPixel(activeEnemyIntentVision.primaryEnemyThreat.to);
      drawTacticalArrowOnCtx(ctx, pFrom.x, pFrom.y, pTo.x, pTo.y, '#c084fc', 5.5, false);
      drawBoardCalloutPillOnCtx(ctx, pTo.x, pTo.y - 42, '[적 노림수] 1차 침투', '#7e22ce', '#fde047');
    }
    if (activeEnemyIntentVision.enemyFollowUpStrike) {
      const fFrom = boardToPixel(activeEnemyIntentVision.enemyFollowUpStrike.from);
      const fTo = boardToPixel(activeEnemyIntentVision.enemyFollowUpStrike.to);
      drawTacticalArrowOnCtx(ctx, fFrom.x, fFrom.y, fTo.x, fTo.y, '#f43f5e', 5, true);
      drawBoardCalloutPillOnCtx(ctx, fTo.x, fTo.y + 40, '[결정타!] 후속 타격', '#be123c', '#fecdd3');
    }
  }

  // 10) 훈수(訓手) 및 호신강기 안전 대안수 화살표 + 말풍선 오버레이
  if (mode === 'hint' || (currentScreen === 'shield' && pendingCrisis)) {
    const recMove =
      (hintSource === 'shield' || currentScreen === 'shield') && pendingCrisis
        ? pendingCrisis.crisis.recommendedSafeMove
        : revealedCoachingForTurn?.step1Move;

    if (currentScreen === 'shield' && pendingCrisis) {
      const dangerPx = boardToPixel(pendingCrisis.crisis.guardedPiecePos);
      const myPieceShort = PIECE_NAMES_KO[pendingCrisis.crisis.guardedPiece.type][mySide];
      ctx.save();
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 5.0;
      ctx.beginPath();
      ctx.arc(dangerPx.x, dangerPx.y, 40, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      drawBoardCalloutPillOnCtx(ctx, dangerPx.x, dangerPx.y - 45, `[경고] 내 [${myPieceShort}] 위험!`, '#dc2626', '#fef08a');
    }

    if (recMove) {
      const pFrom = boardToPixel(recMove.from);
      const pTo = boardToPixel(recMove.to);
      const stepShort = PIECE_NAMES_KO[recMove.piece.type][mySide];
      drawTacticalArrowOnCtx(ctx, pFrom.x, pFrom.y, pTo.x, pTo.y, '#10b981', 6.0);
      ctx.save();
      ctx.fillStyle = 'rgba(16, 185, 129, 0.28)';
      ctx.strokeStyle = '#34d399';
      ctx.lineWidth = 4.2;
      ctx.beginPath();
      ctx.arc(pTo.x, pTo.y, 35, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      drawBoardCalloutPillOnCtx(ctx, pTo.x, pTo.y - 43, `[추천수] [${stepShort}] 여기로!`, '#059669', '#fef08a');
    } else if (mode === 'hint') {
      const pFrom = boardToPixel({ row: 9, col: 0 });
      const pTo = boardToPixel({ row: 7, col: 0 });
      drawTacticalArrowOnCtx(ctx, pFrom.x, pFrom.y, pTo.x, pTo.y, '#10b981', 6.0);
      drawBoardCalloutPillOnCtx(ctx, pTo.x + 28, pTo.y - 40, `[추천수] [차] 여기로!`, '#059669', '#fef08a');
    }

    if (mode === 'hint' && revealedCoachingForTurn?.step2FollowUpMove) {
      const fFrom = boardToPixel(revealedCoachingForTurn.step2FollowUpMove.from);
      const fTo = boardToPixel(revealedCoachingForTurn.step2FollowUpMove.to);
      const step2Short = PIECE_NAMES_KO[revealedCoachingForTurn.step2FollowUpMove.piece.type][mySide];
      drawTacticalArrowOnCtx(ctx, fFrom.x, fFrom.y, fTo.x, fTo.y, '#38bdf8', 4.8, true);
      drawBoardCalloutPillOnCtx(ctx, fTo.x, fTo.y + 42, `[후속수] 그다음 [${step2Short}]`, '#0369a1', '#7dd3fc');
    }
  }

  // 11) 선택형 터치 좌표 진단 오버레이 (?touchDiag=1)
  if (!compact && touchDiagEnabled && lastTouchDiagnostic) {
    const { rawPoint, hit } = lastTouchDiagnostic;
    ctx.save();
    ctx.strokeStyle = '#22d3ee';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(rawPoint.x - 10, rawPoint.y);
    ctx.lineTo(rawPoint.x + 10, rawPoint.y);
    ctx.moveTo(rawPoint.x, rawPoint.y - 10);
    ctx.lineTo(rawPoint.x, rawPoint.y + 10);
    ctx.stroke();
    if (hit) {
      const hp = boardToPixel(hit.pos);
      ctx.strokeStyle = hit.kind === 'piece' ? '#4ade80' : '#facc15';
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.arc(hp.x, hp.y, 34, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  if (!compact) {
    lastDrawnSelectablePieces = drawnPiecesThisFrame;
  }

  ctx.restore();
}

export function renderAllBoardCanvases(): void {
  if (typeof document === 'undefined') return;
  document.querySelectorAll<HTMLCanvasElement>('canvas.janggi-canvas').forEach(c => {
    drawCanvasBoard(c);
  });
}

// ============================================================================
// 14개 화면 뷰 렌더러 (01_lobby ~ 14_more + 4대 랭킹 서열전)
// ============================================================================
function renderLobbyView(): string {
  const tierSpec = getTierByIndex(userProfile.tierIndex);
  const secPortrait = getActiveSecretaryPortrait();

  return (
    `<section class="screen lobby" data-screen="lobby">` +
    `<header class="lobby-header">` +
    `<button class="profile-chip" data-go="more">` +
    `${avatar()}` +
    `<span><strong>${userProfile.nickname} · ${tierSpec.label}</strong><small>오늘도, 즐거운 한 수</small></span>` +
    `</button>` +
    `${act('settings', ico('settings'), 'round-btn')}` +
    `</header>` +
    `<div class="lobby-content">` +
    `<div class="lobby-logo">` +
    `<p>한 판의 여유</p>` +
    `<h1>천하장기<span class="seal">將棋</span></h1>` +
    `<div class="ink-rule"></div>` +
    `</div>` +
    `<button class="lobby-greeting" data-go="secretary"><img src="${secPortrait}" alt="">오늘도 한 판, 같이해요.</button>` +
    `<div class="lobby-spacer"></div>` +
    `<div class="primary-trio">` +
    `<button class="portal friend" data-go="friends">${ico('people')}<span>친구랑</span></button>` +
    `<button class="portal main" data-go="matching"><span class="stone-mini">將</span><span>대국 시작</span></button>` +
    `<button class="portal solo" data-go="solo">${ico('cap')}<span>혼자 두기</span></button>` +
    `</div>` +
    `<div class="lobby-board" aria-hidden="true">` +
    `<div class="lobby-board-plate"><div class="mini-grid"></div>` +
    `<span class="lobby-piece blue" style="left:61%;top:2%">車</span>` +
    `<span class="lobby-piece" style="left:18%;top:31%">馬</span>` +
    `<span class="lobby-piece" style="left:65%;top:53%">將</span>` +
    `<span class="lobby-piece blue" style="left:33%;top:6%">兵</span>` +
    `</div></div>` +
    `<div class="lobby-secondary">` +
    `<button data-go="puzzles">${ico('bulb')}정답찾기</button>` +
    `<button data-go="secretary"><img src="${secPortrait}" alt="">내 비서</button>` +
    `</div>` +
    `</div>` +
    `${nav('home')}` +
    `</section>`
  );
}

function renderFriendsView(): string {
  return screen(
    'friends',
    header('친구랑 두기') +
      `<div class="screen-body">` +
      `<div class="friend-hero">` +
      `<span class="icon-medallion blue">${ico('people')}</span>` +
      `<h2>아는 사람과<br>편하게 한 판.</h2>` +
      `<p>초대 링크만 보내면 돼요.</p>` +
      `${act('invite', ico('share') + '친구 초대하기')}` +
      `</div>` +
      `<div class="friend-code card">` +
      `<h3>방 번호가 있나요?</h3>` +
      `<div class="code-input-row">` +
      `<input id="friend-room-code-input" aria-label="방 번호" inputmode="numeric" maxlength="4" placeholder="숫자 4자리">` +
      `${act('join', '입장', 'btn btn-primary')}` +
      `</div>` +
      `</div>` +
      `<div class="help-row">${ico('lock')}초대한 친구와 함께 두어요.</div>` +
      `</div>`,
    nav()
  );
}

function renderMatchingView(): string {
  return screen(
    'matching',
    header('대국 시작') +
      `<div class="matchmaking-body">` +
      `<div class="match-rings"><div class="inner-ring"></div><span class="orbit-dot"></span><span class="stone-mini">將</span></div>` +
      `<h2>상대를 찾고 있어요</h2>` +
      `<p>잠시만 기다려 주세요.</p>` +
      `<div class="match-users">` +
      `<div class="match-user">${avatar()}<strong>${userProfile.nickname}</strong></div>` +
      `<span class="match-vs">對</span>` +
      `<div class="match-user waiting"><span class="avatar illustrated">${ico('people')}</span><span>찾는 중</span></div>` +
      `</div>` +
      `<div class="matchmaking-hint">${ico('leaf')}대국이 시작되면<br>내 비서가 함께해요.</div>` +
      `<div class="matchmaking-actions">` +
      `<div class="center muted" style="font-size:16px;padding:12px 0">상대를 기다리고 있어요.</div>` +
      `${act('cancel-matching', '그만 찾기', 'btn btn-outline')}` +
      `</div>` +
      `</div>`
  );
}

function renderSoloView(): string {
  const ch = getBeautyChapterById(activeChapterId);
  return screen(
    'solo',
    header('혼자 두기') +
      `<div class="screen-body">` +
      `<div class="resume-card">` +
      `<div class="eyebrow">이어서 즐기기</div>` +
      `<div class="row between">` +
      `<div><h3>미녀대국 · 두 번째 만남</h3><p>${ch.knightName}과(와) 한 판 더 두어볼까요?</p></div>` +
      `<button class="resume-arrow" data-go="campaign">${ico('arrow')}</button>` +
      `</div>` +
      `</div>` +
      `<div class="solo-divider">오늘은 어떻게 둘까요?</div>` +
      `<div class="stack">` +
      `${list('cap', 'AI와 한 판', '내 실력에 맞는 편안한 대국', 'ai-start', '', false, true)}` +
      `${list('heart', '미녀대국', '새로운 비서를 만나보세요', 'campaign', 'rose')}` +
      `${list('bulb', '정답찾기', '짧게 풀고, 한 수씩 익혀요', 'puzzles', 'gold')}` +
      `</div>` +
      `<div class="solo-extra">` +
      `${act('spectate', ico('eye') + 'AI 고수 구경', '')}` +
      `${act('level', ico('target') + '내 실력 알아보기', '')}` +
      `</div>` +
      `</div>`,
    nav()
  );
}

function renderSecretaryView(): string {
  const ch = getBeautyChapterById(userProfile.equippedSecretaryId || 'ch_01');
  const activePose: BeautyPoseId = userProfile.equippedSecretaryPoseId || 'pose1';
  const poseSpec = ch.poses[activePose] || ch.poses.pose1;
  const secPortrait = getActiveSecretaryPortrait();
  const poseIdx = (['pose1', 'pose2', 'pose3', 'pose4', 'pose5'] as BeautyPoseId[]).indexOf(activePose) + 1;

  return screen(
    'secretary',
    header('내 비서') +
      `<div class="screen-body secretary-body">` +
      `<div class="secretary-hero">` +
      `<img src="${secPortrait}" alt="전속 비서 ${ch.knightName}" ${IMG_FALLBACK}>` +
      `${tag(ico('check') + `${ch.chapterNumber}장 · ${poseIdx}관문 화보 동행 중`)}` +
      `<div class="secretary-identity"><p>${ch.titleBadge} · ${ch.tierLabel}</p><h2>${ch.knightName}</h2><span>${poseSpec.poseTitle}</span></div>` +
      `</div>` +
      `<p class="secretary-quote">“${poseSpec.quote || '좋은 수가 보이면 제가 먼저 알려드릴게요.'}”</p>` +
      `<div class="secretary-buttons">` +
      `${act('choose-secretary', ico('people') + '비서 바꾸기 (20인)', 'btn btn-secondary')}` +
      `${act('album', ico('heart') + '화보 보기 (5관문)', 'btn btn-primary')}` +
      `</div>` +
      `<button class="secretary-bottom" data-go="campaign" style="width:100%;text-align:left">` +
      `<span>새로운 만남이 기다려요<strong>미녀대국으로 가기</strong></span><span class="chev">${ico('chevron')}</span>` +
      `</button>` +
      `</div>`,
    nav()
  );
}

function renderCampaignView(): string {
  const ch = getBeautyChapterById(activeChapterId);
  const clearedStars = getChapterClearedStars(ch, userProfile.beautyStagesCleared);
  const currentGateNum = Math.min(5, Math.max(1, clearedStars + 1));
  const poseKey = (`pose${currentGateNum}` as BeautyPoseId) || 'pose1';
  const heroPortrait = ch.poses[poseKey]?.imageUrl || ch.poses.pose1.imageUrl;

  const nodesHtml = [1, 2, 3, 4, 5]
    .map(n => {
      const stId = `${ch.id}_st_${n}`;
      const isDone = userProfile.beautyStagesCleared.includes(stId);
      const isCurr = n === currentGateNum && !isDone;
      return `<span class="progress-node ${isDone ? 'done' : isCurr ? 'current' : ''}" data-action="start-campaign-gate" data-gate="${n}">${
        isDone ? ico('check') : n
      }</span>`;
    })
    .join('');

  const previewChapters = BEAUTY_KNIGHT_CHAPTERS.slice(0, 4);
  const previewRowHtml = previewChapters
    .map(
      pCh =>
        `<button class="mini-portrait" data-action="select-chapter-${pCh.id}" title="${pCh.knightName}"><img src="${pCh.poses.pose1.imageUrl}" alt="${pCh.knightName}" ${IMG_FALLBACK}></button>`
    )
    .join('');

  return screen(
    'campaign',
    header('미녀대국', 'solo') +
      `<div class="screen-body">` +
      `<div class="campaign-hero">` +
      `<img src="${heroPortrait}" alt="${ch.knightName}" ${IMG_FALLBACK}>` +
      `<div class="campaign-hero-copy">${tag(`${ch.chapterNumber}번째 이야기 · ${ch.tierLabel}`, 'gold')}<h2>${ch.knightName}</h2><p>${ch.titleBadge} · 5관문 화보 도전</p></div>` +
      `</div>` +
      `<div class="chapter-progress-wrap">` +
      `<div class="row between"><h3>다섯 번의 만남</h3><small>${Math.min(5, clearedStars)} / 5</small></div>` +
      `<div class="campaign-progress">${nodesHtml}</div>` +
      `</div>` +
      `<div class="chapter-next">` +
      `<span class="icon-medallion rose">${ico('heart')}</span>` +
      `<div><h3>${currentGateNum}관문 · ${ch.poses[poseKey]?.poseTitle || '새로운 화보'}</h3><p>1~5관문 모두 훈수를 받으며 도전할 수 있어요.</p></div>` +
      `</div>` +
      `${act('start-campaign-current', `${currentGateNum}번째 대국 시작`, 'btn btn-red')}` +
      `<div class="section-row"><h2>미녀 비서 20인</h2>${act('roster', '20명 전체 보기', '')}</div>` +
      `<div class="mini-portrait-row">${previewRowHtml}</div>` +
      `</div>`,
    nav()
  );
}

function renderPuzzlesView(): string {
  const stage = ENDGAME_PUZZLE_STAGES.find(s => s.id === activePuzzleId) || ENDGAME_PUZZLE_STAGES[0];
  const classicCardsHtml = ENDGAME_PUZZLE_STAGES.map(
    st =>
      `<button class="classic-card card" data-action="classic-problem" data-puzzle-id="${st.id}" style="width:100%;text-align:left;color:var(--ink);margin-bottom:8px;${
        st.id === activePuzzleId ? 'border:2px solid #c6933a' : ''
      }">` +
      `<span class="stone-mini classic-stone">車</span>` +
      `<span><h3>${st.title}</h3><p>${st.subtitle} · 제한 ${st.maxMovesToWin}수 (보상 金 ${st.rewardCoins.toLocaleString()})</p></span>` +
      `<span class="chev">${ico('chevron')}</span>` +
      `</button>`
  ).join('');

  return screen(
    'puzzles',
    header('정답찾기', 'solo') +
      `<div class="screen-body">` +
      `<div class="puzzle-hero">` +
      `${tag(`오늘의 한 문제 · ${stage.title}`, 'gold')}` +
      `<h2>좋은 수를<br>찾아볼까요?</h2>` +
      `${board('puzzle', true)}` +
      `${act('start-puzzle', `${stage.title.replace(/\(.*\)/, '')} 문제 풀기`, 'btn btn-primary', `data-puzzle-id="${stage.id}"`)}` +
      `<p class="puzzle-caption">${stage.description}</p>` +
      `</div>` +
      `<div class="section-row"><h2>고전 명국 5선</h2>${act('classic-list', '모두 보기', '')}</div>` +
      `${classicCardsHtml}` +
      `</div>`,
    nav()
  );
}

function renderGameBase(id = 'game'): string {
  if (activeGameMode !== 'pvp' && isTurnClockActiveMode() && !localTurnClock) {
    localTurnClock = createTurnClock(
      `local_${activeGameMode}_${activeMatchSessionId}`,
      1,
      currentTurn,
      Date.now(),
      TURN_MS
    );
  }

  const cacheKey = getHunsuCacheKey(activeBoard, mySide);
  if (hunsuEligibilityCache.has(cacheKey)) {
    currentEligibility = hunsuEligibilityCache.get(cacheKey)!;
    hunsuEvaluationPending = false;
  } else {
    scheduleAsyncHunsuEvaluation();
  }

  const isHintScreen = id === 'hint';
  const isReady = isHintScreen || (!hunsuEvaluationPending && currentEligibility?.status === 'eligible');
  const tierSpec = getTierByIndex(userProfile.tierIndex);
  const oppSide: Side = mySide === 'cho' ? 'han' : 'cho';

  const oppCapturedSummary =
    capturedByOpponent.length > 0
      ? formatCapturedGroupBadgesHtml(capturedByOpponent, oppSide)
      : '<span class="muted">없음</span>';
  const myCapturedSummary =
    capturedByMe.length > 0
      ? formatCapturedGroupBadgesHtml(capturedByMe, mySide)
      : '<span class="muted">없음</span>';

  const modeTitle =
    activeGameMode === 'pvp'
      ? '실시간 대국'
      : activeGameMode === 'campaign'
      ? `미녀대국 · ${activeStageIndex + 1}관문`
      : activeGameMode === 'puzzle'
      ? '정답찾기'
      : activeGameMode === 'eval'
      ? '실력 측정 심사국'
      : activeGameMode === 'spectate'
      ? 'AI 9단 실시간 관전'
      : 'AI 대국';

  const turnStatusText =
    activeGameMode === 'spectate'
      ? `AI 9단 실시간 관전 중 (${currentTurn === 'cho' ? '초 차례' : '한 차례'})`
      : currentTurn === mySide
      ? '내 차례예요'
      : '상대 차례예요';

  const moveBudgetText =
    activeGameMode === 'puzzle'
      ? `${moveNumber}번째 수 · 남은 수순 ${puzzleMoveBudgetRemaining}수`
      : `${moveNumber}번째 수`;

  const puzzleExtraBar =
    activeGameMode === 'puzzle'
      ? `<div class="row between puzzle-extra-row" style="gap:8px;margin-bottom:4px">` +
        `${act('retry-puzzle', ico('refresh') + '다시 풀기', 'btn btn-secondary', 'style="flex:1;min-height:36px;padding:5px 10px;font-size:13px"')}` +
        `${act('extend-moves', `+3수 연장 (보유 ${userProfile.ownedMoveExtensions || 0})`, 'btn btn-secondary', 'style="flex:1;min-height:36px;padding:5px 10px;font-size:13px"')}` +
        `</div>`
      : '';

  const headerStatusChip =
    activeGameMode === 'pvp'
      ? `<span class="connection-chip"><span></span>${socket?.connected ? '연결됨' : '동기화'}</span>`
      : activeGameMode === 'spectate'
      ? `<span class="connection-chip"><span></span>관전 중</span>`
      : `<span class="connection-chip solo-mode-chip">${activeGameMode === 'puzzle' ? '묘수풀이' : '30초 대국'}</span>`;

  const clockActive = isTurnClockActiveMode();
  const clk = getActiveTurnClock();
  const durMs = clk?.turnDurationMs || TURN_MS;
  const remMs =
    clockActive && clk
      ? Math.max(0, Math.min(durMs, remainingMs(clk, getActiveClockNowMs())))
      : TURN_MS;
  const remSec = Math.ceil(remMs / 1000);
  const pct = Math.max(0, Math.min(100, (remMs / durMs) * 100));
  const isMyTurn = currentTurn === mySide;
  const isPaused = clk?.status === 'paused';
  const myStreak = clk?.idleStreak[mySide] ?? 0;
  const oppStreak = clk?.idleStreak[oppSide] ?? 0;

  const turnClockBarHtml = clockActive
    ? `<div class="turn-clock-bar ${isMyTurn ? 'is-my-turn' : 'is-opp-turn'} ${isPaused ? 'is-paused' : ''} ${
        !isPaused && remSec <= 10 && remSec > 5 ? 'is-warning' : ''
      } ${!isPaused && remSec <= 5 ? 'is-danger' : ''}">` +
      `<div class="turn-clock-top">` +
      `<span class="turn-clock-label">${
        isPaused
          ? '연결 대기 · 시간 정지'
          : (isMyTurn ? '내 차례' : '상대 차례') + (activeGameMode === 'pvp' && pvpPendingMove ? ' · 전송 중' : '')
      }</span>` +
      `<span class="turn-clock-meta">` +
      `<span class="idle-streak-pill my-idle-pill ${myStreak > 0 ? 'has-warning' : ''} ${
        myStreak >= 2 ? 'is-critical' : ''
      }">시간초과 ${myStreak}/${IDLE_LIMIT}</span>` +
      `<strong class="turn-clock-seconds">${String(remSec).padStart(2, '0')}초</strong>` +
      `</span>` +
      `</div>` +
      `<div class="turn-clock-track" role="progressbar" aria-valuemin="0" aria-valuemax="30" aria-valuenow="${remSec}">` +
      `<div class="turn-clock-fill" style="width:${pct.toFixed(2)}%"></div>` +
      `</div>` +
      `</div>`
    : '';

  const themeCls = surroundTheme === 'warm-stone' ? 'theme-warm-stone' : 'theme-graphite';
  const oppActiveCls = activeGameMode !== 'spectate' && currentTurn === oppSide ? 'is-active-turn' : '';
  const selfActiveCls = activeGameMode !== 'spectate' && currentTurn === mySide ? 'is-active-turn' : '';

  const assistBtnLabel =
    hunsuEvaluationPending && !isHintScreen
      ? `${ico('bulb')}분석 중`
      : `${ico('bulb')}훈수${isReady ? ' 보기' : ''}`;
  const assistBtnAction =
    hunsuEvaluationPending && !isHintScreen
      ? 'hint-analyzing'
      : isReady
      ? 'hint-detail'
      : 'hint-waiting';
  const assistCaptionText =
    hunsuEvaluationPending && !isHintScreen
      ? '좋은 수를 살피고 있어요 · 차감 없음'
      : isReady
      ? '좋은 수가 있어요 · 내 화면에만 보여요'
      : '지금은 어느 수를 두어도 무난해요 · 차감 없음';

  return (
    `<section class="screen paper game-screen ${themeCls}" data-screen="${id}">` +
    `<div class="game-header">` +
    `${act('game-menu', ico('back'), 'round-btn plain')}` +
    `<h1>${modeTitle}</h1>` +
    `${headerStatusChip}` +
    `${act('game-menu', ico('more'), 'round-btn plain')}` +
    `</div>` +
    `<div class="player-line ${oppActiveCls}">` +
    `<span class="avatar illustrated">${oppSide === 'han' ? '漢' : '楚'}</span>` +
    `<div class="player-info"><strong>${opponentNickname} <span class="muted small">· ${opponentTierLabel}</span></strong><small>상대가 잡은 말　${oppCapturedSummary}</small></div>` +
    (clockActive
      ? `<span class="idle-streak-pill opp-idle-pill ${oppStreak > 0 ? 'has-warning' : ''} ${
          oppStreak >= 2 ? 'is-critical' : ''
        }">시간초과 ${oppStreak}/${IDLE_LIMIT}</span>`
      : '') +
    `<span class="side-tag ${oppSide}">${oppSide === 'han' ? '漢 · 한' : '楚 · 초'}</span>` +
    `</div>` +
    `${turnClockBarHtml}` +
    `<div class="match-main" style="position:relative">` +
    `<div id="floating-emote-slot" style="position:absolute;top:10px;right:12px;z-index:30;pointer-events:none"></div>` +
    `${board(isHintScreen && hintState === 'revealed' ? 'hint' : 'game')}` +
    `<div class="turn-row"><span class="turn-text"><i class="turn-dot"></i>${turnStatusText}</span><span class="move-number">${moveBudgetText}</span></div>` +
    `${puzzleExtraBar}` +
    `<div class="self-line ${selfActiveCls}">${avatar('secretary')}<strong>${userProfile.nickname} · ${tierSpec.label}</strong><span class="capture-count">잡은 말　${myCapturedSummary}</span><span class="side-tag ${mySide}">${mySide === 'cho' ? '楚 · 초' : '漢 · 한'}</span></div>` +
    `<div class="assist-row">` +
    `<button class="assist-button ${isReady ? 'ready' : 'disabled'}" data-action="${assistBtnAction}">${assistBtnLabel}</button>` +
    `${act('sangsu', ico('eye') + '상수', 'assist-button')}` +
    `${act('game-menu', ico('more') + '메뉴', 'assist-button menu')}` +
    `</div>` +
    `<div class="assist-caption ${isReady ? 'ready' : ''}">${assistCaptionText}</div>` +
    `</div>`
  );
}

function renderHintView(): string {
  const buckets = ensureInventoryBuckets(userProfile);
  if (hintState === 'ready') {
    return (
      renderGameBase('hint') +
      `<div class="state-sheet hint-sheet ready-hint-sheet">` +
      `<div class="sheet-handle"></div>` +
      `<div class="hint-title">${avatar('secretary')}<div><h2>좋은 수가 있어요</h2><p>아직 횟수를 쓰지 않았어요.</p></div></div>` +
      `<div class="hint-quota">오늘 무료 훈수 <strong>${buckets.dailyAllowance.hunsu}회 남음</strong> (구매 ${buckets.purchasedInventory.hunsu}회)</div>` +
      `${act('reveal-hint', '좋은 수 보기 · 1회', 'btn btn-primary')}` +
      `<p class="hint-private">${ico('lock')}도움은 내 화면에만 보여요.</p>` +
      `</div></section>`
    );
  }

  const explainText =
    hintSource === 'shield' && pendingCrisis
      ? `<strong>${formatKoreanNotation(
          pendingCrisis.crisis.recommendedSafeMove.piece,
          pendingCrisis.crisis.recommendedSafeMove.from,
          pendingCrisis.crisis.recommendedSafeMove.to,
          pendingCrisis.crisis.recommendedSafeMove.captured
        )}</strong> 수로 안전하게 지켜보세요.`
      : revealedCoachingForTurn
      ? `<strong>${revealedCoachingForTurn.step1Description}</strong><br>${revealedCoachingForTurn.step2Description}`
      : `<strong>차를 표시한 자리로 옮겨볼까요?</strong><br>다음 수는 상대가 둔 뒤 다시 봐요.`;

  return (
    renderGameBase('hint') +
    `<div class="state-sheet hint-sheet revealed-hint-sheet">` +
    `<div class="sheet-handle"></div>` +
    `<div class="hint-title">${avatar('secretary')}<div><h2>이 수를 살펴볼까요?</h2></div></div>` +
    `<p class="hint-explain">${explainText}</p>` +
    `<div class="hint-receipt">${ico('check')}${
      hintSource === 'shield' ? '실수 막기 1회 사용' : '오늘 무료 훈수 1회 사용'
    }</div>` +
    `<div class="sheet-actions">${b('game', '닫기', 'btn btn-secondary')}${act('apply-hint', '이 수 두기', 'btn btn-primary')}</div>` +
    `<p class="hint-private">${ico('lock')}나에게만 보이는 도움</p>` +
    `</div></section>`
  );
}

function renderShieldView(): string {
  const copy = pendingCrisis
    ? pendingCrisis.crisis.explanation
    : '계획한 수라면 그대로 두세요.';
  return (
    renderGameBase('shield') +
    `<div class="state-veil"></div>` +
    `<div class="state-sheet shield-sheet">` +
    `<div class="sheet-handle"></div>` +
    `<div class="shield-heading"><span class="icon-medallion">${ico('shield')}</span><h2>이 수, 확인할까요?</h2></div>` +
    `<p class="warning-copy">${copy}</p>` +
    `<div class="free-warning">${ico('check')}아직 방지권 사용 전이에요.</div>` +
    `<div class="sheet-actions">` +
    `${act('keep-move', '그대로 두기', 'btn btn-secondary')}` +
    `${act('safe-move', '안전한 수 보기<br>· 1회', 'btn btn-primary')}` +
    `</div>` +
    `<p class="hint-private">추천을 확인할 때만 사용해요.</p>` +
    `</div></section>`
  );
}

function renderReconnectView(): string {
  const mine = connectState === 'mine';
  const mm = String(Math.floor(graceCountdownSec / 60)).padStart(2, '0');
  const ss = String(graceCountdownSec % 60).padStart(2, '0');
  return (
    renderGameBase('reconnect') +
    `<div class="reconnect-overlay">` +
    `<div class="reconnect-card">` +
    `<div class="connection-rings">${ico('wifi')}</div>` +
    `<h2>${mine ? '내 연결을<br>다시 확인하고 있어요' : '상대 연결을<br>확인하고 있어요'}</h2>` +
    `<p>아직 대국이 끝난 것은 아니에요.<br>잠시만 기다려 주세요.</p>` +
    `<div class="reconnect-countdown">${mm}:${ss}</div>` +
    `<div class="reconnect-label">연결을 기다리는 시간</div>` +
    `<div class="connection-checklist">` +
    `<span>${ico('check')}마지막 대국판을 보관했어요.</span>` +
    `<span>${ico('refresh')}연결되면 차례를 다시 맞춰요.</span>` +
    `</div>` +
    `${act('restore', '다시 연결 확인', 'btn btn-outline')}` +
    `</div></div></section>`
  );
}

function renderResultView(): string {
  const win = resultState === 'win';
  const isVoid = resultState === 'void';
  const secPortrait = getActiveSecretaryPortrait();
  const secName = getActiveSecretaryName();

  return screen(
    'result',
    header('대국 결과') +
      `<div class="result-body">` +
      `<div class="result-medallion ${win ? '' : 'void'}">${ico(win ? 'trophy' : 'refresh')}</div>` +
      `<h2>${win ? '이겼어요!' : isVoid ? '대국 무효' : '아쉽게 졌어요'}</h2>` +
      `<p class="result-reason">${
        win ? resultReasonText : isVoid ? '연결이 다시 이어지지 않았어요.' : resultReasonText || '다음 판에서 설욕해 보세요.'
      }</p>` +
      `<div class="result-score">` +
      `<div><small>대국 결과</small><strong>${win ? '승리' : isVoid ? '기록 제외' : '패배'}</strong></div>` +
      `<span class="score-separator"></span>` +
      `<div><small>대국 점수</small><strong>${win ? resultDeltaText : isVoid ? '변동 없음' : resultDeltaText || '- 16'}</strong></div>` +
      `</div>` +
      (win
        ? `<div class="result-companion"><img src="${secPortrait}" alt="${secName}" ${IMG_FALLBACK}><p><strong>내 비서 ${secName}</strong>좋은 한 판이었어요.<br>멋진 수를 다시 볼까요?</p></div>`
        : `<div class="result-void-copy">${
            isVoid
              ? '승패와 점수가 바뀌지 않아요.<br>받지 못한 도움은 차감하지 않아요.'
              : '패배 시에는 비서 축하 팝업이 노출되지 않아요.<br>복기로 승부처를 확인해 보세요.'
          }</div>`) +
      `<div class="result-actions">` +
      `${act('rematch', win ? '한 판 더 두기' : '다시 대국하기', 'btn btn-primary')}` +
      `${act('review', '이 대국 다시 보기', 'btn btn-secondary')}` +
      `</div>` +
      `${b('lobby', '홈으로', 'result-footer-link')}` +
      `</div>`
  );
}

function renderProductCard(
  icon: string,
  title: string,
  copy: string,
  price: string,
  color: string,
  skuId: string
): string {
  return (
    `<div class="product-card">` +
    `<span class="icon-medallion ${color}">${ico(icon)}</span>` +
    `<div class="product-info"><h3>${title}</h3><p>${copy}</p></div>` +
    `${act('buy-sku', `<small>보석</small>◆ ${price}`, 'product-buy', `data-sku="${skuId}"`)}` +
    `</div>`
  );
}

function renderShopView(): string {
  const help = shopTab === 'help';
  const buckets = ensureInventoryBuckets(userProfile);

  const boardCardsHtml = BOARD_SKIN_CATALOG.map(bSkin => {
    const isEquipped = userProfile.equippedBoardSkin === bSkin.id;
    const isOwned =
      bSkin.priceGems === 0 && bSkin.priceCoins === 0 ||
      userProfile.unlockedBoardSkins.includes(bSkin.id);
    const btnLabel = isEquipped
      ? '장착 중'
      : isOwned
      ? '장착하기'
      : `◆ ${bSkin.priceGems} 구매`;
    return (
      `<div class="product-card">` +
      `<img class="skin-preview" src="${bSkin.imageUrl}" alt="${bSkin.name}" style="object-fit:cover">` +
      `<div class="product-info"><h3>${bSkin.name}</h3><p>${bSkin.subtitle}</p></div>` +
      `${act('select-board-skin', btnLabel, 'product-buy', `data-skin-id="${bSkin.id}"`)}` +
      `</div>`
    );
  }).join('');

  const pieceCardsHtml = PIECE_SKIN_CATALOG.map(pSkin => {
    const isEquipped = userProfile.equippedPieceSkin === pSkin.id;
    const isOwned =
      pSkin.priceGems === 0 && pSkin.priceCoins === 0 ||
      userProfile.unlockedPieceSkins.includes(pSkin.id);
    const btnLabel = isEquipped
      ? '장착 중'
      : isOwned
      ? '장착하기'
      : `◆ ${pSkin.priceGems} 구매`;
    return (
      `<div class="product-card">` +
      `<span class="stone-mini classic-stone" style="background:radial-gradient(circle at 30% 25%, ${pSkin.choBgGradient[0]}, ${pSkin.choBgGradient[1]} 65%, ${pSkin.choBgGradient[2]});color:${pSkin.choTextColor};border-color:${pSkin.choBorderColor}">楚</span>` +
      `<div class="product-info"><h3>${pSkin.name}</h3><p>${pSkin.subtitle}</p></div>` +
      `${act('select-piece-skin', btnLabel, 'product-buy', `data-piece-skin-id="${pSkin.id}"`)}` +
      `</div>`
    );
  }).join('');

  return screen(
    'shop',
    header('상점') +
      `<div class="screen-body">` +
      `<div class="wallet">` +
      `<div><small>보유 엽전</small><strong><span class="coin-icon">金</span>${userProfile.coins.toLocaleString()}</strong></div>` +
      `<div class="wallet-line"></div>` +
      `<div><small>보유 보석</small><strong><span class="gem-icon">◆</span>${userProfile.gems.toLocaleString()}</strong></div>` +
      `</div>` +
      `<div class="pill-tabs">` +
      `<button class="${help ? 'active' : ''}" data-action="shop-help">도움 아이템</button>` +
      `<button class="${!help ? 'active' : ''}" data-action="shop-skin">장기판 · 장기알 (12종)</button>` +
      `</div>` +
      (help
        ? `<div class="quota-panel">` +
          `<div class="row between"><span>오늘의 무료 훈수</span><strong>${buckets.dailyAllowance.hunsu}회 남음</strong></div>` +
          `<div class="row between"><span>구매한 훈수</span><strong>${buckets.purchasedInventory.hunsu}회 보유</strong></div>` +
          `</div>` +
          renderProductCard('bulb', '훈수 3회', '좋은 수를 알려드려요', '60', 'gold', 'sku_hunsu_3') +
          renderProductCard('eye', '상수 4회', '몇 수 앞을 살펴봐요', '40', 'blue', 'sku_sangsu_4') +
          renderProductCard('shield', '실수 막기 1회', '안전한 대안을 알려드려요', '45', '', 'sku_shield_1') +
          `${act('all-items', '상대 노림수 · 무르기 · 보너스 보기', 'inline-link')}` +
          `<button class="shop-mini-banner" data-action="vip" style="width:100%;text-align:left">` +
          `${ico('crown')}<span><h3>매일 함께하는 도움 이용권</h3><p>제공 횟수와 혜택을 확인해 보세요.</p></span><span class="chev">${ico('chevron')}</span>` +
          `</button>`
        : `<div class="menu-section-title" style="margin-bottom:8px">명품 원목·청자·흑단 장기판 6종</div>` +
          boardCardsHtml +
          `<div class="space-12"></div>` +
          `<div class="menu-section-title" style="margin-bottom:8px">수제 팔각 장기알 6종</div>` +
          pieceCardsHtml +
          `<div class="space-12"></div>` +
          `${act('all-skins', '장기판 6종 · 장기알 6종 한눈에 비교', 'btn btn-secondary')}`) +
      `</div>`,
    nav('shop')
  );
}

function renderRankContentHtml(): string {
  const tierSpec = getTierByIndex(userProfile.tierIndex);
  const leaders = getLeaderboardByCategory(
    activeRankCategory,
    userProfile.nickname,
    tierSpec.label,
    userProfile.rating,
    userProfile.upsetWins || 3,
    userProfile.puzzlesCleared.length
  );

  const tabs: Array<[LeaderboardCategory, string]> = [
    ['tier', '급수 서열'],
    ['upset', '하극상 명인'],
    ['puzzle', '묘수 풀이'],
    ['guild', '명문 기원']
  ];

  const tabsHtml = tabs
    .map(
      ([cat, label]) =>
        `<button class="${activeRankCategory === cat ? 'active' : ''}" data-action="rank-cat" data-cat="${cat}">${label}</button>`
    )
    .join('');

  const rowsHtml = leaders
    .map(
      l =>
        `<div class="product-card" style="align-items:center">` +
        `<img class="avatar large" src="${l.avatarUrl}" alt="${l.nickname}" ${IMG_FALLBACK}>` +
        `<div class="product-info">` +
        `<div class="row between"><h3>${l.rank}위 · ${l.nickname} (${l.tierLabel})</h3><span class="tag gold">${l.titleBadge} · 시뮬레이션 명인</span></div>` +
        `<p>${l.scoreValue} · ${l.subDetail}</p>` +
        `</div>` +
        `${act(
          'challenge-rank-master',
          '도전하기',
          'product-buy',
          `data-tier-idx="${l.aiMasterTierIndex ?? 22}" data-master-name="${l.nickname}"`
        )}` +
        `</div>`
    )
    .join('');

  return (
    `<div class="quota-panel" style="margin-bottom:12px">` +
    `<div class="row between"><span>내 서열 정보</span><strong>${userProfile.nickname} · ${tierSpec.label} (${userProfile.rating} RP)</strong></div>` +
    `<div class="row between"><span>정답찾기 · 하극상 기록</span><strong>묘수 ${userProfile.puzzlesCleared.length}/5 · 하극상 ${userProfile.upsetWins || 3}승</strong></div>` +
    `</div>` +
    `<div class="pill-tabs" style="grid-template-columns:repeat(4,1fr);margin-bottom:12px">${tabsHtml}</div>` +
    `${rowsHtml}`
  );
}

function renderRankView(): string {
  return screen(
    'rank',
    header('천하 명인 4대 랭킹전') +
      `<div class="screen-body">` +
      renderRankContentHtml() +
      `</div>`,
    nav('rank')
  );
}

function renderSettingRow(icon: string, text: string, action: string, trailing: string): string {
  const isToggle = trailing === 'on' || trailing === 'off';
  return act(
    action,
    `${ico(icon)}<span>${text}</span>` +
      (isToggle
        ? `<span class="toggle ${trailing === 'off' ? 'off' : ''}"></span>`
        : `<span class="trailing">${trailing}${ico('chevron')}</span>`),
    'settings-row'
  );
}

function renderMoreView(): string {
  const tierSpec = getTierByIndex(userProfile.tierIndex);
  const isLargeText = document.getElementById('app')?.classList.contains('large-text');
  const surroundLabel = surroundTheme === 'warm-stone' ? '따뜻한 한지' : '차콜 골드';

  return screen(
    'more',
    header('더보기') +
      `<div class="screen-body">` +
      `<div class="more-profile" data-action="account-transfer" style="cursor:pointer">${avatar()}<div><h2>${userProfile.nickname}</h2><p>${tierSpec.label} · 계정 이어하기 / 앱 설치</p></div><span class="chev">${ico('chevron')}</span></div>` +
      `${list('book', '대국 기록 · 찬스복기', '방금 둔 대국을 복기하고 승부처부터 다시 두어봐요', 'records', 'gold', true, true)}` +
      `<div class="space-12"></div>` +
      `<div class="more-grid">` +
      (
        [
          ['book', '대국 기록', 'records'],
          ['trophy', '순위 · 랭킹', 'rank'],
          ['eye', 'AI 관전', 'spectate'],
          ['target', '실력 측정', 'level']
        ] as Array<[string, string, string]>
      )
        .map(([i, t, a]) => act(a, ico(i) + t, 'more-tile'))
        .join('') +
      `</div>` +
      `<div class="menu-section-title">대국 준비</div>` +
      `<div class="settings-list">` +
      `${renderSettingRow('grid', '장기말 차림', 'formation', formationDisplayLabel(myFormation))}` +
      `${renderSettingRow('palette', '장기판 · 장기알', 'skin-settings', '내 꾸미기')}` +
      `${renderSettingRow('shield', '도움 설정', 'assist-settings', '직접 확인')}` +
      `</div>` +
      `<div class="menu-section-title">편하게 보기 · 앱 설치</div>` +
      `<div class="settings-list">` +
      `${renderSettingRow('audio', '소리', 'sound', soundEnabled ? 'on' : 'off')}` +
      `${renderSettingRow('text', '큰 글씨', 'big-text', isLargeText ? 'on' : 'off')}` +
      `${renderSettingRow('palette', '대국 배경 테마', 'surround-theme', surroundLabel)}` +
      `${renderSettingRow('frame', '화면 · 앱 설치', 'settings', fullscreenEnabled ? '전체화면' : '설치·전체화면')}` +
      `</div>` +
      `<div class="row between" style="margin-top:16px">` +
      `${act('help', '게임 도움말', 'inline-link')}` +
      `${act('account-transfer', '내 계정 · 기기 이전', 'inline-link')}` +
      `</div>` +
      `</div>`,
    nav('more')
  );
}

const VIEWS: Record<string, () => string> = {
  lobby: renderLobbyView,
  friends: renderFriendsView,
  matching: renderMatchingView,
  solo: renderSoloView,
  secretary: renderSecretaryView,
  campaign: renderCampaignView,
  puzzles: renderPuzzlesView,
  game: () => renderGameBase('game') + '</section>',
  hint: renderHintView,
  shield: renderShieldView,
  reconnect: renderReconnectView,
  result: renderResultView,
  shop: renderShopView,
  rank: renderRankView,
  more: renderMoreView
};

// ============================================================================
// 화면 전환 · 모달 · 토스트 · 복기 패널 컨트롤러
// ============================================================================
function stopSpectatorTimerIfActive(): void {
  if (spectatorTimer) {
    clearInterval(spectatorTimer);
    spectatorTimer = null;
  }
  spectatorStepInFlight = false;
}

function syncBodyPlayClasses(screenId: string): void {
  if (typeof document === 'undefined' || !document.body) return;
  const isPlaying = ['game', 'hint', 'shield', 'reconnect'].includes(screenId);
  document.body.classList.toggle('is-playing', isPlaying);
  document.body.dataset.surroundTheme = surroundTheme;
}

export function renderScreen(id: string, replace = false): void {
  const nextId = id in VIEWS ? id : 'lobby';
  if (nextId !== 'game' && nextId !== 'hint') {
    stopSpectatorTimerIfActive();
  }
  currentScreen = nextId;
  syncBodyPlayClasses(currentScreen);

  const appEl = document.getElementById('app');
  if (appEl) {
    appEl.innerHTML = VIEWS[currentScreen]();
  }
  syncBoardCanvasAspect();
  renderAllBoardCanvases();
  updateClockHud();

  // 로비 진입 시 대기 중인 서비스워커 업데이트가 있으면 안전하게 적용
  if (currentScreen === 'lobby' && typeof window !== 'undefined' && (window as any).__jangkiApplyPendingSwUpdate) {
    (window as any).__jangkiApplyPendingSwUpdate();
  }

  const params = new URLSearchParams(window.location.search);
  params.set('screen', currentScreen);
  const url = window.location.pathname + '?' + params.toString();
  if (replace) {
    window.history.replaceState({ screen: currentScreen }, '', url);
  } else {
    window.history.pushState({ screen: currentScreen }, '', url);
  }
  updateReviewPanel();
  const spec = SCREEN_DATA.find(s => s.id === currentScreen);
  if (spec) {
    document.title = `천하장기 · ${spec.title}`;
  }
}

function updateReviewPanel(): void {
  const idxEl = document.getElementById('review-index');
  if (idxEl) {
    idxEl.innerHTML = SCREEN_DATA.map(
      s =>
        `<button class="index-btn ${currentScreen === s.id ? 'active' : ''}" data-go="${s.id}"><span>${s.number}</span>${s.title}</button>`
    ).join('');
  }
  const d = SCREEN_DATA.find(s => s.id === currentScreen) || SCREEN_DATA[0];
  const numEl = document.getElementById('screen-number');
  const titleEl = document.getElementById('screen-title');
  const descEl = document.getElementById('screen-description');
  const checksEl = document.getElementById('screen-checks');
  const ctrlsEl = document.getElementById('review-controls');

  if (numEl) numEl.textContent = `SCREEN ${d.number} / 14`;
  if (titleEl) titleEl.textContent = d.subtitle;
  if (descEl) descEl.textContent = d.description;
  if (checksEl) checksEl.innerHTML = d.checks.map(c => `<li>${c}</li>`).join('');
  if (ctrlsEl) {
    ctrlsEl.innerHTML =
      currentScreen === 'matching'
        ? b('game', '상대 발견 상태로 보기', 'review-control')
        : currentScreen === 'result'
        ? act('result-win', '승리 상태', 'review-control') + act('result-void', '무효 상태', 'review-control')
        : currentScreen === 'reconnect'
        ? act('connection-opponent', '상대 연결', 'review-control') + act('connection-mine', '내 연결', 'review-control')
        : ['game', 'hint', 'shield'].includes(currentScreen)
        ? b('game', '훈수 대기', 'review-control') +
          b('hint', '훈수 기회', 'review-control') +
          b('shield', '위험 확인', 'review-control') +
          b('reconnect', '연결 복구', 'review-control')
        : '';
  }
}

export function showToast(msg: string): void {
  const el = document.getElementById('toast');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('show'), 3000);
}

let floatingEmoteHideTimer: any = null;
function showFloatingEmoteBubble(emote: EmoteItem, fromSide: Side): void {
  const slot = document.getElementById('floating-emote-slot');
  if (!slot) {
    showToast(`${fromSide === mySide ? '나' : opponentNickname}: ${emote.subtitle}`);
    return;
  }
  slot.innerHTML =
    `<div class="card" style="display:flex;align-items:center;gap:10px;padding:8px 12px;background:rgba(34,23,16,0.94);color:#fff7e8;border:2px solid #eab308;border-radius:16px;box-shadow:0 8px 24px rgba(0,0,0,0.45)">` +
    `<img src="${emote.imageUrl}" alt="${emote.title}" style="width:48px;height:48px;object-fit:contain;border-radius:10px" ${IMG_FALLBACK}>` +
    `<div><strong style="font-size:13px;color:#fde047">${fromSide === mySide ? userProfile.nickname : opponentNickname} · ${emote.title}</strong><p style="margin:2px 0 0;font-size:12px;color:#fff">${emote.subtitle}</p></div>` +
    `</div>`;
  if (floatingEmoteHideTimer) clearTimeout(floatingEmoteHideTimer);
  floatingEmoteHideTimer = setTimeout(() => {
    if (slot) slot.innerHTML = '';
  }, 3200);
}

function showModal(title: string, bodyHtml: string): void {
  document.querySelector('.app-modal')?.remove();
  const el = document.createElement('div');
  el.className = 'app-modal';
  el.innerHTML =
    `<div class="modal-panel">` +
    `<div class="sheet-handle"></div>` +
    `<div class="sheet-subhead"><h2>${title}</h2>${act('close-modal', ico('close'), 'round-btn')}</div>` +
    `${bodyHtml}` +
    `</div>`;
  document.getElementById('app')?.appendChild(el);
  renderAllBoardCanvases();
}

function closeModal(): void {
  document.querySelector('.app-modal')?.remove();
}

function rebuildCapturedListsFromHistory(): void {
  capturedByMe = [];
  capturedByOpponent = [];
  for (const ply of recordedHistory) {
    const cap = ply.move?.captured;
    if (cap) {
      if (ply.side === mySide) {
        capturedByMe.push(cap);
      } else {
        capturedByOpponent.push(cap);
      }
    }
  }
}

function recordMatchToStorage(outcome: 'win' | 'loss' | 'void', reason: string): void {
  if (recordedHistory.length === 0 && outcome === 'void') return;
  const modeLabel =
    activeGameMode === 'pvp'
      ? '친구·실시간 대국'
      : activeGameMode === 'campaign'
      ? `미녀대국 ${activeStageIndex + 1}관문`
      : activeGameMode === 'puzzle'
      ? '정답찾기 묘수풀이'
      : activeGameMode === 'eval'
      ? '실력 측정 심사국'
      : 'AI 대국';

  const rec: SavedMatchRecord = {
    id: `rec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    title: `${opponentNickname}과(와) 대국`,
    modeLabel,
    outcome,
    reason,
    dateLabel: '방금 전',
    mySide,
    history: [...recordedHistory]
  };
  savedMatchRecords.unshift(rec);
  if (savedMatchRecords.length > 20) {
    savedMatchRecords.length = 20;
  }
  activeReviewRecordId = rec.id;
  saveMatchRecords();
}

// ============================================================================
// 실시간 보드 착수 · AI 응수 · 훈수/위기감지 · 사운드/성우 음성 처리
// ============================================================================
function playBoardMoveSoundEffects(
  boardBefore: BoardGrid,
  boardAfter: BoardGrid,
  move: Move,
  captured: Piece | null | undefined,
  moverSide: Side,
  skipClack = false
): void {
  if (!soundEnabled) return;
  if (!skipClack) {
    soundEngine.playPieceClack(1, move.piece.type, userProfile.equippedBoardSkin, userProfile.equippedPieceSkin);
  }

  if (captured) {
    soundEngine.playTieredCaptureImpact(captured.type);
  }

  const enemySide: Side = moverSide === 'cho' ? 'han' : 'cho';
  const wasInCheckBefore = isKingInCheck(boardBefore, moverSide);
  const gaveCheck = isKingInCheck(boardAfter, enemySide);

  if (isBikjangState(boardAfter)) {
    soundEngine.playBikjangAlert();
  } else if (gaveCheck) {
    soundEngine.playCheckAlert(move.piece.type, false);
  } else if (wasInCheckBefore && !isKingInCheck(boardAfter, moverSide)) {
    soundEngine.playMeonggunAlert();
  }
}

export function startNewLocalMatch(
  mode: 'ai' | 'eval' | 'campaign' | 'puzzle' | 'spectate',
  preserveStageOrOpponent = false
): void {
  stopSpectatorTimerIfActive();
  bgAnalysisBroker.cancel();
  actionAnalysisBroker.cancel('state_changed', true);
  activeMatchSessionId += 1;
  localTurnGeneration += 1;
  const sessionId = activeMatchSessionId;
  activeGameMode = mode;
  selectedPos = null;
  legalTargetsForSelected = [];
  activeBoardDrag = null;
  capturedByMe = [];
  capturedByOpponent = [];
  recordedHistory = [];
  localRemainingBudgetStack = [];
  activeSangsuVision = null;
  activeEnemyIntentVision = null;
  pendingCrisis = null;
  crisisCheckInFlight = false;
  assistRevealInFlight = false;
  pvpPendingMove = null;
  awaitingMoveAck = false;
  lastMove = null;
  moveAnim = null;
  activeCaptureBursts = [];
  captureParticles = [];
  suppressedMoveKeys.clear();
  shieldUsesInCurrentMatch = 0;
  revealedCoachingForTurn = null;
  revealedTurnHash = '';
  mySide = 'cho';
  currentTurn = 'cho';
  moveNumber = 1;
  pvpTurnClock = null;
  localTurnClock = isTurnClockActiveMode(mode)
    ? createTurnClock(`local_${mode}_${sessionId}`, 1, 'cho', Date.now(), TURN_MS)
    : null;

  if (mode === 'campaign') {
    const ch = getBeautyChapterById(activeChapterId);
    if (!preserveStageOrOpponent) {
      const cleared = getChapterClearedStars(ch, userProfile.beautyStagesCleared);
      activeStageIndex = Math.min(4, Math.max(0, cleared));
    } else {
      activeStageIndex = Math.min(4, Math.max(0, activeStageIndex));
    }
    const stage = ch.stages[activeStageIndex] || ch.stages[0];
    activeBoard = stage.setupBoard();
    campaignMoveBudgetRemaining = stage.maxMoves || 60;
    aiOpponentTierIndex = ch.aiTierIndex ?? 9;
    opponentNickname = `${ch.knightName} (${activeStageIndex + 1}관문)`;
    opponentTierLabel = ch.tierLabel;
  } else if (mode === 'puzzle') {
    const pStage = ENDGAME_PUZZLE_STAGES.find(s => s.id === activePuzzleId) || ENDGAME_PUZZLE_STAGES[0];
    activeBoard = pStage.setupBoard();
    puzzleMoveBudgetRemaining = pStage.maxMovesToWin;
    opponentNickname = pStage.title;
    opponentTierLabel = `문제 ${pStage.stageNumber}`;
  } else if (mode === 'eval') {
    if (!preserveStageOrOpponent) {
      aiOpponentTierIndex = Math.min(26, userProfile.tierIndex + 2);
    }
    activeBoard = createInitialBoard(myFormation, 'sangMaSangMa');
    const aiTier = getTierByIndex(aiOpponentTierIndex);
    opponentNickname = `승급 심사관 (${aiTier.label})`;
    opponentTierLabel = aiTier.label;
  } else if (mode === 'spectate') {
    activeBoard = createInitialBoard(myFormation, 'sangMaSangMa');
    aiOpponentTierIndex = 26;
    opponentNickname = '어전백학국수 (漢 · 9단)';
    opponentTierLabel = '9단';
    spectatorTimer = setInterval(() => {
      if (sessionId !== activeMatchSessionId || activeGameMode !== 'spectate') {
        stopSpectatorTimerIfActive();
        return;
      }
      if (spectatorStepInFlight) return;
      spectatorStepInFlight = true;
      const turnSide = currentTurn;
      const oppSide: Side = turnSide === 'cho' ? 'han' : 'cho';
      const stamp = buildCurrentAnalysisStamp(turnSide, activeBoard);

      const applySpectateMove = (specMove: Move | null) => {
        spectatorStepInFlight = false;
        if (sessionId !== activeMatchSessionId || activeGameMode !== 'spectate' || currentTurn !== turnSide) {
          return;
        }
        if (!specMove) {
          stopSpectatorTimerIfActive();
          showToast('관전 대국이 마무리되었어요.');
          return;
        }
        const bBefore = activeBoard;
        const { nextBoard, captured } = applyMoveOnClone(activeBoard, specMove.from, specMove.to);
        activeBoard = nextBoard;
        lastMove = specMove;
        localTurnGeneration += 1;
        if (captured) {
          if (turnSide === mySide) capturedByMe.push(captured);
          else capturedByOpponent.push(captured);
          triggerTieredCaptureVfx(specMove.to, captured.type);
        }
        triggerMoveAnimation(specMove.from, specMove.to, specMove.piece);
        playBoardMoveSoundEffects(bBefore, nextBoard, specMove, captured, turnSide);
        recordedHistory.push({
          ply: recordedHistory.length + 1,
          side: turnSide,
          move: specMove,
          boardBefore: bBefore,
          boardAfter: nextBoard,
          elapsedSeconds: 1
        });
        moveNumber += 1;
        currentTurn = oppSide;
        updateGameHudInPlace();
        renderAllBoardCanvases();
      };

      if (typeof Worker !== 'undefined') {
        actionAnalysisBroker
          .request<Move | null>('ai', { board: activeBoard, side: turnSide, tierIndex: 26 }, stamp)
          .then(out => {
            if (out.status === 'ok') {
              applySpectateMove(out.value);
            } else {
              spectatorStepInFlight = false;
            }
          })
          .catch(() => {
            spectatorStepInFlight = false;
          });
      } else {
        applySpectateMove(selectAiMoveForTier(activeBoard, turnSide, 26));
      }
    }, 850);
  } else {
    activeBoard = createInitialBoard(myFormation, 'sangMaSangMa');
    const aiTier = getTierByIndex(aiOpponentTierIndex);
    if (!preserveStageOrOpponent || !opponentNickname || opponentNickname.includes('관문') || opponentNickname.includes('문제')) {
      opponentNickname = `AI 대국수`;
    }
    opponentTierLabel = aiTier.label;
  }

  if (soundEnabled) {
    soundEngine.playMatchStart();
  }
  renderScreen('game');
}

function scheduleAiTurnResponse(sessionId: number, enemySide: Side): void {
  setTimeout(() => {
    // ★ R11: 시트나 모달을 열어도 대국 세션(sessionId)이 유효하면 Web Worker에서 블로킹 없이 AI 응수 계산!
    if (sessionId !== activeMatchSessionId || currentTurn !== enemySide || activeGameMode === 'pvp' || activeGameMode === 'spectate') {
      return;
    }
    const stamp = buildCurrentAnalysisStamp(enemySide, activeBoard);

    const commitAiMove = (aiMove: Move | null) => {
      if (
        sessionId !== activeMatchSessionId ||
        currentTurn !== enemySide ||
        activeGameMode === 'pvp' ||
        activeGameMode === 'spectate' ||
        stampKey(stamp) !== stampKey(buildCurrentAnalysisStamp(enemySide, activeBoard))
      ) {
        return;
      }
      if (localTurnClock && localTurnClock.status === 'running' && remainingMs(localTurnClock, Date.now()) <= 0) {
        handleLocalTurnTimeout();
        return;
      }
      if (!aiMove) {
        handleLocalMatchFinished('win', '상대가 더 이상 둘 수가 없어요.');
        return;
      }
      const nowMs = Date.now();
      if (localTurnClock) {
        localRemainingBudgetStack.push({
          budgetMs: remainingMs(localTurnClock, nowMs),
          isTimeoutPass: false
        });
        localTurnClock = commitLegalAction(
          localTurnClock,
          localTurnClock.token,
          enemySide,
          nowMs,
          'move',
          true,
          isKingInCheck(activeBoard, enemySide)
        ).clock;
      }
      localTurnGeneration += 1;

      const bBeforeAi = activeBoard;
      const { nextBoard: afterAi, captured: aiCap } = applyMoveOnClone(activeBoard, aiMove.from, aiMove.to);
      activeBoard = afterAi;
      lastMove = aiMove;
      if (aiCap) {
        capturedByOpponent.push(aiCap);
        triggerTieredCaptureVfx(aiMove.to, aiCap.type);
      }
      triggerMoveAnimation(aiMove.from, aiMove.to, aiMove.piece);
      moveNumber += 1;
      currentTurn = mySide;

      playBoardMoveSoundEffects(bBeforeAi, afterAi, aiMove, aiCap, enemySide);

      recordedHistory.push({
        ply: recordedHistory.length + 1,
        side: enemySide,
        move: aiMove,
        boardBefore: bBeforeAi,
        boardAfter: afterAi,
        elapsedSeconds: 2
      });

      const myLegal = getAllLegalMoves(activeBoard, mySide);
      if (myLegal.length === 0) {
        handleLocalMatchFinished('loss', '외통수로 패배했어요.');
        return;
      }
      updateGameHudInPlace();
      renderAllBoardCanvases();
    };

    if (typeof Worker !== 'undefined') {
      actionAnalysisBroker
        .request<Move | null>(
          'ai',
          { board: activeBoard, side: enemySide, tierIndex: aiOpponentTierIndex },
          stamp
        )
        .then(out => {
          if (out.status === 'ok') {
            commitAiMove(out.value);
          }
        })
        .catch(() => {
          // Superseded or cancelled AI search
        });
    } else {
      commitAiMove(selectAiMoveForTier(activeBoard, enemySide, aiOpponentTierIndex));
    }
  }, 95);
}

function attemptMoveWithAsyncCrisisCheck(candidateMove: Move): void {
  if (crisisCheckInFlight) return;
  crisisCheckInFlight = true;
  activeBoardDrag = null;

  const stamp = buildCurrentAnalysisStamp(mySide, activeBoard);
  const handleCrisisResult = (crisis: CrisisInterventionResult | null) => {
    crisisCheckInFlight = false;
    if (
      currentTurn !== mySide ||
      awaitingMoveAck ||
      stampKey(stamp) !== stampKey(buildCurrentAnalysisStamp(mySide, activeBoard))
    ) {
      return;
    }
    const clk = getActiveTurnClock();
    if (clk && clk.status === 'running' && remainingMs(clk, getActiveClockNowMs()) <= 0) {
      return;
    }
    if (crisis && crisis.triggered) {
      selectedPos = null;
      legalTargetsForSelected = [];
      pendingCrisis = { candidateMove, crisis };
      if (soundEnabled) soundEngine.playCrisisShieldTrigger();
      // 경고 시트 자체는 100% 무료(0회 차감)로 표시 (A05)
      renderScreen('shield', true);
      return;
    }
    executeMoveOnBoard(candidateMove);
  };

  if (typeof Worker !== 'undefined') {
    actionAnalysisBroker
      .request<CrisisInterventionResult | null>(
        'crisis',
        {
          board: activeBoard,
          move: candidateMove,
          side: mySide,
          guardConfig,
          suppressedMoveKeys: Array.from(suppressedMoveKeys)
        },
        stamp
      )
      .then(out => {
        if (out.status === 'ok') {
          handleCrisisResult(out.value);
        } else {
          crisisCheckInFlight = false;
        }
      })
      .catch(() => {
        crisisCheckInFlight = false;
      });
  } else {
    handleCrisisResult(
      detectMoveTriggersMultiTurnCrisis(activeBoard, candidateMove, mySide, guardConfig, suppressedMoveKeys)
    );
  }
}

function handleBoardIntersectionClick(row: number, col: number): void {
  if (activeGameMode === 'spectate') {
    showToast('AI 9단 실시간 관전 중이에요.');
    return;
  }
  if (currentTurn !== mySide || awaitingMoveAck || crisisCheckInFlight) return;

  // 1) 이미 선택된 기물이 있고 합법 이동 칸을 누른 경우 -> 착수 시도
  if (selectedPos && legalTargetsForSelected.some(t => t.row === row && t.col === col)) {
    const piece = activeBoard[selectedPos.row]?.[selectedPos.col];
    if (!piece) return;

    const captured = activeBoard[row]?.[col] || null;
    const candidateMove: Move = {
      from: { ...selectedPos },
      to: { row, col },
      piece,
      captured,
      notation: formatKoreanNotation(piece, selectedPos, { row, col }, captured)
    };

    // 위기감지 방지권(호신강기): <0.5ms 초고속 사전 판별을 통과한 위협 국면에서만 예열된 Worker 검사 실행, 일반 착수는 0ms 즉시 착수!
    const remainingShields = getBucketTotal(userProfile, 'shield');
    if (
      remainingShields > 0 &&
      shieldUsesInCurrentMatch < 3 &&
      activeGameMode !== 'puzzle' &&
      canMovePossiblyTriggerCrisisFast(activeBoard, candidateMove, mySide, guardConfig, suppressedMoveKeys)
    ) {
      attemptMoveWithAsyncCrisisCheck(candidateMove);
      return;
    }

    executeMoveOnBoard(candidateMove);
    return;
  }

  // 2) 내 기물을 클릭한 경우 -> 선택 및 합법 이동 칸 즉시 하이라이트 (R01 & R21: 전체 DOM 교체 없이 캔버스만 즉시 재렌더!)
  const clickedPiece = activeBoard[row]?.[col];
  if (clickedPiece && clickedPiece.side === mySide) {
    selectedPos = { row, col };
    legalTargetsForSelected = getLegalMovesForPiece(activeBoard, selectedPos);
    if (soundEnabled) soundEngine.playPieceSelect();
    renderAllBoardCanvases();
  } else {
    selectedPos = null;
    legalTargetsForSelected = [];
    activeBoardDrag = null;
    renderAllBoardCanvases();
  }
}

function executeMoveOnBoard(move: Move): void {
  selectedPos = null;
  legalTargetsForSelected = [];
  activeBoardDrag = null;
  activeSangsuVision = null;
  activeEnemyIntentVision = null;
  pendingCrisis = null;

  // ★ PvP 권위 서버 모드: committedBoard(activeBoard)와 lastCommittedMove(lastMove)를 서버 승인 전까지 절대 덮어쓰지 않되,
  //   착수 즉시(0ms) 딱! 타격음과 120ms 슬라이딩을 단일 pvpPendingMove로 재생해 로컬과 동일한 즉각 손맛 제공!
  if (activeGameMode === 'pvp' && socket && pvpRoomId) {
    if (pvpTurnClock && pvpTurnClock.status === 'running' && remainingMs(pvpTurnClock, getActiveClockNowMs()) <= 0) {
      showToast('제한시간(30초)이 지나 차례 전환을 확인하고 있어요.');
      return;
    }
    const cmdId = `cmd_${pvpEpoch}_${pvpVersion}_${Date.now()}`;
    awaitingMoveAck = true;
    const playImmediateClack = Boolean(soundEnabled);
    if (playImmediateClack) {
      soundEngine.playPieceClack(1, move.piece.type, userProfile.equippedBoardSkin, userProfile.equippedPieceSkin);
    }
    pvpPendingMove = {
      commandId: cmdId,
      expectedVersion: pvpVersion,
      move,
      sentAtMs: Date.now(),
      clackPlayed: playImmediateClack
    };
    triggerMoveAnimation(move.from, move.to, move.piece);
    if (currentScreen !== 'game') {
      renderScreen('game', true);
    } else {
      updateClockHud();
      renderAllBoardCanvases();
    }
    socket.emit(
      'pvp_commit_move',
      {
        matchId: pvpMatchId || pvpRoomId,
        epoch: pvpEpoch,
        expectedVersion: pvpVersion,
        commandId: cmdId,
        kind: 'move',
        from: move.from,
        to: move.to,
        isPass: false,
        notation: move.notation
      },
      (ack?: any) => {
        if (ack?.status === 'committed') {
          applyAuthoritativePvpCommit(ack, 'self');
        } else if (ack?.status === 'rejected') {
          handlePvpMoveRejected(ack);
        }
      }
    );
    return;
  }

  lastMove = move;
  localTurnGeneration += 1;

  // 정답찾기(퍼즐) 모드인 경우 다중 해법 검증 + 수순 제한 체크 (U08 & R09)
  if (activeGameMode === 'puzzle') {
    const boardBefore = activeBoard;
    const check = validateEndgamePuzzleSolution(activePuzzleId, activeBoard, move, mySide);
    const { nextBoard, captured } = applyMoveOnClone(activeBoard, move.from, move.to);
    activeBoard = nextBoard;
    moveNumber += 1;
    puzzleMoveBudgetRemaining = Math.max(0, puzzleMoveBudgetRemaining - 1);
    if (captured) {
      capturedByMe.push(captured);
      triggerTieredCaptureVfx(move.to, captured.type);
    }
    triggerMoveAnimation(move.from, move.to, move.piece);
    playBoardMoveSoundEffects(boardBefore, nextBoard, move, captured, mySide);

    recordedHistory.push({
      ply: recordedHistory.length + 1,
      side: mySide,
      move,
      boardBefore,
      boardAfter: nextBoard,
      elapsedSeconds: 2
    });

    if (check.isCorrect) {
      const pStage = ENDGAME_PUZZLE_STAGES.find(s => s.id === activePuzzleId) || ENDGAME_PUZZLE_STAGES[0];
      if (!userProfile.puzzlesCleared.includes(activePuzzleId)) {
        userProfile.puzzlesCleared.push(activePuzzleId);
      }
      userProfile.coins += pStage.rewardCoins;
      userProfile.gems += pStage.rewardGems;
      saveProfile();
      recordMatchToStorage('win', check.reason);
      resultState = 'win';
      resultReasonText = check.reason;
      resultDeltaText = `+ ${pStage.rewardCoins.toLocaleString()} 金`;
      if (soundEnabled) soundEngine.playVictoryFanfare(true);
      renderScreen('result');
      return;
    } else if (puzzleMoveBudgetRemaining <= 0) {
      showToast('수순 제한을 모두 소진했어요! 다시 풀거나 +3수 연장을 사용해 보세요.');
      updateGameHudInPlace();
      renderAllBoardCanvases();
      return;
    } else {
      showToast(check.reason);
      if (currentScreen !== 'game') {
        renderScreen('game', true);
      } else {
        updateGameHudInPlace();
        renderAllBoardCanvases();
      }
      return;
    }
  }

  // 일반 AI / 실력측정(eval) / 미녀대국 로컬 진행 (30초 시계 갱신 & 내 idleStreak 0 초기화)
  const enemySide: Side = mySide === 'cho' ? 'han' : 'cho';
  const nowMs = Date.now();
  if (localTurnClock) {
    localRemainingBudgetStack.push({
      budgetMs: remainingMs(localTurnClock, nowMs),
      isTimeoutPass: false
    });
    localTurnClock = commitLegalAction(
      localTurnClock,
      localTurnClock.token,
      mySide,
      nowMs,
      'move',
      true,
      isKingInCheck(activeBoard, mySide)
    ).clock;
  }

  const boardBefore = activeBoard;
  const { nextBoard, captured } = applyMoveOnClone(activeBoard, move.from, move.to);
  activeBoard = nextBoard;
  if (captured) {
    capturedByMe.push(captured);
    triggerTieredCaptureVfx(move.to, captured.type);
  }
  triggerMoveAnimation(move.from, move.to, move.piece);
  moveNumber += 1;

  recordedHistory.push({
    ply: recordedHistory.length + 1,
    side: mySide,
    move,
    boardBefore,
    boardAfter: nextBoard,
    elapsedSeconds: 3
  });

  const oppLegal = getAllLegalMoves(activeBoard, enemySide);
  playBoardMoveSoundEffects(boardBefore, nextBoard, move, captured, mySide);

  if (oppLegal.length === 0) {
    handleLocalMatchFinished('win', '외통수로 승리했어요!');
    return;
  }

  currentTurn = enemySide;
  if (currentScreen !== 'game') {
    renderScreen('game', true);
  } else {
    updateGameHudInPlace();
    renderAllBoardCanvases();
  }

  scheduleAiTurnResponse(activeMatchSessionId, enemySide);
}

function handleLocalMatchFinished(outcome: 'win' | 'loss', reasonText: string): void {
  stopSpectatorTimerIfActive();
  bgAnalysisBroker.cancel();
  actionAnalysisBroker.cancel();
  if (localTurnClock) {
    localTurnClock = finishClock(localTurnClock);
  }
  activeMatchSessionId += 1;
  localTurnGeneration += 1;
  resultState = outcome;
  resultReasonText = reasonText;

  if (soundEnabled) {
    if (outcome === 'win') {
      soundEngine.playVictoryFanfare(true);
    } else {
      soundEngine.playDefeatDrum();
    }
  }

  if (outcome === 'win' && activeGameMode === 'campaign') {
    const ch = getBeautyChapterById(activeChapterId);
    const stage = ch.stages[activeStageIndex] || ch.stages[0];
    if (!userProfile.beautyStagesCleared.includes(stage.stageId)) {
      userProfile.beautyStagesCleared.push(stage.stageId);
    }
    // ★ U05: 1관문 승리만으로 비서 동행 즉시 해금!
    if (canHireChapterSecretary(ch, userProfile.beautyStagesCleared)) {
      if (!userProfile.hiredSecretaryIds.includes(ch.id)) {
        userProfile.hiredSecretaryIds.push(ch.id);
      }
    }
  }

  const prevTierIdx = userProfile.tierIndex;
  const applied = applyMatchResultToProfile(userProfile, outcome, aiOpponentTierIndex);
  userProfile = applied.updated;
  if (outcome === 'win' && activeGameMode === 'eval') {
    userProfile.tierIndex = Math.min(26, Math.max(userProfile.tierIndex, prevTierIdx + 1));
    userProfile.rating += 18;
    resultReasonText = `${reasonText} (승급 심사 통과 · ${getTierByIndex(userProfile.tierIndex).label} 달성!)`;
    resultDeltaText = '+ 36';
  } else {
    resultDeltaText = outcome === 'win' ? '+ 24' : '- 16';
  }
  saveProfile();
  recordMatchToStorage(outcome, resultReasonText);
  renderScreen('result');
}

// ============================================================================
// 전체화면 설정 (정직한 Fullscreen API 상태 동기화 + Test 15 호환)
// ============================================================================
function syncFullscreenStateFromDom(): void {
  if (typeof document === 'undefined') return;
  const isStandalone =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches;
  fullscreenEnabled = Boolean(document.fullscreenElement) || isStandalone;
  const shell = document.getElementById('app-shell');
  if (shell) {
    shell.classList.toggle('fullscreen-mode', fullscreenEnabled);
  }
  document.documentElement.style.setProperty(
    '--camera-top-safe-px',
    fullscreenEnabled ? 'env(safe-area-inset-top, 0px)' : '0px'
  );
  syncBoardCanvasAspect();
}

async function toggleFullscreenMode(): Promise<void> {
  if (typeof document === 'undefined') return;
  const currentlyFull = Boolean(document.fullscreenElement);

  try {
    if (!currentlyFull) {
      if (!document.documentElement.requestFullscreen) {
        syncFullscreenStateFromDom();
        showToast('이 브라우저에서는 전체화면이 제한돼요. 홈 화면에 앱으로 설치해 보세요.');
        return;
      }
      await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
    } else if (document.exitFullscreen) {
      await document.exitFullscreen();
    }
  } catch {
    syncFullscreenStateFromDom();
    showToast('전체화면 전환이 브라우저 정책으로 제한되었어요. 앱 설치 모드를 이용해 보세요.');
    return;
  }

  syncFullscreenStateFromDom();
  showToast(fullscreenEnabled ? '전체화면 모드를 켰어요.' : '전체화면 모드를 껐어요.');
}

// ============================================================================
// Socket.IO PvP 연결 초기화 & 권위 스냅샷 / 30초 시계 / 착수 중복방지 동기화
// ============================================================================
function syncPvpSnapshotClockAndLastMove(snapshot: any, preserveLocalLastMoveWhenOmitted = false): void {
  if (!snapshot) return;
  if (typeof snapshot.serverNowMs === 'number') {
    serverClockOffsetMs = snapshot.serverNowMs - Date.now();
  }
  if (snapshot.turnClock) {
    pvpTurnClock = snapshot.turnClock as TurnClock;
  }
  if ('lastCommittedMove' in snapshot) {
    const lm = snapshot.lastCommittedMove;
    if (lm && lm.from && lm.to && lm.piece) {
      lastMove = {
        from: lm.from,
        to: lm.to,
        piece: lm.piece,
        captured: lm.captured || null,
        notation: lm.notation || ''
      };
    } else if (!lm) {
      lastMove = null;
    }
  } else if (!preserveLocalLastMoveWhenOmitted) {
    lastMove = null;
  }
}

function applyAuthoritativePvpCommit(data: any, source: 'self' | 'opponent'): void {
  const ver = data?.version ?? data?.snapshot?.version;
  const ep = data?.epoch ?? data?.snapshot?.epoch ?? pvpEpoch;
  const mId = data?.matchId || data?.snapshot?.matchId || pvpMatchId || pvpRoomId || 'pvp';
  const commitKey = typeof ver === 'number' ? `${mId}:${ep}:${ver}` : '';
  const cmdKey = data?.commandId ? `cmd:${mId}:${data.commandId}` : '';

  if ((commitKey && appliedPvpCommitKeys.has(commitKey)) || (cmdKey && appliedPvpCommitKeys.has(cmdKey))) {
    awaitingMoveAck = false;
    pvpPendingMove = null;
    if (data?.snapshot) {
      syncPvpSnapshotClockAndLastMove(data.snapshot, true);
    }
    updateClockHud();
    renderAllBoardCanvases();
    return;
  }
  if (commitKey) appliedPvpCommitKeys.add(commitKey);
  if (cmdKey) appliedPvpCommitKeys.add(cmdKey);

  const prevBoard = activeBoard;
  const hadMatchingPending = Boolean(
    pvpPendingMove &&
      data?.from &&
      data?.to &&
      pvpPendingMove.move.from.row === data.from.row &&
      pvpPendingMove.move.from.col === data.from.col &&
      pvpPendingMove.move.to.row === data.to.row &&
      pvpPendingMove.move.to.col === data.to.col
  );
  const pendingClackAlreadyPlayed = Boolean(hadMatchingPending && pvpPendingMove?.clackPlayed);

  awaitingMoveAck = false;
  pvpPendingMove = null;
  selectedPos = null;
  legalTargetsForSelected = [];
  activeBoardDrag = null;
  localTurnGeneration += 1;

  if (data?.snapshot) {
    activeBoard = data.snapshot.board;
    pvpVersion = data.snapshot.version;
    pvpEpoch = data.snapshot.epoch || pvpEpoch;
    currentTurn = data.snapshot.sideToMove;
    moveNumber = (data.snapshot.moveCount || 0) + 1;
    syncPvpSnapshotClockAndLastMove(data.snapshot, true);
  }

  const moverSide: Side = data?.side || (source === 'self' ? mySide : mySide === 'cho' ? 'han' : 'cho');

  if (data?.captured) {
    if (moverSide === mySide) {
      capturedByMe.push(data.captured);
    } else {
      capturedByOpponent.push(data.captured);
    }
    if (data.to) {
      triggerTieredCaptureVfx(data.to, data.captured.type);
    }
  }

  if (data?.from && data?.to && !data?.isPass) {
    const movedPiece = activeBoard[data.to.row]?.[data.to.col] || prevBoard[data.from.row]?.[data.from.col];
    if (movedPiece) {
      const committedMove: Move = {
        from: data.from,
        to: data.to,
        piece: movedPiece,
        captured: data.captured || null,
        notation: data.notation || ''
      };
      lastMove = committedMove;
      if (!hadMatchingPending) {
        triggerMoveAnimation(data.from, data.to, movedPiece);
      }
      playBoardMoveSoundEffects(
        prevBoard,
        activeBoard,
        committedMove,
        data.captured,
        moverSide,
        pendingClackAlreadyPlayed
      );
      recordedHistory.push({
        ply: recordedHistory.length + 1,
        side: moverSide,
        move: committedMove,
        boardBefore: prevBoard,
        boardAfter: activeBoard,
        elapsedSeconds: 2
      });
    }
  } else if (data?.isPass) {
    // 수동 한 수 쉬기 시 직전 실제 착수 표시(lastMove)는 유지!
    if (soundEnabled) {
      soundEngine.playPassTurn();
    }
  }

  updateGameHudInPlace();
  renderAllBoardCanvases();
}

function handlePvpMoveRejected(err: any): void {
  awaitingMoveAck = false;
  pvpPendingMove = null;
  moveAnim = null;
  selectedPos = null;
  legalTargetsForSelected = [];
  activeBoardDrag = null;
  localTurnGeneration += 1;

  if (err?.snapshot) {
    activeBoard = err.snapshot.board;
    pvpVersion = err.snapshot.version;
    pvpEpoch = err.snapshot.epoch || pvpEpoch;
    currentTurn = err.snapshot.sideToMove;
    moveNumber = (err.snapshot.moveCount || 0) + 1;
    syncPvpSnapshotClockAndLastMove(err.snapshot, true);
  }

  const msg =
    err?.reason === 'cannot_pass_in_check'
      ? '장군 상태에서는 한 수 쉬기를 할 수 없어요!'
      : err?.reason === 'turn_expired'
      ? '제한시간(30초)이 지나 차례가 넘어갔어요.'
      : '착수가 서버 기준과 달라 판을 동기화했어요.';
  showToast(msg);
  updateGameHudInPlace();
  renderAllBoardCanvases();
}

async function syncAuthenticatedProfileToServer(): Promise<void> {
  if (!sessionToken || !userProfile?.userId || typeof fetch === 'undefined') return;
  try {
    await fetch('/api/session/sync-profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: userProfile.userId,
        sessionToken,
        profile: userProfile
      })
    });
  } catch {
    // ignore background sync failures
  }
}

function ensureSocketConnected(): Socket {
  if (socket) return socket;
  socket = io({ transports: ['websocket', 'polling'] });

  socket.on('connect', () => {
    socket?.emit('pvp_auth_session', {
      userId: userProfile.userId,
      sessionToken,
      nickname: userProfile.nickname
    });
  });

  socket.on('pvp_session_issued', (data: any) => {
    if (data?.ok && data.sessionToken) {
      sessionToken = data.sessionToken;
      userProfile.userId = data.userId;
      saveProfile();
      try {
        localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
      } catch {
        // ignore
      }
      syncAuthenticatedProfileToServer();
    }
  });

  socket.on('pvp_room_created', (data: any) => {
    pvpRoomId = data.roomId;
    pvpMatchId = data.matchId || data.roomId;
    pvpInviteCode = data.roomCode || pvpInviteCode;
    showToast(`초대방(${pvpInviteCode})이 열렸어요.`);
  });

  socket.on('pvp_join_error', (err: any) => {
    showToast(err?.message || '방에 입장할 수 없어요.');
  });

  socket.on('pvp_match_started', (data: any) => {
    stopSpectatorTimerIfActive();
    bgAnalysisBroker.cancel();
    actionAnalysisBroker.cancel('state_changed', true);
    activeMatchSessionId += 1;
    localTurnGeneration += 1;
    activeGameMode = 'pvp';
    pvpRoomId = data.roomId;
    pvpMatchId = data.matchId || data.roomId;
    pvpEpoch = data.epoch || 1;
    pvpVersion = data.version || 0;
    mySide = data.mySide || 'cho';
    currentTurn = data.snapshot?.sideToMove || 'cho';
    activeBoard = data.snapshot?.board || createInitialBoard(data.choFormation, data.hanFormation);
    opponentNickname = data.opponent?.nickname || '대국 상대';
    opponentTierLabel = getTierByIndex(data.opponent?.tierIndex ?? 9).label;
    moveNumber = 1;
    selectedPos = null;
    legalTargetsForSelected = [];
    activeBoardDrag = null;
    capturedByMe = [];
    capturedByOpponent = [];
    recordedHistory = [];
    lastMove = null;
    awaitingMoveAck = false;
    pvpPendingMove = null;
    localTurnClock = null;
    appliedPvpCommitKeys.clear();
    syncPvpSnapshotClockAndLastMove(data.snapshot, false);
    closeModal();
    if (soundEnabled) soundEngine.playMatchStart();
    renderScreen('game');
  });

  socket.on('pvp_move_committed', (data: any) => {
    applyAuthoritativePvpCommit(data, 'self');
  });

  socket.on('pvp_opponent_moved', (data: any) => {
    applyAuthoritativePvpCommit(data, 'opponent');
  });

  socket.on('pvp_turn_timeout_pass', (data: any) => {
    const ver = data?.version ?? data?.snapshot?.version;
    const ep = data?.epoch ?? data?.snapshot?.epoch ?? pvpEpoch;
    const mId = data?.matchId || data?.snapshot?.matchId || pvpMatchId || pvpRoomId || 'pvp';
    const commitKey = typeof ver === 'number' ? `${mId}:${ep}:${ver}` : '';
    if (commitKey) {
      if (appliedPvpCommitKeys.has(commitKey)) return;
      appliedPvpCommitKeys.add(commitKey);
    }

    awaitingMoveAck = false;
    pvpPendingMove = null;
    selectedPos = null;
    legalTargetsForSelected = [];
    activeBoardDrag = null;
    pendingCrisis = null;
    crisisCheckInFlight = false;
    assistRevealInFlight = false;
    actionAnalysisBroker.cancel('state_changed', true);
    bgAnalysisBroker.cancel();
    localTurnGeneration += 1;

    if (data?.snapshot) {
      activeBoard = data.snapshot.board;
      pvpVersion = data.snapshot.version;
      pvpEpoch = data.snapshot.epoch || pvpEpoch;
      currentTurn = data.snapshot.sideToMove;
      moveNumber = (data.snapshot.moveCount || 0) + 1;
      syncPvpSnapshotClockAndLastMove(data.snapshot, true);
    } else if (data?.nextTurn) {
      currentTurn = data.nextTurn;
      moveNumber += 1;
    }

    if (soundEnabled) {
      soundEngine.playSfx('sfx_byoyomi_tick', 0.9);
    }

    const streak = data?.idleStreakCount ?? data?.streak ?? 1;
    const timedOut = data?.side ?? data?.timedOutSide;
    if (timedOut === mySide) {
      showToast(`시간초과로 차례가 넘어갔어요 (연속 시간초과 ${streak}/${IDLE_LIMIT})`);
    } else {
      showToast(`상대 시간초과로 내 차례가 되었어요 (상대 시간초과 ${streak}/${IDLE_LIMIT})`);
    }

    if (currentScreen === 'hint' || currentScreen === 'shield') {
      renderScreen('game', true);
    } else {
      updateGameHudInPlace();
      renderAllBoardCanvases();
    }
  });

  socket.on('pvp_move_rejected', (err: any) => {
    handlePvpMoveRejected(err);
  });

  socket.on('pvp_opponent_emoticon', (data: any) => {
    const matched =
      EMOTE_CATALOG.find(e => e.id === data?.emoteId || e.imageUrl === data?.imageUrl) ||
      {
        id: data?.emoteId || 'laugh',
        title: data?.icon || '감정표현',
        subtitle: data?.label || '인사를 보냈어요!',
        imageUrl: data?.imageUrl || '/assets/images/astra/emote_laugh_hd.png',
        soundType: (data?.soundType || 'praise') as any
      };
    if (soundEnabled) {
      soundEngine.playEmoteSound(matched.soundType);
    }
    showFloatingEmoteBubble(matched, data?.fromSide || (mySide === 'cho' ? 'han' : 'cho'));
  });

  socket.on('pvp_undo_requested', () => {
    showModal(
      '상대방의 한 수 무르기 요청',
      `<p>${opponentNickname}님이 직전 수 무르기를 요청했어요. 수락할까요?</p>` +
        `<div class="sheet-actions">` +
        `${act('pvp-undo-decline', '거절하기', 'btn btn-secondary')}` +
        `${act('pvp-undo-accept', '무르기 수락', 'btn btn-primary')}` +
        `</div>`
    );
  });

  socket.on('pvp_undo_resolved', (data: any) => {
    closeModal();
    if (data?.accepted && data?.snapshot) {
      awaitingMoveAck = false;
      pvpPendingMove = null;
      selectedPos = null;
      legalTargetsForSelected = [];
      localTurnGeneration += 1;
      activeBoard = data.snapshot.board;
      pvpVersion = data.snapshot.version;
      pvpEpoch = data.snapshot.epoch || pvpEpoch;
      currentTurn = data.snapshot.sideToMove;
      moveNumber = (data.snapshot.moveCount || 0) + 1;
      if (recordedHistory.length > 0) {
        recordedHistory.pop();
      }
      rebuildCapturedListsFromHistory();
      syncPvpSnapshotClockAndLastMove(
        data.snapshot,
        false
      );
      if (!lastMove && recordedHistory.length > 0) {
        const lastReal = [...recordedHistory].reverse().find(p => p.move && p.move.notation !== '한수쉼');
        lastMove = lastReal ? lastReal.move : null;
      }
      showToast('합의로 한 수를 물렸어요.');
      updateGameHudInPlace();
      renderAllBoardCanvases();
    } else if (data?.reason === 'cannot_undo_timeout_pass') {
      showToast('시간초과로 넘어간 차례는 무를 수 없어요.');
    } else if (data?.reason === 'no_moves_to_undo') {
      showToast('아직 무를 수 있는 수순이 없어요.');
    } else {
      showToast('상대방이 무르기 요청을 사양했어요.');
    }
  });

  socket.on('pvp_rematch_requested', () => {
    showModal(
      '상대방의 재대국 요청',
      `<p>${opponentNickname}님이 한 판 더 두기를 요청했어요!</p>` +
        `<div class="sheet-actions">` +
        `${act('pvp-rematch-decline', '사양하기', 'btn btn-secondary')}` +
        `${act('pvp-rematch-accept', '재대국 수락', 'btn btn-primary')}` +
        `</div>`
    );
  });

  socket.on('pvp_rematch_unavailable', (data: any) => {
    closeModal();
    showToast(data?.message || '상대방과 재대국할 수 없어 새 상대를 찾아요.');
    socket?.emit('join_pvp_queue', {
      userId: userProfile.userId,
      sessionToken,
      nickname: userProfile.nickname,
      tierIndex: userProfile.tierIndex,
      rating: userProfile.rating,
      formation: myFormation
    });
    renderScreen('matching');
  });

  socket.on('pvp_opponent_away', (data: any) => {
    // ★ S04 & S05: informationalOnly(화면 전환/시트 열람)일 때는 재연결 모달을 띄우지 않음!
    if (data?.informationalOnly || data?.timerStarted === false) {
      return;
    }
    if (pvpTurnClock && pvpTurnClock.status === 'running') {
      pvpTurnClock = {
        ...pvpTurnClock,
        status: 'paused',
        pausedRemainingMs: remainingMs(pvpTurnClock, getActiveClockNowMs()),
        deadlineAtMs: null
      };
    }
    connectState = 'opponent';
    graceCountdownSec = data?.graceSeconds || 60;
    renderScreen('reconnect', true);
  });

  socket.on('pvp_opponent_returned', (data: any) => {
    if (data?.snapshot) {
      syncPvpSnapshotClockAndLastMove(data.snapshot, true);
    }
    if (currentScreen === 'reconnect') {
      renderScreen('game', true);
    } else {
      updateClockHud();
    }
  });

  socket.on('pvp_match_restored', (data: any) => {
    // ★ S13: 항상 서버의 최신 권위 스냅샷으로 보드·턴·버전·남은 30초 시계·lastMove 완벽 복원!
    awaitingMoveAck = false;
    pvpPendingMove = null;
    selectedPos = null;
    legalTargetsForSelected = [];
    localTurnGeneration += 1;
    if (data?.snapshot) {
      activeBoard = data.snapshot.board;
      pvpVersion = data.snapshot.version;
      pvpEpoch = data.snapshot.epoch || pvpEpoch;
      currentTurn = data.snapshot.sideToMove;
      moveNumber = (data.snapshot.moveCount || 0) + 1;
      syncPvpSnapshotClockAndLastMove(data.snapshot, true);
    }
    renderScreen('game', true);
  });

  socket.on('pvp_match_ended', (data: any) => {
    if (data?.resultId) {
      if (appliedPvpResultIds.has(data.resultId)) return;
      appliedPvpResultIds.add(data.resultId);
    }
    awaitingMoveAck = false;
    pvpPendingMove = null;
    pendingCrisis = null;
    crisisCheckInFlight = false;
    assistRevealInFlight = false;
    actionAnalysisBroker.cancel();
    bgAnalysisBroker.cancel();
    if (pvpTurnClock) {
      pvpTurnClock = finishClock(pvpTurnClock);
    }
    if (data?.snapshot) {
      activeBoard = data.snapshot.board;
      syncPvpSnapshotClockAndLastMove(data.snapshot, true);
    }

    const reasonCode = data?.reasonCode || '';
    const isWinner = data?.winnerSide === mySide || data?.winner === mySide;

    if (
      data?.isVoid ||
      reasonCode === 'disconnect_void_under_10_moves' ||
      reasonCode === 'dual_disconnect_void'
    ) {
      resultState = 'void';
      resultReasonText = '10수 전에 연결이 끊겨 무효 처리되었어요.';
      resultDeltaText = '변동 없음';
    } else if (isWinner) {
      resultState = 'win';
      resultReasonText =
        reasonCode === 'idle_forfeit'
          ? '상대의 3회 연속 시간초과로 실격승했어요!'
          : reasonCode === 'time_forfeit_in_check'
          ? '상대가 장군 상태에서 시간초과되어 승리했어요!'
          : reasonCode === 'resign'
          ? '상대가 기권했어요.'
          : reasonCode === 'disconnect_forfeit'
          ? '상대방의 연결 이탈로 승리했어요.'
          : reasonCode === 'score_decision'
          ? '기물 점수 판정으로 승리했어요!'
          : '대국에서 승리했어요!';
      resultDeltaText = '+ 18';
      if (soundEnabled) soundEngine.playVictoryFanfare(true);
    } else {
      resultState = 'loss';
      resultReasonText =
        reasonCode === 'idle_forfeit'
          ? '3회 연속 시간초과로 실격패했어요.'
          : reasonCode === 'time_forfeit_in_check'
          ? '장군 상태에서 제한시간(30초)을 넘겨 패배했어요.'
          : reasonCode === 'resign'
          ? '기권으로 대국을 마쳤어요.'
          : reasonCode === 'disconnect_forfeit'
          ? '연결 시간초과로 패배했어요.'
          : reasonCode === 'bikjang_draw'
          ? '빅장 무승부로 대국이 종료되었어요.'
          : '대국이 종료되었어요.';
      resultDeltaText = reasonCode === 'bikjang_draw' ? '변동 없음' : '- 16';
      if (soundEnabled) soundEngine.playDefeatDrum();
    }
    recordMatchToStorage(resultState, resultReasonText);
    syncAuthenticatedProfileToServer();
    renderScreen('result');
  });

  return socket;
}

// ============================================================================
// 5관문 화보 앨범 렌더러 & 20인 비서 선택 모달 렌더러
// ============================================================================
function openGlamourAlbumModal(chapterId?: string, poseId?: BeautyPoseId): void {
  if (chapterId) albumPreviewChapterId = chapterId;
  if (poseId) albumPreviewPoseId = poseId;

  const ch = getBeautyChapterById(albumPreviewChapterId);
  const unlockedPoses = getUnlockedPosesForChapter(ch, userProfile.beautyStagesCleared);
  const poseOrder: BeautyPoseId[] = ['pose1', 'pose2', 'pose3', 'pose4', 'pose5'];
  const activePoseSpec = ch.poses[albumPreviewPoseId] || ch.poses.pose1;
  const activePoseNum = poseOrder.indexOf(albumPreviewPoseId) + 1;
  const isPoseUnlocked = allPosesUnlockedPreview || unlockedPoses.includes(albumPreviewPoseId);

  const chapterChipsHtml = BEAUTY_KNIGHT_CHAPTERS.map(item => {
    const isSel = item.id === ch.id;
    return `<button class="btn btn-secondary" data-action="album-select-chapter" data-ch-id="${item.id}" style="flex:0 0 auto;white-space:nowrap;padding:6px 12px;min-height:34px;font-size:12.5px;${
      isSel ? 'background:#4b3322;color:#fff7e8;border-color:#c89a49' : ''
    }">${item.chapterNumber}장 ${item.knightName}</button>`;
  }).join('');

  const poseThumbsHtml = poseOrder
    .map((pId, idx) => {
      const pSpec = ch.poses[pId];
      const isSelected = pId === albumPreviewPoseId;
      return (
        `<button class="mini-portrait ${isSelected ? 'active' : ''}" style="height:82px;border-width:${
          isSelected ? '2.5px' : '1.5px'
        };border-color:${isSelected ? '#c6933a' : 'rgba(93,66,42,0.28)'};position:relative" data-action="album-select-pose" data-pose="${pId}">` +
        `<img src="${pSpec.imageUrl}" alt="${ch.knightName} ${idx + 1}관문" ${IMG_FALLBACK}>` +
        `<span style="position:absolute;bottom:3px;right:4px;background:rgba(24,15,10,0.82);color:#fff6e6;font-size:10px;font-weight:800;padding:1px 5px;border-radius:6px">${
          idx + 1
        }관문</span>` +
        `</button>`
      );
    })
    .join('');

  showModal(
    `${ch.chapterNumber}장 · ${ch.knightName} 화보첩 (5관문)`,
    `<div style="display:flex;gap:6px;overflow-x:auto;margin:4px 0 10px;padding-bottom:4px">${chapterChipsHtml}</div>` +
      `<div class="secretary-hero" style="height:290px;margin-bottom:10px">` +
      `<img src="${activePoseSpec.imageUrl}" alt="${ch.knightName} ${activePoseSpec.poseTitle}" ${IMG_FALLBACK}>` +
      `${tag(`${activePoseNum}관문 화보 · ${ch.tierLabel}`, 'gold')}` +
      `<div class="secretary-identity"><p>${activePoseSpec.poseTitle} · ${activePoseSpec.expressionTag || ch.titleBadge}</p><h2>${ch.knightName} (${ch.age}세)</h2><span>“${activePoseSpec.quote}”</span></div>` +
      `</div>` +
      `<div style="display:grid;grid-template-columns:repeat(5,1fr);gap:6px;margin-bottom:12px">${poseThumbsHtml}</div>` +
      `<div class="sheet-actions">` +
      `${act('challenge-album-chapter', `${ch.knightName} 대국 도전`, 'btn btn-secondary', `data-ch-id="${ch.id}"`)}` +
      `${act(
        'equip-pose',
        isPoseUnlocked ? '이 화보를 내 비서로 지정' : '이 화보 비서 지정',
        'btn btn-primary',
        `data-ch-id="${ch.id}" data-pose="${albumPreviewPoseId}"`
      )}` +
      `</div>`
  );
}

// ============================================================================
// 액션 핸들러 맵 (14개 화면의 모든 버튼 인터랙션 처리)
// ============================================================================
const actionHandlers: Record<string, (el: HTMLElement) => void> = {
  invite: () => {
    pvpInviteCode = String(Math.floor(1000 + Math.random() * 9000));
    const s = ensureSocketConnected();
    s.emit('join_pvp_queue', {
      userId: userProfile.userId,
      sessionToken,
      nickname: userProfile.nickname,
      tierIndex: userProfile.tierIndex,
      rating: userProfile.rating,
      formation: myFormation,
      roomCode: pvpInviteCode,
      createOnly: true
    });
    showModal(
      '친구를 초대해요',
      `<p class="center">이 번호를 친구에게 알려주세요.</p>` +
        `<div class="invite-number center">${pvpInviteCode}</div>` +
        `${act('share-demo', ico('share') + '초대 번호 복사하기')}` +
        `<div class="space-12"></div>` +
        `${b('matching', '방 대기 화면으로 이동', 'btn btn-secondary')}` +
        `<div class="space-12"></div>` +
        `<p class="center small">친구가 들어오면 대국이 자동으로 시작돼요.</p>`
    );
  },
  'share-demo': () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(pvpInviteCode).catch(() => {});
    }
    showToast(`방 번호 ${pvpInviteCode}번을 복사했어요.`);
  },
  join: () => {
    const input = document.querySelector('#friend-room-code-input, .code-input-row input') as HTMLInputElement | null;
    const val = (input?.value || '').trim();
    if (!/^\d{4}$/.test(val)) {
      showToast('방 번호 4자리를 입력해 주세요.');
      return;
    }
    const s = ensureSocketConnected();
    s.emit('join_pvp_queue', {
      userId: userProfile.userId,
      sessionToken,
      nickname: userProfile.nickname,
      tierIndex: userProfile.tierIndex,
      rating: userProfile.rating,
      formation: myFormation,
      roomCode: val,
      joinOnly: true
    });
    // ★ R15: 서버에서 pvp_match_started가 오기 전에는 game 화면으로 선전환하지 않음!
    showToast(`${val}번 초대방 입장을 요청했어요. 연결을 확인하고 있어요.`);
  },
  'cancel-matching': () => {
    // ★ R14: 매칭 취소 시 서버 큐에서 즉시 제거 후 로비로 복귀
    if (socket) {
      socket.emit('leave_pvp_queue');
    }
    pvpRoomId = '';
    pvpMatchId = '';
    renderScreen('lobby');
    showToast('대국 찾기를 취소하고 홈으로 돌아왔어요.');
  },
  'ai-start': () => {
    const allTierChipsHtml = ALL_TIERS.map(
      (t, idx) =>
        `<button class="btn btn-secondary" data-action="level-choice" data-tier-idx="${idx}" style="${
          aiOpponentTierIndex === idx ? 'background:#d2dfc5;border-color:#2a5a41' : ''
        }">${t.label}</button>`
    ).join('');

    showModal(
      'AI와 한 판 (18급 ~ 9단 27단계)',
      `<p>원하는 급수·단수를 직접 선택하거나 실력 측정 대국을 시작하세요.</p>` +
        `<div class="mini-chips" style="max-height:190px;overflow-y:auto;padding:4px 2px">${allTierChipsHtml}</div>` +
        `<div class="space-12"></div>` +
        `<div class="settings-detail-row"><strong>현재 차림</strong><span>${formationDisplayLabel(myFormation)}</span></div>` +
        `<div class="space-12"></div>` +
        `${act('start-ai-match', `${getTierByIndex(aiOpponentTierIndex).label} AI와 한 판 시작`, 'btn btn-primary')}` +
        `<div class="space-12"></div>` +
        `${act('start-eval-match', '실력 측정 심사 대국 시작', 'btn btn-secondary')}` +
        `<div class="space-12"></div>` +
        `${act('formation', '장기말 차림 바꾸기', 'inline-link')}`
    );
  },
  'start-ai-match': () => {
    closeModal();
    startNewLocalMatch('ai');
  },
  'start-eval-match': () => {
    closeModal();
    startNewLocalMatch('eval');
  },
  'level-choice': el => {
    el.parentElement?.querySelectorAll('button').forEach(btn => {
      (btn as HTMLElement).style.background = '';
      (btn as HTMLElement).style.borderColor = '';
    });
    el.style.background = '#d2dfc5';
    el.style.borderColor = '#2a5a41';
    const idxAttr = el.getAttribute('data-tier-idx');
    if (idxAttr !== null) {
      const idx = Number(idxAttr);
      if (!Number.isNaN(idx) && idx >= 0 && idx < ALL_TIERS.length) {
        aiOpponentTierIndex = idx;
      }
    } else {
      const label = (el.textContent || '').trim();
      const foundIdx = ALL_TIERS.findIndex(t => t.label === label);
      if (foundIdx >= 0) aiOpponentTierIndex = foundIdx;
    }
    const primaryBtn = document.querySelector('[data-action="start-ai-match"]');
    if (primaryBtn) {
      primaryBtn.textContent = `${getTierByIndex(aiOpponentTierIndex).label} AI와 한 판 시작`;
    }
    showToast(`${getTierByIndex(aiOpponentTierIndex).label} 난이도를 골랐어요.`);
  },
  level: () => {
    const allTierChipsHtml = ALL_TIERS.map(
      (t, idx) =>
        `<button class="btn btn-secondary" data-action="level-choice" data-tier-idx="${idx}" style="${
          aiOpponentTierIndex === idx ? 'background:#d2dfc5;border-color:#2a5a41' : ''
        }">${t.label}</button>`
    ).join('');

    showModal(
      '내 실력 알아보기 (급수 심사국 & 27단계)',
      `<p>심사 대국을 통해 내 실력을 측정하거나, 18급~9단 중 원하는 상대를 직접 골라보세요.</p>` +
        `<div class="mini-chips" style="max-height:180px;overflow-y:auto;padding:4px 2px">${allTierChipsHtml}</div>` +
        `<div class="space-12"></div>` +
        `${act('start-eval-match', '실력 측정 심사 대국 시작', 'btn btn-primary')}` +
        `<div class="space-12"></div>` +
        `${act('start-ai-match', `${getTierByIndex(aiOpponentTierIndex).label} 상대와 연습 대국`, 'btn btn-secondary')}`
    );
  },
  'choose-secretary': () => {
    const listHtml = BEAUTY_KNIGHT_CHAPTERS.map(ch => {
      const isEquipped = userProfile.equippedSecretaryId === ch.id;
      const isUnlocked = allPosesUnlockedPreview || canHireChapterSecretary(ch, userProfile.beautyStagesCleared);
      return (
        `<button class="list-card compact" data-action="equip-secretary" data-ch-id="${ch.id}">` +
        `<img class="avatar large" src="${ch.poses.pose1.imageUrl}" alt="${ch.knightName}" ${IMG_FALLBACK}>` +
        `<span><h3>${ch.chapterNumber}장 · ${ch.knightName} (${ch.age}세)</h3><p>${ch.titleBadge} · ${ch.secretaryBuffDesc}</p></span>` +
        `<span class="tag ${isEquipped ? 'gold' : ''}">${isEquipped ? '동행 중' : isUnlocked ? '동행 지정' : '도전'}</span>` +
        `</button>`
      );
    }).join('<div class="space-12"></div>');

    showModal(
      '함께할 미녀 비서 (20인)',
      `<p>원하는 비서를 선택해 동행하거나 화보를 감상해 보세요.</p>` +
        listHtml +
        `<div class="space-12"></div>` +
        `${b('campaign', '미녀대국으로 가기', 'btn btn-secondary')}`
    );
  },
  'equip-secretary': el => {
    const chId = el.getAttribute('data-ch-id') || 'ch_01';
    userProfile.equippedSecretaryId = chId;
    userProfile.equippedSecretaryPoseId = 'pose1';
    if (!userProfile.hiredSecretaryIds.includes(chId)) {
      userProfile.hiredSecretaryIds.push(chId);
    }
    saveProfile();
    closeModal();
    if (soundEnabled) soundEngine.playVictoryFanfare(false);
    renderScreen(currentScreen, true);
    showToast(`${getBeautyChapterById(chId).knightName} 비서와 함께해요!`);
  },
  album: () => {
    openGlamourAlbumModal(
      userProfile.equippedSecretaryId || 'ch_01',
      userProfile.equippedSecretaryPoseId || 'pose1'
    );
  },
  'album-select-chapter': el => {
    const chId = el.getAttribute('data-ch-id') || 'ch_01';
    openGlamourAlbumModal(chId, 'pose1');
  },
  'album-select-pose': el => {
    const pId = (el.getAttribute('data-pose') as BeautyPoseId) || 'pose1';
    openGlamourAlbumModal(albumPreviewChapterId, pId);
  },
  'challenge-album-chapter': el => {
    const chId = el.getAttribute('data-ch-id') || albumPreviewChapterId || 'ch_01';
    activeChapterId = chId;
    closeModal();
    renderScreen('campaign');
  },
  'equip-pose': el => {
    const chId = el.getAttribute('data-ch-id') || albumPreviewChapterId || userProfile.equippedSecretaryId || 'ch_01';
    const pose = (el.getAttribute('data-pose') as BeautyPoseId) || albumPreviewPoseId || 'pose1';
    userProfile.equippedSecretaryId = chId;
    userProfile.equippedSecretaryPoseId = pose;
    if (!userProfile.hiredSecretaryIds.includes(chId)) {
      userProfile.hiredSecretaryIds.push(chId);
    }
    saveProfile();
    closeModal();
    if (soundEnabled) soundEngine.playVictoryFanfare(false);
    renderScreen(currentScreen, true);
    const ch = getBeautyChapterById(chId);
    showToast(`${ch.knightName} 비서의 화보 모습을 지정했어요!`);
  },
  locked: () => showToast('미녀대국에서 한 판씩 이기며 새로운 모습을 만나요.'),
  roster: () => {
    const itemsHtml = BEAUTY_KNIGHT_CHAPTERS.map(ch => {
      return (
        `<button class="list-card compact" data-action="select-chapter-${ch.id}">` +
        `<img class="avatar large" src="${ch.poses.pose1.imageUrl}" alt="${ch.knightName}" ${IMG_FALLBACK}>` +
        `<span><h3>${ch.chapterNumber}장 · ${ch.knightName} (${ch.age}세)</h3><p>${ch.tierLabel} · ${ch.titleBadge}</p></span>` +
        `<span class="tag gold">대국 도전</span>` +
        `</button>`
      );
    }).join('<div class="space-12"></div>');
    showModal('20인 미녀대국 전체 보기', `<p>원하는 상대의 장을 선택하면 바로 대국할 수 있어요.</p>${itemsHtml}`);
  },
  'start-campaign-current': () => {
    startNewLocalMatch('campaign', false);
  },
  'start-campaign-gate': el => {
    const gate = Number(el.getAttribute('data-gate') || '1');
    activeStageIndex = Math.max(0, Math.min(4, gate - 1));
    // ★ R08: 선택한 관문(activeStageIndex)을 덮어쓰지 않고 그대로 시작!
    startNewLocalMatch('campaign', true);
  },
  'start-puzzle': el => {
    const pid = el.getAttribute('data-puzzle-id');
    if (pid) activePuzzleId = pid;
    startNewLocalMatch('puzzle');
  },
  'classic-list': () => {
    showModal(
      '고전 명국 5선 전체 보기',
      `<div class="stack">` +
        ENDGAME_PUZZLE_STAGES.map(
          st =>
            `<button class="list-card compact" data-action="classic-problem" data-puzzle-id="${st.id}">` +
            `<span class="icon-medallion gold">${ico('bulb')}</span>` +
            `<span><h3>${st.stageNumber}관문 · ${st.title}</h3><p>${st.subtitle} · 제한 ${st.maxMovesToWin}수 (보상 金 ${st.rewardCoins.toLocaleString()})</p></span>` +
            `<span class="chev">${ico('chevron')}</span>` +
            `</button>`
        ).join('<div class="space-12"></div>') +
        `</div>`
    );
  },
  'classic-problem': el => {
    const pid = el.getAttribute('data-puzzle-id');
    if (pid) {
      activePuzzleId = pid;
    }
    closeModal();
    startNewLocalMatch('puzzle');
  },
  'retry-puzzle': () => {
    closeModal();
    startNewLocalMatch('puzzle');
    showToast('문제를 처음 배치부터 다시 시작했어요.');
  },
  'extend-moves': () => {
    // ★ R09: +3수 연장권 사용 또는 즉시 구매 후 남은 수순 +3 증가
    if ((userProfile.ownedMoveExtensions || 0) <= 0) {
      const buyRes = purchaseCatalogSku(userProfile, 'sku_extend_3');
      if (!buyRes.ok) {
        showToast('보유한 +3수 연장권과 보석이 부족해요.');
        return;
      }
      userProfile = buyRes.updatedProfile;
    }
    userProfile.ownedMoveExtensions = Math.max(0, (userProfile.ownedMoveExtensions || 1) - 1);
    puzzleMoveBudgetRemaining += 3;
    saveProfile();
    closeModal();
    if (soundEnabled) soundEngine.playHunsuActivate();
    renderScreen('game', true);
    showToast('수순 제한을 +3수 연장했어요! 이어서 풀어보세요.');
  },
  'hint-analyzing': () => showToast('좋은 수를 분석하고 있어요. 잠시만 기다려 주세요 (차감 없음).'),
  'hint-waiting': () => showToast('지금은 어느 수를 두어도 무난해요. 횟수는 줄지 않아요.'),
  'hint-detail': () => {
    hintSource = 'hunsu';
    const currHash = getBoardHash(activeBoard, mySide);
    hintState = revealedTurnHash === currHash && revealedCoachingForTurn ? 'revealed' : 'ready';
    renderScreen('hint');
  },
  'reveal-hint': () => {
    if (assistRevealInFlight) return;
    if (currentTurn !== mySide && activeGameMode !== 'puzzle') {
      showToast('내 차례에만 훈수를 열람할 수 있어요 (차감 없음).');
      return;
    }
    hintSource = 'hunsu';
    const currHash = getBoardHash(activeBoard, mySide);
    // ★ A09: 동일 국면(currHash)에서 이미 열람한 경우에는 중복 차감하지 않음!
    if (revealedTurnHash === currHash && revealedCoachingForTurn) {
      if (soundEnabled) soundEngine.playHunsuActivate();
      hintState = 'revealed';
      renderScreen('hint', true);
      return;
    }

    if (getBucketTotal(userProfile, 'hunsu') <= 0) {
      showToast('보유한 훈수가 부족해요. 상점에서 충전할 수 있어요.');
      return;
    }

    const stamp = buildCurrentAnalysisStamp(mySide, activeBoard);
    const finalizeReveal = (evalRes: { hunsu?: HunsuCoachingData | null } | null) => {
      assistRevealInFlight = false;
      // 턴이 만료되었거나 국면이 바뀐 경우 아이템 차감 0회 보장!
      if (
        stampKey(stamp) !== stampKey(buildCurrentAnalysisStamp(mySide, activeBoard)) ||
        (currentTurn !== mySide && activeGameMode !== 'puzzle')
      ) {
        return;
      }
      const clk = getActiveTurnClock();
      if (clk && clk.status === 'running' && remainingMs(clk, getActiveClockNowMs()) <= 0) {
        return;
      }
      if (!evalRes?.hunsu) {
        showToast('현재 국면에서는 추천할 뚜렷한 수가 없어 차감되지 않았어요.');
        return;
      }
      const deductRes = consumeInventoryItem(userProfile, 'hunsu', 1);
      if (!deductRes.ok) {
        showToast('보유한 훈수가 부족해요. 상점에서 충전할 수 있어요.');
        return;
      }
      saveProfile();
      syncAuthenticatedProfileToServer();
      if (activeGameMode === 'pvp' && socket && pvpRoomId) {
        socket.emit('assist_reveal', {
          roomId: pvpRoomId,
          userId: userProfile.userId,
          sessionToken,
          expectedBoardHash: currHash
        });
      }
      revealedCoachingForTurn = evalRes.hunsu;
      revealedTurnHash = currHash;
      if (soundEnabled) {
        soundEngine.playHunsuActivate();
      }
      hintState = 'revealed';
      renderScreen('hint', true);
    };

    if (typeof Worker !== 'undefined') {
      assistRevealInFlight = true;
      bgAnalysisBroker
        .request<HunsuCoachingData | null>('coaching', { board: activeBoard, side: mySide }, stamp)
        .then(out => {
          if (out.status === 'ok') {
            finalizeReveal({ hunsu: out.value });
          } else {
            assistRevealInFlight = false;
          }
        })
        .catch(() => {
          assistRevealInFlight = false;
        });
    } else {
      finalizeReveal(computeHunsuCoaching(activeBoard, mySide));
    }
  },
  'apply-hint': () => {
    const moveToPlay =
      hintSource === 'shield' && pendingCrisis
        ? pendingCrisis.crisis.recommendedSafeMove
        : revealedCoachingForTurn?.step1Move;

    if (moveToPlay) {
      executeMoveOnBoard(moveToPlay);
    } else {
      renderScreen('game');
    }
  },
  'safe-move': () => {
    if (currentTurn !== mySide || awaitingMoveAck) {
      showToast('차례가 바뀌어 방지권이 차감되지 않았어요.');
      renderScreen('game', true);
      return;
    }
    const clk = getActiveTurnClock();
    if (clk && clk.status === 'running' && remainingMs(clk, getActiveClockNowMs()) <= 0) {
      return;
    }
    // ★ A06: 검증된 안전 대안수가 존재할 때만 1회 차감!
    if (pendingCrisis && !pendingCrisis.crisis.hasVerifiedSafeAlternative) {
      showToast('현재 뚜렷한 안전 회피수가 없어 방지권은 차감되지 않아요.');
      renderScreen('game', true);
      return;
    }
    const deductRes = consumeInventoryItem(userProfile, 'shield', 1);
    if (!deductRes.ok) {
      showToast('보유한 위기 감지 방지권이 부족해요.');
      return;
    }
    shieldUsesInCurrentMatch += 1;
    saveProfile();
    syncAuthenticatedProfileToServer();
    if (soundEnabled) {
      soundEngine.playHunsuActivate();
    }
    hintSource = 'shield';
    hintState = 'revealed';
    renderScreen('hint', true);
  },
  'keep-move': () => {
    // ★ A05: '그대로 두기' 선택 시 방지권 0회 차감 & 동일 수 재경고 억제!
    if (pendingCrisis) {
      suppressedMoveKeys.add(pendingCrisis.crisis.moveKey);
      const move = pendingCrisis.candidateMove;
      pendingCrisis = null;
      executeMoveOnBoard(move);
      return;
    }
    renderScreen('game');
  },
  restore: () => {
    if (socket && pvpRoomId) {
      socket.emit('pvp_rejoin_room', {
        roomId: pvpRoomId,
        side: mySide,
        userId: userProfile.userId,
        sessionToken
      });
    }
    renderScreen('game');
    showToast('연결 상태를 확인하고 대국판을 동기화했어요.');
  },
  sangsu: () => {
    activeSangsuVision = computeSangsuMasuVision(activeBoard, mySide, 'sangsu');
    if (soundEnabled) {
      soundEngine.playSangsuActivate();
    }
    renderScreen('game', true);
    showToast('상(象)의 3턴 이동 경로를 판 위에 표시했어요.');
  },
  'enemy-intent': () => {
    activeEnemyIntentVision = computeEnemyIntentVision(activeBoard, mySide);
    if (soundEnabled) {
      soundEngine.playMyosuActivate();
    }
    closeModal();
    renderScreen('game', true);
    showToast(activeEnemyIntentVision.summaryComment || '상대의 다음 노림수를 판 위에 표시했어요.');
  },
  'game-menu': () => {
    showModal(
      '대국 메뉴',
      `<div class="more-grid">` +
        `${act('all-items', ico('bag') + '도움 충전', 'more-tile')}` +
        `${act('enemy-intent', ico('eye') + '상대 노림수', 'more-tile')}` +
        `${act('emote', ico('smile') + '감정표현', 'more-tile')}` +
        `${act('pass', ico('play') + '한 수 쉬기', 'more-tile')}` +
        `${act('undo', ico('undo') + '무르기', 'more-tile')}` +
        `${act('settings', ico('settings') + '화면·소리', 'more-tile')}` +
        `</div>` +
        `${act('resign', '기권하기', 'btn btn-secondary')}` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '계속 두기', 'btn btn-primary')}`
    );
  },
  emote: () => {
    // ★ R17: 12종 HD 감정표현 스티커 그리드 팝업
    const gridHtml = EMOTE_CATALOG.map(
      em =>
        `<button class="list-card compact" data-action="send-emote" data-emote-id="${em.id}" style="padding:8px;flex-direction:column;text-align:center;gap:4px">` +
        `<img src="${em.imageUrl}" alt="${em.title}" style="width:52px;height:52px;object-fit:contain" ${IMG_FALLBACK}>` +
        `<strong style="font-size:13px">${em.title}</strong>` +
        `<small class="muted" style="font-size:11px">${em.subtitle}</small>` +
        `</button>`
    ).join('');

    showModal(
      '감정표현 스티커 (12종 HD)',
      `<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;max-height:340px;overflow-y:auto;margin-bottom:12px">${gridHtml}</div>` +
        `${act('close-modal', '닫기', 'btn btn-secondary')}`
    );
  },
  'send-emote': el => {
    const emoteId = el.getAttribute('data-emote-id') || 'laugh';
    const emote = EMOTE_CATALOG.find(e => e.id === emoteId) || EMOTE_CATALOG[0];
    if (socket && pvpRoomId) {
      socket.emit('pvp_send_emoticon', { emoteId: emote.id });
    }
    if (soundEnabled) {
      soundEngine.playEmoteSound(emote.soundType);
    }
    closeModal();
    showFloatingEmoteBubble(emote, mySide);
    showToast(`${emote.title} · "${emote.subtitle}" 감정표현을 보냈어요.`);
  },
  'emote-demo': el => {
    actionHandlers['send-emote'](el);
  },
  pass: () => {
    closeModal();
    if (activeGameMode === 'spectate') {
      showToast('관전 중에는 수를 쉴 수 없어요.');
      return;
    }
    if (currentTurn !== mySide || awaitingMoveAck || crisisCheckInFlight) {
      showToast('내 차례에만 한 수 쉬기를 할 수 있어요.');
      return;
    }
    // ★ R11: 장군 상태에서는 한 수 쉬기 불가!
    if (isKingInCheck(activeBoard, mySide)) {
      if (soundEnabled) soundEngine.playSfx('sfx_byoyomi_tick', 0.9);
      showToast('장군을 받은 상태에서는 한 수 쉬기를 할 수 없어요!');
      return;
    }

    if (activeGameMode === 'pvp' && socket && pvpRoomId) {
      if (pvpTurnClock && pvpTurnClock.status === 'running' && remainingMs(pvpTurnClock, getActiveClockNowMs()) <= 0) {
        showToast('제한시간(30초)이 지나 차례 전환을 확인하고 있어요.');
        return;
      }
      awaitingMoveAck = true;
      socket.emit(
        'pvp_commit_move',
        {
          matchId: pvpMatchId || pvpRoomId,
          epoch: pvpEpoch,
          expectedVersion: pvpVersion,
          commandId: `pass_${pvpEpoch}_${pvpVersion}_${Date.now()}`,
          kind: 'pass',
          isPass: true
        },
        (ack?: any) => {
          if (ack?.status === 'committed') {
            applyAuthoritativePvpCommit(ack, 'self');
          } else if (ack?.status === 'rejected') {
            handlePvpMoveRejected(ack);
          }
        }
      );
      return;
    }

    if (soundEnabled) {
      soundEngine.playPassTurn();
    }

    // 로컬 모드: 패스 기보 기록 (lastMove 직전 실제 착수 표시는 보존!), 30초 시계 갱신 & 내 idleStreak 0 초기화
    const enemySide: Side = mySide === 'cho' ? 'han' : 'cho';
    const nowMs = Date.now();
    if (localTurnClock) {
      localRemainingBudgetStack.push({
        budgetMs: remainingMs(localTurnClock, nowMs),
        isTimeoutPass: false
      });
      localTurnClock = commitLegalAction(
        localTurnClock,
        localTurnClock.token,
        mySide,
        nowMs,
        'manual_pass',
        true,
        false
      ).clock;
    }
    localTurnGeneration += 1;

    recordedHistory.push({
      ply: recordedHistory.length + 1,
      side: mySide,
      move: {
        from: { row: 0, col: 0 },
        to: { row: 0, col: 0 },
        piece: { id: 'manual_pass_king', type: 'king', side: mySide },
        captured: null,
        notation: '한수쉼'
      },
      boardBefore: activeBoard.map(r => r.map(c => (c ? { ...c } : null))),
      boardAfter: activeBoard.map(r => r.map(c => (c ? { ...c } : null))),
      elapsedSeconds: 1
    });
    moveNumber += 1;
    currentTurn = enemySide;
    selectedPos = null;
    legalTargetsForSelected = [];
    updateGameHudInPlace();
    renderAllBoardCanvases();
    showToast('한 수 쉬기를 선택했어요. 상대가 수를 둡니다.');
    scheduleAiTurnResponse(activeMatchSessionId, enemySide);
  },
  undo: () => {
    showModal(
      '한 수 무르기',
      `<p>${
        activeGameMode === 'pvp'
          ? '상대에게 한 수 무르기 동의를 요청할까요?'
          : `직전에 둔 수를 되돌릴 수 있어요. (보유 무르기권 ${userProfile.ownedUndoItems ?? 0}개)`
      }</p>` +
        `${act('undo-demo', activeGameMode === 'pvp' ? '무르기 요청 보내기' : '무르기 실행', 'btn btn-secondary')}` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '계속 두기', 'btn btn-primary')}`
    );
  },
  'undo-demo': () => {
    closeModal();
    // ★ R12: PvP에서는 서버 합의형 무르기 요청 발송
    if (activeGameMode === 'pvp' && socket && pvpRoomId) {
      socket.emit('pvp_request_undo');
      showToast('상대에게 한 수 무르기를 요청했어요.');
      return;
    }

    if (recordedHistory.length === 0) {
      showToast('아직 무를 수 있는 수순이 없어요.');
      return;
    }

    const lastPly = recordedHistory[recordedHistory.length - 1];
    const prevPly = recordedHistory[recordedHistory.length - 2];
    const willUndoTwo = recordedHistory.length >= 2 && lastPly?.side !== mySide;
    if (
      lastPly?.move?.notation === '시간초과쉼' ||
      (willUndoTwo && prevPly?.move?.notation === '시간초과쉼')
    ) {
      showToast('시간초과로 넘어간 차례는 무를 수 없어요.');
      return;
    }

    if ((userProfile.ownedUndoItems ?? 0) > 0) {
      userProfile.ownedUndoItems = Math.max(0, userProfile.ownedUndoItems - 1);
      saveProfile();
    }

    bgAnalysisBroker.cancel();
    actionAnalysisBroker.cancel();
    crisisCheckInFlight = false;
    assistRevealInFlight = false;
    localTurnGeneration += 1;

    let restoredBudgetMs = TURN_MS;
    if (willUndoTwo) {
      recordedHistory.pop();
      localRemainingBudgetStack.pop();
      const prev = recordedHistory.pop();
      const prevBudget = localRemainingBudgetStack.pop();
      if (prevBudget && prevBudget.budgetMs > 0) {
        restoredBudgetMs = prevBudget.budgetMs;
      }
      if (prev) {
        activeBoard = prev.boardBefore.map(r => r.map(c => (c ? { ...c } : null)));
      }
    } else {
      const prev = recordedHistory.pop();
      const prevBudget = localRemainingBudgetStack.pop();
      if (prevBudget && prevBudget.budgetMs > 0) {
        restoredBudgetMs = prevBudget.budgetMs;
      }
      if (prev) {
        activeBoard = prev.boardBefore.map(r => r.map(c => (c ? { ...c } : null)));
      }
    }

    if (localTurnClock) {
      localTurnClock = restoreClockOnUndo(localTurnClock, mySide, restoredBudgetMs, Date.now());
    }

    // lastMove를 직전 실제 기물 착수 기록으로 복원
    lastMove = null;
    for (let i = recordedHistory.length - 1; i >= 0; i--) {
      const m = recordedHistory[i]?.move;
      if (m && m.notation !== '한수쉼' && m.notation !== '시간초과쉼') {
        lastMove = m;
        break;
      }
    }

    currentTurn = mySide;
    moveNumber = Math.max(1, recordedHistory.length + 1);
    selectedPos = null;
    legalTargetsForSelected = [];
    rebuildCapturedListsFromHistory();
    renderScreen('game', true);
    showToast('직전 수를 물렸어요.');
  },
  'pvp-undo-accept': () => {
    closeModal();
    if (socket && pvpRoomId) {
      socket.emit('pvp_respond_undo', { accepted: true, accept: true });
    }
  },
  'pvp-undo-decline': () => {
    closeModal();
    if (socket && pvpRoomId) {
      socket.emit('pvp_respond_undo', { accepted: false, accept: false });
    }
  },
  rematch: () => {
    // ★ R13: 결과 화면 재대국 버튼 핸들러
    if (activeGameMode === 'pvp') {
      if (socket && pvpRoomId) {
        socket.emit('pvp_request_rematch');
        showToast('상대에게 재대국을 요청했어요. 수락하면 바로 시작돼요.');
      }
      return;
    }
    startNewLocalMatch(activeGameMode, true);
    showToast('새 대국을 시작했어요!');
  },
  'pvp-rematch-accept': () => {
    closeModal();
    if (socket && pvpRoomId) {
      socket.emit('pvp_respond_rematch', { accepted: true, accept: true });
    }
  },
  'pvp-rematch-decline': () => {
    closeModal();
    if (socket && pvpRoomId) {
      socket.emit('pvp_respond_rematch', { accepted: false, accept: false });
    }
    showToast('재대국 요청을 거절했어요.');
  },
  resign: () => {
    showModal(
      '이 대국을 마칠까요?',
      `<p>기권하면 이번 대국은 패배로 끝나요.</p>` +
        `${act('resign-demo', '기권하기', 'btn btn-red')}` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '계속 두기', 'btn btn-secondary')}`
    );
  },
  'resign-demo': () => {
    closeModal();
    if (activeGameMode === 'pvp' && socket && pvpRoomId) {
      socket.emit('pvp_resign');
    } else {
      handleLocalMatchFinished('loss', '기권으로 대국을 마쳤어요.');
    }
  },
  records: () => {
    // ★ R16: 실제 저장된 대국 기록(savedMatchRecords) 표시 (기록 없을 때만 [데모 샘플] 배지 명시)
    let recordsHtml = '';
    if (savedMatchRecords.length > 0) {
      recordsHtml = savedMatchRecords
        .slice(0, 8)
        .map(
          (rec, idx) =>
            `<div class="record-row" style="cursor:pointer" data-action="review-record" data-record-idx="${idx}">` +
            `<div class="row between"><strong>${rec.title} (${rec.modeLabel})</strong>` +
            `<span class="${rec.outcome === 'win' ? 'record-win' : 'muted'}">${
              rec.outcome === 'win' ? '승리' : rec.outcome === 'loss' ? '패배' : '무효'
            }</span></div>` +
            `<p>${rec.dateLabel} · ${rec.reason} · 총 ${rec.history.length}수</p>` +
            `</div>`
        )
        .join('');
    } else {
      recordsHtml =
        `<div class="record-row"><div class="row between"><strong>[데모 샘플] 산들바람과 대국</strong><span class="tag">데모 샘플</span></div><p>실제 대국을 마치면 이곳에 내 기보가 자동 저장돼요.</p></div>`;
    }

    showModal(
      `내 대국 기록 (${savedMatchRecords.length}국 보관 중)`,
      recordsHtml +
        `<div class="space-12"></div>` +
        `${act('review', '최근 대국 AI 복기 · 승률 그래프 보기', 'btn btn-primary')}` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '닫기', 'btn btn-secondary')}`
    );
  },
  'empty-records': () => {
    showModal(
      '내 대국 기록',
      `<p class="center" style="margin:30px 0 40px">아직 대국 기록이 없어요.<br>첫 대국을 시작해 볼까요?</p>` +
        `${b('matching', '대국 시작')}`
    );
  },
  'review-record': el => {
    const idx = Number(el.getAttribute('data-record-idx') || '0');
    const rec = savedMatchRecords[idx];
    if (rec && rec.history.length > 0) {
      recordedHistory = rec.history.map(m => ({
        ...m,
        boardBefore: m.boardBefore.map(r => r.map(c => (c ? { ...c } : null))),
        boardAfter: m.boardAfter.map(r => r.map(c => (c ? { ...c } : null)))
      }));
      mySide = rec.mySide;
    }
    actionHandlers.review(el);
  },
  review: () => {
    // ★ R16: AI 승률 그래프 + 패착/승착 포인트 + 선택한 수순부터 이어서 두기
    const historyToReview =
      recordedHistory.length > 0
        ? recordedHistory
        : savedMatchRecords[0]?.history || [];

    const report = generatePostGameReview('match_latest', mySide, historyToReview);
    selectedReviewPlyIndex = Math.max(0, historyToReview.length - 1);

    const summary =
      report.missedWinningPoints.length > 0
        ? report.missedWinningPoints[0].coachComment
        : report.overallSummary;

    const barsHtml =
      report.winRateCurve.length > 0
        ? report.winRateCurve
            .slice(-16)
            .map(
              pt =>
                `<div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:2px">` +
                `<div style="width:100%;height:44px;background:#ece3d2;border-radius:4px;display:flex;align-items:flex-end;overflow:hidden">` +
                `<div style="width:100%;height:${pt.winRatePercent}%;background:${
                  pt.winRatePercent >= 50 ? '#2a5a41' : '#b83c32'
                }"></div>` +
                `</div>` +
                `<small style="font-size:10px">${pt.ply}수</small>` +
                `</div>`
            )
            .join('')
        : `<div class="muted small center" style="width:100%;padding:10px 0">대국 수순이 쌓이면 승률 그래프가 표시돼요.</div>`;

    const plyChipsHtml =
      historyToReview.length > 0
        ? historyToReview
            .map(
              (m, idx) =>
                `<button class="btn btn-secondary" data-action="select-review-ply" data-ply-idx="${idx}" style="min-height:32px;padding:4px 8px;font-size:12px;${
                  idx === selectedReviewPlyIndex ? 'background:#d2dfc5;border-color:#2a5a41' : ''
                }">${m.ply}수 · ${m.move?.notation || '착수'}</button>`
            )
            .join('')
        : '';

    if (historyToReview[selectedReviewPlyIndex]) {
      activeBoard = historyToReview[selectedReviewPlyIndex].boardBefore.map(r => r.map(c => (c ? { ...c } : null)));
    }

    showModal(
      `AI 복기 리포트 (정확도 ${report.accuracyPercent}%)`,
      `<p>${summary}</p>` +
        `<div style="display:flex;gap:4px;align-items:flex-end;padding:8px;background:#f7f2e7;border-radius:10px;margin:8px 0">${barsHtml}</div>` +
        (plyChipsHtml
          ? `<div class="mini-chips" style="max-height:84px;overflow-y:auto;margin-bottom:8px">${plyChipsHtml}</div>`
          : '') +
        `<div class="review-board-preview">${board('hint', true)}</div>` +
        `${act('resume-from-review', '이 자리부터 AI와 이어서 두기', 'btn btn-primary')}` +
        `<div class="space-12"></div>` +
        `${act('records', '대국 기록 목록으로', 'btn btn-secondary')}`
    );
  },
  'select-review-ply': el => {
    const historyToReview =
      recordedHistory.length > 0
        ? recordedHistory
        : savedMatchRecords[0]?.history || [];
    const idx = Number(el.getAttribute('data-ply-idx') || '0');
    if (historyToReview[idx]) {
      selectedReviewPlyIndex = idx;
      activeBoard = historyToReview[idx].boardBefore.map(r => r.map(c => (c ? { ...c } : null)));
      el.parentElement?.querySelectorAll('button').forEach(btn => {
        (btn as HTMLElement).style.background = '';
        (btn as HTMLElement).style.borderColor = '';
      });
      el.style.background = '#d2dfc5';
      el.style.borderColor = '#2a5a41';
      renderAllBoardCanvases();
      showToast(`${historyToReview[idx].ply}수 (${historyToReview[idx].move?.notation || '착수'}) 시점의 국면이에요.`);
    }
  },
  'resume-from-review': () => {
    // ★ R16: 선택한 복기 수순의 국면 스냅샷에서 실제로 AI 대국 이어하기!
    const historyToReview =
      recordedHistory.length > 0
        ? recordedHistory
        : savedMatchRecords[0]?.history || [];
    const targetPly = historyToReview[selectedReviewPlyIndex];
    closeModal();
    stopSpectatorTimerIfActive();
    bgAnalysisBroker.cancel();
    actionAnalysisBroker.cancel();
    activeMatchSessionId += 1;
    localTurnGeneration += 1;
    activeGameMode = 'ai';
    if (targetPly) {
      activeBoard = targetPly.boardBefore.map(r => r.map(c => (c ? { ...c } : null)));
      recordedHistory = historyToReview.slice(0, selectedReviewPlyIndex);
      moveNumber = recordedHistory.length + 1;
    } else {
      activeBoard = createInitialBoard(myFormation, 'sangMaSangMa');
      recordedHistory = [];
      moveNumber = 1;
    }
    currentTurn = mySide;
    selectedPos = null;
    legalTargetsForSelected = [];
    localRemainingBudgetStack = [];
    localTurnClock = createTurnClock(`local_ai_${activeMatchSessionId}`, 1, mySide, Date.now(), TURN_MS);
    lastMove = null;
    for (let i = recordedHistory.length - 1; i >= 0; i--) {
      const m = recordedHistory[i]?.move;
      if (m && m.notation !== '한수쉼' && m.notation !== '시간초과쉼') {
        lastMove = m;
        break;
      }
    }
    rebuildCapturedListsFromHistory();
    opponentNickname = `${getTierByIndex(aiOpponentTierIndex).label} 복기 대국 AI`;
    opponentTierLabel = getTierByIndex(aiOpponentTierIndex).label;
    renderScreen('game', true);
    showToast(`${moveNumber}수 국면부터 AI와 이어서 대국을 시작해요!`);
  },
  rank: () => {
    renderScreen('rank');
  },
  'rank-cat': el => {
    const cat = (el.getAttribute('data-cat') as LeaderboardCategory) || 'tier';
    activeRankCategory = cat;
    renderScreen('rank', true);
  },
  'challenge-rank-master': el => {
    const tierIdx = Number(el.getAttribute('data-tier-idx') || '22');
    const masterName = el.getAttribute('data-master-name') || '천하명인';
    aiOpponentTierIndex = Math.max(0, Math.min(ALL_TIERS.length - 1, tierIdx));
    closeModal();
    startNewLocalMatch('ai');
    opponentNickname = masterName;
    opponentTierLabel = getTierByIndex(aiOpponentTierIndex).label;
    renderScreen('game', true);
    showToast(`${masterName} (${opponentTierLabel}) 명인에게 도전합니다!`);
  },
  spectate: () => {
    showModal(
      'AI 고수 구경 (시뮬레이션)',
      `<p>9단 천하명인 AI 두 명이 실시간으로 수순을 주고받는 시뮬레이션 대국을 관전해요.</p>` +
        `${act('start-spectate', '실시간 관전 시작하기', 'btn btn-primary')}` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '돌아가기', 'btn btn-secondary')}`
    );
  },
  'start-spectate': () => {
    closeModal();
    startNewLocalMatch('spectate');
  },
  formation: () => {
    const choices: Array<[FormationType, string]> = [
      ['maSangMaSang', '마상마상'],
      ['sangMaSangMa', '상마상마'],
      ['maSangSangMa', '안상 (마상상마)'],
      ['sangMaMaSang', '바깥상 (상마마상)']
    ];
    showModal(
      '장기말 차림 (4대 정통 포진)',
      `<p>다음 대국에 적용할 마(馬)와 상(象) 배치를 선택하세요.</p>` +
        `<div class="mini-chips">` +
        choices
          .map(
            ([code, label]) =>
              `<button class="btn btn-secondary" data-action="formation-choice" data-formation="${code}" style="${
                myFormation === code ? 'background:#d1dfc2;border-color:#2a5a41' : ''
              }">${label}</button>`
          )
          .join('') +
        `</div>` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '이 차림으로 준비')}`
    );
  },
  'formation-choice': el => {
    el.parentElement?.querySelectorAll('button').forEach(btn => {
      (btn as HTMLElement).style.background = '';
      (btn as HTMLElement).style.borderColor = '';
    });
    el.style.background = '#d1dfc2';
    el.style.borderColor = '#2a5a41';
    const raw = el.getAttribute('data-formation') || el.textContent || 'sangMaSangMa';
    myFormation = normalizeFormation(raw);
    try {
      window.localStorage.setItem(FORMATION_STORAGE_KEY, myFormation);
    } catch {
      // ignore
    }
    showToast(`${formationDisplayLabel(myFormation)} 차림으로 저장했어요.`);
  },
  'skin-settings': () => {
    shopTab = 'skin';
    renderScreen('shop');
  },
  'assist-settings': () => {
    const activeLabels = (['cha', 'po', 'sa', 'ma', 'sang'] as GuardablePieceType[])
      .filter(k => guardConfig[k])
      .map(k => PIECE_NAMES_KO[k].cho)
      .join(' · ');
    showModal(
      '도움 설정',
      `<p>내가 원할 때 도움을 확인해요.</p>` +
        `<div class="settings-detail-row"><strong>위험한 수 알림</strong><span>무료 표시</span></div>` +
        `<div class="settings-detail-row"><strong>아이템 사용</strong><span>직접 확인</span></div>` +
        `<div class="settings-detail-row"><strong>보호할 장기말</strong><span>${activeLabels || '없음'}</span></div>` +
        `<div class="space-20"></div>` +
        `<div class="mini-chips">` +
        (
          [
            ['차', 'cha'],
            ['포', 'po'],
            ['사', 'sa'],
            ['마', 'ma'],
            ['상', 'sang']
          ] as Array<[string, GuardablePieceType]>
        )
          .map(
            ([label, key]) =>
              `<button class="btn btn-secondary" data-action="guard-choice" data-guard-key="${key}" style="${
                guardConfig[key] ? 'background:#d1dfc2' : ''
              }">${label}</button>`
          )
          .join('') +
        `</div>` +
        `${act('close-modal', '저장하고 돌아가기')}`
    );
  },
  'guard-choice': el => {
    const key = el.getAttribute('data-guard-key') as GuardablePieceType | null;
    if (key && key in guardConfig) {
      guardConfig[key] = !guardConfig[key];
      el.style.background = guardConfig[key] ? '#d1dfc2' : '';
      showToast('보호할 장기말 설정을 바꿨어요.');
    }
  },
  'surround-theme': () => {
    surroundTheme = surroundTheme === 'graphite' ? 'warm-stone' : 'graphite';
    try {
      window.localStorage.setItem(SURROUND_THEME_STORAGE_KEY, surroundTheme);
    } catch {
      // ignore
    }
    syncBodyPlayClasses(currentScreen);
    const liveGameScreen = document.querySelector('.game-screen');
    if (liveGameScreen) {
      liveGameScreen.classList.toggle('theme-warm-stone', surroundTheme === 'warm-stone');
      liveGameScreen.classList.toggle('theme-graphite', surroundTheme === 'graphite');
    }
    const label = surroundTheme === 'warm-stone' ? '따뜻한 한지(Warm Stone)' : '차콜 골드(Graphite Champagne)';
    if (document.querySelector('.app-modal')) {
      actionHandlers.settings(document.body);
    } else {
      renderScreen(currentScreen, true);
    }
    showToast(`대국 배경을 ${label} 테마로 바꿨어요.`);
  },
  'install-pwa': async () => {
    const isStandalone =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches;
    if (isStandalone) {
      showToast('이미 설치형 전체화면 앱 모드로 실행 중이에요!');
      return;
    }
    if (deferredPwaInstallPrompt) {
      try {
        await deferredPwaInstallPrompt.prompt();
        const choice = await deferredPwaInstallPrompt.userChoice;
        deferredPwaInstallPrompt = null;
        if (choice?.outcome === 'accepted') {
          showToast('홈 화면에 천하장기 앱을 설치하고 있어요!');
          closeModal();
        }
      } catch {
        showToast('브라우저 메뉴에서 [홈 화면에 추가]를 선택해 설치할 수 있어요.');
      }
      return;
    }
    showModal(
      '전체화면 앱으로 설치하기',
      `<p>천하장기를 스마트폰 바탕화면에 앱으로 설치하면 상단·하단 주소창 없이 1:1 정사각 대형 장기판으로 즐길 수 있어요.</p>` +
        `<div class="quota-panel" style="margin:12px 0">` +
        `<div class="row between"><span>Android (Chrome)</span><strong>메뉴(⋮) → 앱 설치 / 홈 화면에 추가</strong></div>` +
        `<div class="row between"><span>iPhone (Safari)</span><strong>공유(□↑) → 홈 화면에 추가</strong></div>` +
        `<div class="row between"><span>카카오톡 내부 브라우저</span><strong>메뉴(⋮) → 다른 브라우저로 열기 후 설치</strong></div>` +
        `</div>` +
        `${act('account-transfer', '설치한 앱으로 내 계정·화보 이어받기', 'btn btn-secondary')}` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '확인', 'btn btn-primary')}`
    );
  },
  'account-transfer': () => {
    showModal(
      '내 계정 · 설치 앱 이어하기',
      `<p>카카오톡 웹뷰나 기존 브라우저에서 플레이하던 급수·보석·보유 아이템·비서 화보를 설치형 앱으로 안전하게 옮길 수 있어요. (1회용 암호 코드 · 10분 유효)</p>` +
        `<div class="quota-panel" style="margin:10px 0">` +
        `<div class="row between"><span>현재 대국자</span><strong>${userProfile.nickname} (${getTierByIndex(userProfile.tierIndex).label})</strong></div>` +
        `<div class="row between"><span>보유 재화</span><strong>金 ${userProfile.coins.toLocaleString()} · ◆ ${userProfile.gems.toLocaleString()}</strong></div>` +
        `</div>` +
        `<div id="transfer-code-display" style="display:none;margin:10px 0;padding:12px;border-radius:12px;background:#1c1e24;color:#f5ebd6;text-align:center;border:1px solid rgba(226,196,138,0.45)"></div>` +
        `${act('issue-transfer-code', '1회용 이어하기 코드 발급하기', 'btn btn-primary')}` +
        `<div class="space-20"></div>` +
        `<div class="menu-section-title" style="margin-bottom:6px">다른 기기·설치 앱에서 코드 입력</div>` +
        `<div class="code-input-row" style="display:flex;gap:8px;align-items:center">` +
        `<input id="transfer-code-input" type="text" maxlength="12" placeholder="8자리 코드 입력" style="flex:1;min-height:44px;padding:8px 12px;border-radius:10px;border:1.5px solid rgba(93,66,42,0.3);font-size:16px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase" />` +
        `${act('redeem-transfer-code', '불러오기', 'btn btn-secondary', 'style="flex:0 0 auto;min-height:44px;padding:0 16px"')}` +
        `</div>`
    );
  },
  'issue-transfer-code': async () => {
    const authed = await ensureRestSessionAuthenticated();
    if (!authed) {
      showToast('서버 연결을 확인한 뒤 다시 시도해 주세요.');
      return;
    }
    try {
      const resp = await fetch('/api/session/transfer-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userProfile.userId,
          sessionToken,
          profile: userProfile
        })
      });
      const data = await resp.json();
      if (!resp.ok || !data?.ok || !data?.transferCode) {
        showToast('이어하기 코드를 발급하지 못했어요. 잠시 후 다시 시도해 주세요.');
        return;
      }
      const box = document.getElementById('transfer-code-display');
      if (box) {
        box.style.display = 'block';
        box.innerHTML =
          `<div style="font-size:12px;color:#d6c39b;margin-bottom:4px">10분 내 1회 사용 가능한 인증 코드</div>` +
          `<div style="font-size:24px;font-weight:800;letter-spacing:0.16em;color:#f3d58d">${data.transferCode}</div>`;
      }
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(data.transferCode).catch(() => {});
      }
      showToast(`이어하기 코드(${data.transferCode})를 발급·복사했어요.`);
    } catch {
      showToast('네트워크 오류로 코드를 발급하지 못했어요.');
    }
  },
  'redeem-transfer-code': async () => {
    const input = document.getElementById('transfer-code-input') as HTMLInputElement | null;
    const raw = (input?.value || '').trim().toUpperCase();
    if (raw.length < 6) {
      showToast('발급받은 8자리 이어하기 코드를 입력해 주세요.');
      return;
    }
    try {
      const resp = await fetch('/api/session/redeem-transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transferCode: raw })
      });
      const data = await resp.json();
      if (!resp.ok || !data?.ok || !data?.sessionToken || !data?.profile) {
        showToast(
          data?.reason === 'transfer_code_expired'
            ? '만료된 코드예요. 새 코드를 발급받아 주세요.'
            : '유효하지 않거나 이미 사용된 코드예요.'
        );
        return;
      }
      sessionToken = data.sessionToken;
      userProfile = migrateUserProfile({ ...data.profile, userId: data.userId });
      saveProfile();
      try {
        localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
      } catch {
        // ignore
      }
      closeModal();
      renderScreen(currentScreen, true);
      showToast(`${userProfile.nickname} 계정과 보유 아이템·화보를 이어받았어요!`);
    } catch {
      showToast('네트워크 오류로 코드를 확인하지 못했어요.');
    }
  },
  settings: () => {
    const isLarge = document.getElementById('app')?.classList.contains('large-text');
    const surroundLabel = surroundTheme === 'warm-stone' ? '따뜻한 한지' : '차콜 골드';
    const isStandalone =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches;
    const installBtnLabel = isStandalone
      ? '설치형 전체화면 앱 실행 중'
      : deferredPwaInstallPrompt
      ? '홈 화면에 전체화면 앱 바로 설치'
      : '앱 설치 · 외부 브라우저 열기 안내';
    showModal(
      '편하게 보기 · 앱 설치',
      `<div class="settings-list">` +
        `${renderSettingRow('audio', '소리', 'sound', soundEnabled ? 'on' : 'off')}` +
        `${renderSettingRow('text', '큰 글씨', 'big-text', isLarge ? 'on' : 'off')}` +
        `${renderSettingRow('palette', '대국 배경 테마', 'surround-theme', surroundLabel)}` +
        `${renderSettingRow('frame', '전체화면', 'full-screen', fullscreenEnabled ? 'on' : 'off')}` +
        `</div>` +
        `<div class="space-12"></div>` +
        `${act('install-pwa', ico('frame') + installBtnLabel, 'btn btn-secondary')}` +
        `<div class="space-12"></div>` +
        `<div class="settings-detail-row"><strong>화면 위 여백</strong><span>안전 영역 자동 보호</span></div>` +
        `<div class="settings-detail-row"><strong>계정 연속성</strong><span>${act('account-transfer', '기기·앱 이전 코드', 'inline-link')}</span></div>` +
        `<div class="space-20"></div>` +
        `${act('close-modal', '확인')}`
    );
  },
  sound: el => {
    soundEnabled = soundEngine.toggle();
    el.querySelector('.toggle')?.classList.toggle('off', !soundEnabled);
    showToast(soundEnabled ? '소리를 켰어요.' : '소리를 껐어요.');
  },
  'big-text': el => {
    const appEl = document.getElementById('app');
    const on = appEl ? appEl.classList.toggle('large-text') : false;
    el.querySelector('.toggle')?.classList.toggle('off', !on);
    showToast(on ? '큰 글씨로 바꿨어요.' : '기본 글씨로 바꿨어요.');
  },
  'full-screen': el => {
    toggleFullscreenMode().then(() => {
      el.querySelector('.toggle')?.classList.toggle('off', !fullscreenEnabled);
    });
  },
  help: () => {
    showModal(
      '쉽게 시작해요',
      list('people', '친구랑 두기', '초대 링크나 방 번호로 만나요', 'friends', 'blue', true) +
        list('cap', '혼자 두기', '편한 상대부터 골라요', 'solo', '', true) +
        list('bulb', '도움받기', '좋은 수가 있을 때 확인해요', 'hint', 'gold', true) +
        `<div class="space-12"></div>` +
        `${act('close-modal', '알겠어요')}`
    );
  },
  'shop-help': () => {
    shopTab = 'help';
    renderScreen('shop', true);
  },
  'shop-skin': () => {
    shopTab = 'skin';
    renderScreen('shop', true);
  },
  'buy-sku': el => {
    const skuId = el.getAttribute('data-sku') || 'sku_hunsu_3';
    // ★ A13 & A14: 통합 SKU 카탈로그 구매 — 보석 부족 시 자동 +500 리필 절대 금지!
    const res = purchaseCatalogSku(userProfile, skuId);
    if (!res.ok) {
      showToast(res.reason === 'insufficient_gems' ? '보석이 부족해요.' : '구매할 수 없는 상품이에요.');
      return;
    }
    userProfile = res.updatedProfile;
    saveProfile();
    syncAuthenticatedProfileToServer();
    closeModal();
    renderScreen(currentScreen, true);
    showToast(`${res.sku?.name} 구매가 완료되었어요!`);
  },
  buy: () => {
    const res = purchaseCatalogSku(userProfile, 'sku_hunsu_3');
    if (!res.ok) {
      showToast('보석이 부족해요.');
      return;
    }
    userProfile = res.updatedProfile;
    saveProfile();
    syncAuthenticatedProfileToServer();
    renderScreen(currentScreen, true);
    showToast('아이템을 충전했어요.');
  },
  equip: () => showToast('현재 장착 중인 꾸미기예요.'),
  'all-skins': () => {
    const boardRows = BOARD_SKIN_CATALOG.map(bSkin => {
      const isEquipped = userProfile.equippedBoardSkin === bSkin.id;
      const isOwned =
        (bSkin.priceGems === 0 && bSkin.priceCoins === 0) ||
        userProfile.unlockedBoardSkins.includes(bSkin.id);
      return (
        `<button class="list-card compact" data-action="select-board-skin" data-skin-id="${bSkin.id}">` +
        `<img class="skin-preview" src="${bSkin.imageUrl}" alt="${bSkin.name}" style="object-fit:cover">` +
        `<span><h3>${bSkin.name}</h3><p>${bSkin.description}</p></span>` +
        `<span class="tag ${isEquipped ? 'gold' : ''}">${
          isEquipped ? '장착 중' : isOwned ? '장착' : `◆ ${bSkin.priceGems}`
        }</span>` +
        `</button>`
      );
    }).join('<div class="space-12"></div>');

    const pieceRows = PIECE_SKIN_CATALOG.map(pSkin => {
      const isEquipped = userProfile.equippedPieceSkin === pSkin.id;
      const isOwned =
        (pSkin.priceGems === 0 && pSkin.priceCoins === 0) ||
        userProfile.unlockedPieceSkins.includes(pSkin.id);
      return (
        `<button class="list-card compact" data-action="select-piece-skin" data-piece-skin-id="${pSkin.id}">` +
        `<span class="stone-mini classic-stone" style="background:radial-gradient(circle at 30% 25%, ${pSkin.choBgGradient[0]}, ${pSkin.choBgGradient[1]} 65%, ${pSkin.choBgGradient[2]});color:${pSkin.choTextColor};border-color:${pSkin.choBorderColor}">楚</span>` +
        `<span><h3>${pSkin.name}</h3><p>${pSkin.description}</p></span>` +
        `<span class="tag ${isEquipped ? 'gold' : ''}">${
          isEquipped ? '장착 중' : isOwned ? '장착' : `◆ ${pSkin.priceGems}`
        }</span>` +
        `</button>`
      );
    }).join('<div class="space-12"></div>');

    showModal(
      '6종 명품 장기판 · 6종 팔각 장기알',
      `<div class="menu-section-title" style="margin-bottom:8px">명품 장기판 6종</div>` +
        boardRows +
        `<div class="space-20"></div>` +
        `<div class="menu-section-title" style="margin-bottom:8px">수제 팔각 장기알 6종</div>` +
        pieceRows +
        `<div class="space-12"></div>` +
        `${act('close-modal', '돌아가기', 'btn btn-secondary')}`
    );
  },
  'select-board-skin': el => {
    const skinId = el.getAttribute('data-skin-id') || 'board_zelkova';
    const bSkin = getBoardSkinById(skinId);
    const isOwned =
      (bSkin.priceGems === 0 && bSkin.priceCoins === 0) ||
      userProfile.unlockedBoardSkins.includes(bSkin.id);

    // ★ R04: 미보유 유료 스킨은 재화 확인 및 차감 후 해금·장착!
    if (!isOwned) {
      if (bSkin.priceGems > 0) {
        if (userProfile.gems < bSkin.priceGems) {
          showToast(`보석이 부족해요. (필요 ◆ ${bSkin.priceGems})`);
          return;
        }
        userProfile.gems -= bSkin.priceGems;
      } else if (bSkin.priceCoins > 0) {
        if (userProfile.coins < bSkin.priceCoins) {
          showToast(`엽전이 부족해요. (필요 金 ${bSkin.priceCoins.toLocaleString()})`);
          return;
        }
        userProfile.coins -= bSkin.priceCoins;
      }
      userProfile.unlockedBoardSkins.push(bSkin.id);
    }

    userProfile.equippedBoardSkin = bSkin.id;
    saveProfile();
    syncAuthenticatedProfileToServer();
    closeModal();
    if (soundEnabled) soundEngine.playPieceClack(1, 'cha', bSkin.id, userProfile.equippedPieceSkin);
    renderScreen(currentScreen, true);
    showToast(`${bSkin.name}을(를) ${isOwned ? '장착' : '구매 및 장착'}했어요!`);
  },
  'select-piece-skin': el => {
    const pieceSkinId = el.getAttribute('data-piece-skin-id') || 'piece_boxwood_master';
    const pSkin = getPieceSkinById(pieceSkinId);
    const isOwned =
      (pSkin.priceGems === 0 && pSkin.priceCoins === 0) ||
      userProfile.unlockedPieceSkins.includes(pSkin.id);

    // ★ R04: 미보유 유료 장기알 스킨은 재화 확인 및 차감 후 해금·장착!
    if (!isOwned) {
      if (pSkin.priceGems > 0) {
        if (userProfile.gems < pSkin.priceGems) {
          showToast(`보석이 부족해요. (필요 ◆ ${pSkin.priceGems})`);
          return;
        }
        userProfile.gems -= pSkin.priceGems;
      } else if (pSkin.priceCoins > 0) {
        if (userProfile.coins < pSkin.priceCoins) {
          showToast(`엽전이 부족해요. (필요 金 ${pSkin.priceCoins.toLocaleString()})`);
          return;
        }
        userProfile.coins -= pSkin.priceCoins;
      }
      userProfile.unlockedPieceSkins.push(pSkin.id);
    }

    userProfile.equippedPieceSkin = pSkin.id;
    saveProfile();
    syncAuthenticatedProfileToServer();
    closeModal();
    if (soundEnabled) soundEngine.playPieceClack(1, 'king', userProfile.equippedBoardSkin, pSkin.id);
    renderScreen(currentScreen, true);
    showToast(`${pSkin.name}을(를) ${isOwned ? '장착' : '구매 및 장착'}했어요!`);
  },
  'all-items': () => {
    showModal(
      '도움 아이템 (통합 상점)',
      renderProductCard('bulb', '훈수 3회', '좋은 수를 확인해요', '60', 'gold', 'sku_hunsu_3') +
        renderProductCard('eye', '상대 노림수 3회', '상대의 위협을 살펴요', '55', 'blue', 'sku_vision_3') +
        renderProductCard('undo', '무르기 3회', '되돌릴 수 있는 수 확인', '50', '', 'sku_undo_3') +
        renderProductCard('shield', '점수 보호권 2회', '패배 점수 하락 방어', '65', 'gold', 'sku_ranked_shield_2') +
        `<div class="space-12"></div>` +
        `${act('tickets', '도전권 · +3수 연장', 'btn btn-secondary')}` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '계속 두기', 'btn btn-primary')}`
    );
  },
  tickets: () => {
    showModal(
      '도전을 이어가요',
      `<p>미녀대국 도전권과 수순 연장권을 확인해요.</p>` +
        renderProductCard('heart', '도전권 5장', '미녀대국에 도전해요', '90', 'rose', 'sku_ticket_5') +
        renderProductCard('play', '+3수 연장', '수순 제한 즉시 +3수 연장', '120', 'gold', 'sku_extend_3')
    );
  },
  vip: () => {
    showModal(
      '매일 받는 도움 (천하명인 패스)',
      `<p>무료 횟수에 더해<br>매일 편하게 도움을 받아보세요.</p>` +
        `<div class="quota-panel">` +
        `<div class="row between"><span>훈수</span><strong>매일 5회</strong></div>` +
        `<div class="row between"><span>상수</span><strong>매일 5회</strong></div>` +
        `<div class="row between"><span>대국 다시 보기</span><strong>매일 3회</strong></div>` +
        `</div>` +
        `${act('buy-sku', '이용권 시작하기 (◆ 490)', 'btn btn-primary', 'data-sku="sku_vip_monthly"')}` +
        `<div class="space-12"></div>` +
        `${act('close-modal', '돌아가기', 'btn btn-secondary')}`
    );
  },
  'close-modal': closeModal,
  'result-win': () => {
    resultState = 'win';
    renderScreen('result', true);
  },
  'result-void': () => {
    resultState = 'void';
    renderScreen('result', true);
  },
  'connection-opponent': () => {
    connectState = 'opponent';
    renderScreen('reconnect', true);
  },
  'connection-mine': () => {
    connectState = 'mine';
    renderScreen('reconnect', true);
  }
};

async function ensureRestSessionAuthenticated(): Promise<boolean> {
  if (typeof fetch === 'undefined') return false;
  try {
    let resp = await fetch('/api/session/guest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: sessionToken ? userProfile.userId : undefined,
        sessionToken: sessionToken || undefined,
        nickname: userProfile.nickname
      })
    });
    if (resp.status === 401) {
      resp = await fetch('/api/session/guest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: userProfile.nickname })
      });
    }
    const data = await resp.json();
    if (!resp.ok || !data?.ok || !data?.sessionToken) return false;
    sessionToken = data.sessionToken;
    userProfile.userId = data.userId;
    saveProfile();
    try {
      localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
    } catch {
      // ignore
    }
    await syncAuthenticatedProfileToServer();
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// 전역 이벤트 리스너 & QA 프리뷰 인터페이스 (window.JangkiPreview)
// ============================================================================
let clockTickerId: any = null;

function getSelectableMyPiecePositions(): Pos[] {
  if (activeGameMode === 'spectate' || currentTurn !== mySide || awaitingMoveAck || crisisCheckInFlight) {
    return [];
  }
  const res: Pos[] = [];
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const p = activeBoard[r]?.[c];
      if (p && p.side === mySide) {
        res.push({ row: r, col: c });
      }
    }
  }
  return res;
}

function initClientApp(): void {
  if (urlParams.get('largeText') === '1') {
    document.getElementById('app')?.classList.add('large-text');
  }

  // ★ 첫 터치 0.5초 콜드부트 지연 원천 제거: 앱 초기화 시점에 두 분석 Web Worker 즉시 예열!
  if (typeof Worker !== 'undefined') {
    actionAnalysisBroker.warmup();
    bgAnalysisBroker.warmup();
  }

  // 웹폰트(JnSerif/JnSans) 로드 완료 시 장기알 오프스크린 스프라이트 캐시 1회 갱신
  if (typeof document !== 'undefined' && (document as any).fonts?.ready) {
    (document as any).fonts.ready
      .then(() => {
        clearCanvasSpriteCaches();
        renderAllBoardCanvases();
      })
      .catch(() => {});
  }

  syncFullscreenStateFromDom();

  document.addEventListener('fullscreenchange', () => {
    syncFullscreenStateFromDom();
  });

  window.addEventListener('beforeinstallprompt', (ev: any) => {
    ev.preventDefault();
    deferredPwaInstallPrompt = ev;
  });

  window.addEventListener('appinstalled', () => {
    deferredPwaInstallPrompt = null;
    syncFullscreenStateFromDom();
  });

  if (!clockTickerId) {
    clockTickerId = setInterval(() => {
      updateClockHud();
    }, 200);
  }

  // ★ 모바일/데스크톱 즉각 반응(0ms pointerdown) + 자석 타겟 보정 + 부드러운 드래그-앤-드롭 착수
  document.addEventListener('pointerdown', e => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const canvasEl = target.closest('canvas.janggi-canvas') as HTMLCanvasElement | null;
    if (!canvasEl || canvasEl.getAttribute('data-board-compact') === '1') return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    soundEngine.unlockAudio();
    lastCanvasPointerHandledAt = performance.now();

    const rect = canvasEl.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    canvasEl.dataset.cssWidth = String(Math.round(rect.width));
    canvasEl.dataset.cssHeight = String(Math.round(rect.height));

    const pt = clientToPoint(e.clientX, e.clientY, rect, SQUARE_BOARD);
    if (!pt) return;

    const hit = resolveBoardInput(pt, mySide, SQUARE_BOARD, lastDrawnSelectablePieces, {
      selectedPos,
      legalTargets: legalTargetsForSelected,
      selectableMyPieces: getSelectableMyPiecePositions(),
      isDragRelease: false
    });
    lastTouchDiagnostic = { rawPoint: pt, hit };
    if (touchDiagEnabled) {
      renderAllBoardCanvases();
    }
    if (!hit) return;

    handleBoardIntersectionClick(hit.pos.row, hit.pos.col);

    // 내 기물이 선택된 상태라면 그 자리에서 바로 밀어서 놓는 드래그-앤-드롭 세션도 함께 준비
    if (selectedPos && legalTargetsForSelected.length > 0) {
      activeBoardDrag = {
        pointerId: e.pointerId,
        originPos: { ...selectedPos },
        startPoint: pt,
        currentPoint: pt,
        isDragging: false,
        hoverTargetPos: null
      };
      try {
        canvasEl.setPointerCapture?.(e.pointerId);
      } catch {
        // ignore synthetic events without active pointerId
      }
    }
  });

  document.addEventListener('pointermove', e => {
    if (!activeBoardDrag || e.pointerId !== activeBoardDrag.pointerId) return;
    const canvasEl =
      document.querySelector<HTMLCanvasElement>('.game-screen canvas.janggi-canvas:not([data-board-compact="1"])') ||
      ((e.target as HTMLElement | null)?.closest('canvas.janggi-canvas') as HTMLCanvasElement | null);
    if (!canvasEl) return;

    const rect = canvasEl.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const rawX = ((e.clientX - rect.left) * SQUARE_BOARD.width) / rect.width;
    const rawY = ((e.clientY - rect.top) * SQUARE_BOARD.height) / rect.height;
    const pt: Point = {
      x: Math.max(0, Math.min(SQUARE_BOARD.width, rawX)),
      y: Math.max(0, Math.min(SQUARE_BOARD.height, rawY))
    };

    if (!activeBoardDrag.isDragging && Math.hypot(pt.x - activeBoardDrag.startPoint.x, pt.y - activeBoardDrag.startPoint.y) >= 12) {
      activeBoardDrag.isDragging = true;
    }

    if (activeBoardDrag.isDragging) {
      activeBoardDrag.currentPoint = pt;
      const hoverHit = resolveBoardInput(pt, mySide, SQUARE_BOARD, lastDrawnSelectablePieces, {
        selectedPos,
        legalTargets: legalTargetsForSelected,
        isDragRelease: true
      });
      activeBoardDrag.hoverTargetPos =
        hoverHit && legalTargetsForSelected.some(t => t.row === hoverHit.pos.row && t.col === hoverHit.pos.col)
          ? hoverHit.pos
          : null;

      if (dragRedrawRafId === null && typeof requestAnimationFrame !== 'undefined') {
        dragRedrawRafId = requestAnimationFrame(() => {
          dragRedrawRafId = null;
          renderAllBoardCanvases();
        });
      }
    }
  });

  const finishCanvasPointerGesture = (e: PointerEvent, commitDrop: boolean) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('canvas.janggi-canvas')) {
      lastCanvasPointerHandledAt = performance.now();
    }
    if (!activeBoardDrag || e.pointerId !== activeBoardDrag.pointerId) return;
    lastCanvasPointerHandledAt = performance.now();
    const drag = activeBoardDrag;
    activeBoardDrag = null;

    if (commitDrop && drag.isDragging && selectedPos) {
      const canvasEl =
        document.querySelector<HTMLCanvasElement>('.game-screen canvas.janggi-canvas:not([data-board-compact="1"])') ||
        (target?.closest('canvas.janggi-canvas') as HTMLCanvasElement | null);
      let dropPos: Pos | null = drag.hoverTargetPos;
      if (canvasEl) {
        const rect = canvasEl.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const rawX = ((e.clientX - rect.left) * SQUARE_BOARD.width) / rect.width;
          const rawY = ((e.clientY - rect.top) * SQUARE_BOARD.height) / rect.height;
          const pt: Point = {
            x: Math.max(0, Math.min(SQUARE_BOARD.width, rawX)),
            y: Math.max(0, Math.min(SQUARE_BOARD.height, rawY))
          };
          const dropHit = resolveBoardInput(pt, mySide, SQUARE_BOARD, lastDrawnSelectablePieces, {
            selectedPos,
            legalTargets: legalTargetsForSelected,
            isDragRelease: true
          });
          if (dropHit && legalTargetsForSelected.some(t => t.row === dropHit.pos.row && t.col === dropHit.pos.col)) {
            dropPos = dropHit.pos;
          }
        }
      }
      if (dropPos) {
        handleBoardIntersectionClick(dropPos.row, dropPos.col);
        return;
      }
    }

    if (drag.isDragging) {
      renderAllBoardCanvases();
    }
  };

  document.addEventListener('pointerup', e => finishCanvasPointerGesture(e, true));
  document.addEventListener('pointercancel', e => finishCanvasPointerGesture(e, false));

  document.addEventListener('click', e => {
    soundEngine.unlockAudio();

    const target = e.target as HTMLElement | null;
    if (!target) return;

    // 1) HTML5 Canvas 장기판 클릭 폴백 (pointerdown에서 이미 처리된 터치/마우스 탭은 중복 실행 방지, 합성 click() 테스트는 그대로 통과!)
    const canvasEl = target.closest('canvas.janggi-canvas') as HTMLCanvasElement | null;
    if (canvasEl && canvasEl.getAttribute('data-board-compact') !== '1') {
      if (performance.now() - lastCanvasPointerHandledAt < 650) {
        return;
      }
      const rect = canvasEl.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const pt = clientToPoint(e.clientX, e.clientY, rect, SQUARE_BOARD);
        if (pt) {
          const hit = resolveBoardInput(pt, mySide, SQUARE_BOARD, lastDrawnSelectablePieces, {
            selectedPos,
            legalTargets: legalTargetsForSelected,
            selectableMyPieces: getSelectableMyPiecePositions(),
            isDragRelease: false
          });
          lastTouchDiagnostic = { rawPoint: pt, hit };
          if (touchDiagEnabled) {
            renderAllBoardCanvases();
          }
          if (hit) {
            handleBoardIntersectionClick(hit.pos.row, hit.pos.col);
            return;
          }
        }
      }
    }

    if (
      target.closest('#btn-fullscreen-toggle, #btn-match-fullscreen, #btn-lobby-fullscreen-toggle')
    ) {
      toggleFullscreenMode();
      return;
    }

    const btn = target.closest('button, [data-action], [data-go]') as HTMLElement | null;
    if (!btn) return;

    const goTarget = btn.getAttribute('data-go');
    if (goTarget) {
      if (soundEnabled) soundEngine.playPieceSelect();
      closeModal();
      if (goTarget === 'matching') {
        const s = ensureSocketConnected();
        s.emit('join_pvp_queue', {
          userId: userProfile.userId,
          sessionToken,
          nickname: userProfile.nickname,
          tierIndex: userProfile.tierIndex,
          rating: userProfile.rating,
          formation: myFormation
        });
      }
      renderScreen(goTarget);
      return;
    }

    const actionName = btn.getAttribute('data-action');
    if (actionName) {
      if (
        soundEnabled &&
        !['reveal-hint', 'sangsu', 'enemy-intent', 'pass', 'emote-demo', 'send-emote'].includes(actionName)
      ) {
        soundEngine.playPieceSelect();
      }
      if (actionName.startsWith('select-chapter-')) {
        activeChapterId = actionName.replace('select-chapter-', '');
        closeModal();
        renderScreen('campaign', true);
        return;
      }
      const handler = actionHandlers[actionName];
      if (handler) {
        handler(btn);
      }
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeModal();
  });

  // ★ S04 & S05: visibilitychange 이벤트는 서버에 정보성 상태만 알리며 몰수패 타이머를 유발하지 않음
  document.addEventListener('visibilitychange', () => {
    if (socket && pvpRoomId) {
      socket.emit('pvp_visibility_state', {
        state: document.hidden ? 'away' : 'active',
        reason: document.hidden ? '화면 전환' : '복귀'
      });
    }
  });

  window.addEventListener('popstate', () => {
    const id = new URLSearchParams(window.location.search).get('screen') || 'lobby';
    currentScreen = id in VIEWS ? id : 'lobby';
    syncBodyPlayClasses(currentScreen);
    const appEl = document.getElementById('app');
    if (appEl) appEl.innerHTML = VIEWS[currentScreen]();
    syncBoardCanvasAspect();
    renderAllBoardCanvases();
    updateClockHud();
    updateReviewPanel();
  });

  (window as any).JangkiPreview = {
    go: renderScreen,
    setHint: (state: string) => {
      hintSource = 'hunsu';
      hintState = state === 'revealed' ? 'revealed' : 'ready';
      renderScreen('hint', true);
    },
    setResult: (state: 'win' | 'loss' | 'void') => {
      resultState = state;
      renderScreen('result', true);
    },
    setConnection: (state: 'opponent' | 'mine') => {
      connectState = state;
      renderScreen('reconnect', true);
    },
    toggleLargeText: (force?: boolean) => {
      const appEl = document.getElementById('app');
      if (!appEl) return false;
      return appEl.classList.toggle('large-text', force);
    },
    setSurroundTheme: (theme: SurroundThemeId) => {
      surroundTheme = theme === 'warm-stone' ? 'warm-stone' : 'graphite';
      renderScreen(currentScreen, true);
    },
    setTouchDiag: (enabled: boolean) => {
      touchDiagEnabled = Boolean(enabled);
      renderAllBoardCanvases();
    },
    getLastTouchDiag: () => lastTouchDiagnostic,
    getTurnClock: () => getActiveTurnClock(),
    getLastMove: () => lastMove,
    getPvpPendingMove: () => pvpPendingMove,
    getProfile: () => userProfile,
    getFormation: () => myFormation,
    getSavedRecords: () => savedMatchRecords,
    screens: SCREEN_DATA.map(s => s.id)
  };

  renderScreen(currentScreen, true);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initClientApp);
  } else {
    initClientApp();
  }
}
